import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  TextFieldProps,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Chip,
  Snackbar,
  Pagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SendIcon from '@mui/icons-material/Send';
import ImageIcon from '@mui/icons-material/Image';
import MicIcon from '@mui/icons-material/Mic';
import VideocamIcon from '@mui/icons-material/Videocam';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EventIcon from '@mui/icons-material/Event';
import CloudIcon from '@mui/icons-material/Cloud';
import LinkIcon from '@mui/icons-material/Link';
import CampaignIcon from '@mui/icons-material/Campaign';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import UploadIcon from '@mui/icons-material/CloudUpload';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Client,
  MessageTemplate,
  Campaign,
  CampaignDayInput,
  CampaignScheduleType,
  MyCampaign,
  AgentSettings,
  CalendlyEventType,
  WhatsAppState,
  TestSendResult,
  fetchAllClients,
  fetchTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  refreshTemplateStatuses,
  fetchCampaigns,
  createCampaign,
  fetchMyCampaigns,
  createMyCampaign,
  updateMyCampaignStructure,
  setMyCampaignActive,
  fetchCampaignEnrollment,
  setCampaignEnrollment,
  uploadConnectorMedia,
  uploadLibraryMedia,
  fetchMediaLibrary,
  ConnectorLibraryMedia,
  ConnectorLibraryMediaType,
  fetchLatestPickerSalesUpdate,
  fetchAgentSettings,
  updateAgentSettings,
  connectCalendly,
  disconnectCalendly,
  fetchCalendlyEventTypes,
  openWhatsAppPairingStream,
  disconnectWhatsApp,
  testSendEngagement,
  testSendCustomCampaignDay,
  fetchWhatsAppStatus,
  shouldQueryLiveWhatsAppBridge,
  WhatsAppCloudStatus,
  EngagementTemplateState,
  TemplateSyncDayResult,
  fetchWhatsAppCloudStatus,
  isDemoMode,
  blockDemoMutation,
  launchWhatsAppEmbeddedSignup,
  onboardWhatsAppCloud,
  refreshWhatsAppCloud,
  disconnectWhatsAppCloud,
  setWhatsAppProvider,
  setWhatsAppCloudAccountSource,
  syncEngagementTemplates,
  syncCustomCampaignTemplates,
  fetchWhatsAppCloudSpend,
  CloudSpendResponse,
  CloudSpendCampaign,
  fetchMyAccessCodes,
  generateAccessCode,
  deleteAccessCode,
  fetchAccessCodeShareLink,
  sendAccessCodeWhatsApp,
  AccessCode,
  MyAccessCodesResponse,
  ClientTag,
  fetchTags,
  InboundTrigger,
  fetchInboundTriggers,
  createInboundTrigger,
  updateInboundTrigger,
  deleteInboundTrigger,
} from '../services/api';
import {
  connectorIssue,
  metaTemplateIssue,
  metaTemplateApproved,
  evaluateSevenDayReadiness,
  evaluateCustomCampaignReadiness,
  campaignRowHasContent,
  readinessWarningText,
} from '../utils/campaignReadiness';
import { templateStatusStyle } from '../utils/metaTemplateStatus';
import {
  ENGAGEMENT_IMAGE_MAX_BYTES,
  ENGAGEMENT_PDF_MAX_BYTES,
  ENGAGEMENT_VIDEO_MAX_BYTES,
  VOICE_RECORDING_MAX_BYTES,
  asTypedUploadBlob,
  effectiveMimeType,
  fileTooLargeMessage,
  formatUploadSize,
  libraryUploadIssue,
  recordingTooLargeMessage,
  uploadCapHint,
} from '../utils/mediaUploadLimits';
import { useMobileMask } from '../context/MobileMaskContext';
import ManageTagsTab from '../components/ManageTagsTab';
import MessageTemplateMetaStatus, { MessageTemplateSpendChip, MessageTemplateStatusLegend } from '../components/MessageTemplateMetaStatus';
import ProjectEssentialMediaPicker from '../components/ProjectEssentialMediaPicker';

// ─── Constants ───
const TEMPLATE_TYPES = ['intro', 'match', 'viewing', 'marketing'];
const TEMPLATE_TYPE_LABELS: Record<string, string> = {
  intro: 'Intro',
  match: 'Match',
  viewing: 'Viewing',
  marketing: 'Marketing',
};
const PLACEHOLDERS = ['{{client_name}}', '{{property_address}}', '{{project_link}}', '{{agent_phone}}', '{{agent_name}}'];
/** Never filled on a manual send, so Meta templates cannot use it. */
const CLOUD_UNSUPPORTED_PLACEHOLDERS = ['{{project_link}}'];

/** Named or numbered `{{…}}` token — Meta forbids these at the body edges. */
const META_PLACEHOLDER_TOKEN = /\{\{\s*[a-zA-Z_][a-zA-Z0-9_]*\s*\}\}|\{\{\s*\d+\s*\}\}/;

/**
 * Reject a message body that starts or ends with a `{{…}}` placeholder
 * (bridge and Official API). Returns a short error, or null when fine / empty.
 */
function metaPlaceholderEdgeError(text: string): string | null {
  const trimmed = (text || '').trim();
  if (!trimmed) return null;
  const token = `(?:${META_PLACEHOLDER_TOKEN.source})`;
  if (new RegExp(`^${token}`).test(trimmed)) {
    return 'Message cannot start with a placeholder (a value in {{}}) — add some text before it.';
  }
  if (new RegExp(`${token}$`).test(trimmed)) {
    return 'Message cannot end with a placeholder (a value in {{}}) — add some text after it.';
  }
  return null;
}

/** Meta's template body limits, applied on both senders so content never needs rewriting on a switch. */
const TEMPLATE_BODY_MAX_CHARS = 1024;
const TEMPLATE_BODY_MAX_EMOJIS = 10;

type GraphemeSegmenter = new (
  locale?: string,
  options?: { granularity: 'grapheme' },
) => { segment(text: string): Iterable<{ segment: string }> };

/** Visible emojis, so a ZWJ family or 🏙️ (with its variation selector) counts once. */
function countEmojis(text: string): number {
  const pictographic = new RegExp('\\p{Extended_Pictographic}', 'u');
  const Segmenter = (Intl as unknown as { Segmenter?: GraphemeSegmenter }).Segmenter;
  if (Segmenter) {
    return Array.from(new Segmenter(undefined, { granularity: 'grapheme' }).segment(text))
      .filter((s) => pictographic.test(s.segment)).length;
  }
  return (text.match(new RegExp('\\p{Extended_Pictographic}', 'gu')) || []).length;
}

function bodyLengthIssue(label: string, length: number): string {
  return `${label} is ${length} characters; Meta allows ${TEMPLATE_BODY_MAX_CHARS}.`;
}

/**
 * Every Meta template rule a body breaks (bridge and Official API). Empty when
 * fine or empty. Pass `checkLength: false` when the caller measures length itself.
 */
function templateBodyIssues(text: string, opts?: { checkLength?: boolean }): string[] {
  const body = text || '';
  if (!body.trim()) return [];
  const issues: string[] = [];
  const edge = metaPlaceholderEdgeError(body);
  if (edge) issues.push(edge);
  const token = `(?:${META_PLACEHOLDER_TOKEN.source})`;
  if (new RegExp(`${token}\\s*${token}`).test(body)) {
    issues.push('Two placeholders (values in {{}}) cannot sit next to each other — put some text between them.');
  }
  const emojis = countEmojis(body);
  if (emojis > TEMPLATE_BODY_MAX_EMOJIS) {
    issues.push(`Message has ${emojis} emojis; Meta allows ${TEMPLATE_BODY_MAX_EMOJIS}.`);
  }
  if (opts?.checkLength !== false && body.length > TEMPLATE_BODY_MAX_CHARS) {
    issues.push(bodyLengthIssue('Message', body.length));
  }
  return issues;
}

// Reserved campaign Name for the built-in 7-Day Engagement sequence. Custom
// campaigns share the same backend table, so this name distinguishes them.
const ENGAGEMENT_CAMPAIGN_NAME = '7-Day Engagement';

/** Format Meta / budget amounts for UI (always 2 decimal places). */
function formatSgd(n: number): string {
  return `S$${Number(n || 0).toFixed(2)}`;
}

const SPEND_ESTIMATE_HINT = 'Estimated · S$0.10 per Official API marketing message';
const SPEND_ESTIMATE_NOTICE =
  'Spend is estimated at S$0.10 per Official API marketing message (not Meta billed amount).';

/** Border and chip colour for anything the agent still has to fix. */
const ATTENTION_RED = '#ef4444';

/** True when `next` may replace `current` without typing past the body limit. */
function fitsBodyLimit(current: string, next: string): boolean {
  return next.length <= TEMPLATE_BODY_MAX_CHARS || next.length <= current.length;
}

type TemplateBodyFieldProps = Omit<TextFieldProps, 'value' | 'onChange' | 'error' | 'helperText' | 'multiline'> & {
  value: string;
  onValueChange: (next: string, el: HTMLTextAreaElement) => void;
  /** Shown under the field; any issue marks the field as an error. */
  issues?: string[];
};

/**
 * Multiline message body with an N/1024 counter. Typing stops at the limit;
 * a paste or drop may go over so the length error can explain it.
 */
function TemplateBodyField({ value, onValueChange, issues = [], InputProps, ...rest }: TemplateBodyFieldProps) {
  const over = value.length > TEMPLATE_BODY_MAX_CHARS;
  return (
    <TextField
      {...rest}
      fullWidth
      multiline
      value={value}
      onChange={(e) => {
        const next = e.target.value;
        const inputType = (e.nativeEvent as InputEvent).inputType;
        const pasted = inputType === 'insertFromPaste' || inputType === 'insertFromDrop';
        if (!pasted && !fitsBodyLimit(value, next)) return;
        onValueChange(next, e.target as HTMLTextAreaElement);
      }}
      error={issues.length > 0}
      helperText={issues.length > 0 ? (
        <Box component="span" sx={{ display: 'flex', flexDirection: 'column' }}>
          {issues.map((issue) => <span key={issue}>{issue}</span>)}
        </Box>
      ) : undefined}
      InputProps={{
        ...InputProps,
        sx: { pb: 3.5 },
        endAdornment: (
          <InputAdornment
            position="end"
            sx={{ position: 'absolute', right: 12, bottom: 14, height: 'auto', m: 0, pointerEvents: 'none' }}
          >
            <Typography
              variant="caption"
              sx={{ color: over ? ATTENTION_RED : 'var(--color-text-faint)', fontVariantNumeric: 'tabular-nums', fontWeight: over ? 700 : 400 }}
            >
              {value.length}/{TEMPLATE_BODY_MAX_CHARS}
            </Typography>
          </InputAdornment>
        ),
      }}
    />
  );
}

type DayKind = 'text' | 'image' | 'text_link' | 'text_image' | 'voice' | 'event';

interface DayConfig {
  kind: DayKind;
  title: string;
  subtitle: string;
  hasText: boolean;
  hasImage: boolean;
  hasFile: boolean;
  hasVoice: boolean;
  autoLinkLabel?: string;
  eventLabel?: string;
  whatsappOnly?: boolean;
  autoImageSource?: 'picker_sales_update';
  defaultMessage?: string;
}

/**
 * Starter copy for each day, pre-filled into empty message boxes.
 *
 * Worded to clear Meta's Marketing template review on the first submission:
 * each body opens with plain text rather than a placeholder, says who is
 * writing and why, and describes any attachment.
 * Mirrors `DEFAULT_TEMPLATE_BODY_BY_DAY` in connectorEngagementTemplateService.
 */
const DEFAULT_DAY_MESSAGES: Record<number, string> = {
  1:
    'Hi {{client_name}}, thank you for getting in touch about your property plans. ' +
    'I am {{agent_name}}, a licensed property agent in Singapore. Over the next week I will send you ' +
    'a few short updates on new launches, pricing and market news so you can plan at your own pace.',
  2:
    'Good morning {{client_name}}, wishing you a great day ahead. Here are the latest prices, ' +
    'unit availability and floor plans for projects you may be considering.',
  3:
    'Hi {{client_name}}, I have prepared a short guide to the new launch projects nearest your preferred ' +
    'location. The attached PDF lists the project names, addresses and indicative pricing so you can ' +
    'compare them easily.',
  4:
    'Hi {{client_name}}, here is this week\'s summary of the best performing new launch projects in ' +
    'Singapore. The attached chart shows the units sold and the average prices for the week.',
  5:
    'Hi {{client_name}}, I have recorded a short voice note with my thoughts on the current property ' +
    'market and what it could mean for your plans. Have a listen when you have a moment.',
  6:
    'Hi {{client_name}}, if you would like to view any of the projects we have discussed, feel free to book a ' +
    'time with me. Here is my scheduling link — pick a date and time that works for you, and I will confirm ' +
    'the viewing details.',
  7:
    'Hi {{client_name}}, it has been a pleasure sharing these property updates with you this week. ' +
    'My name card is attached, so do save my contact for whenever you need help.',
};

/** Strip legacy opt-out and reply prompts from saved or imported copy. */
function cleanEngagementMessageBody(text: string): string {
  let s = (text || '').trim();
  if (!s) return s;
  s = s.replace(/\s*Reply STOP(?:\s+to\s+unsubscribe)?[^.!?\n]*[.!?]?\s*/gi, ' ');
  s = s.replace(/,?\s*simply reply to this message and I will send them over\.?/gi, '.');
  s = s.replace(/\s*Reply to this message if you would like details on any project\.?/gi, '');
  s = s.replace(/\s*Reply to this message if you would like the full report for any project\.?/gi, '');
  s = s.replace(/\s*and reply here if you have any questions\.?/gi, '.');
  s = s.replace(/\s*When you are ready to take the next step, send me a message and I will guide you from there\.?/gi, '');
  s = s.replace(/\s*[Rr]eply to this message[^.!?\n]*[.!?]?\s*/g, ' ');
  s = s.replace(/\s*[Ss]imply reply[^.!?\n]*[.!?]?\s*/g, ' ');
  s = s.replace(
    /\s*Tap the button below to choose a date and time that suits you, and I will confirm the viewing details\.?/gi,
    ' Here is my scheduling link — pick a date and time that works for you, and I will confirm the viewing details.',
  );
  return s.replace(/\s+/g, ' ').replace(/\s+([.!?])/g, '$1').replace(/\.{2,}/g, '.').trim();
}

const DAY_CONFIG: DayConfig[] = [
  { kind: 'text',       title: 'Welcome Message',  subtitle: 'Introduce yourself and set expectations for the week', hasText: true,  hasImage: false, hasFile: false, hasVoice: false, defaultMessage: DEFAULT_DAY_MESSAGES[1] },
  { kind: 'image',      title: 'Greeting Image',   subtitle: 'A good-morning card with a short friendly note', hasText: true,  hasImage: true,  hasFile: false, hasVoice: false, defaultMessage: DEFAULT_DAY_MESSAGES[2] },
  { kind: 'text_link',  title: 'Nearby Projects',  subtitle: 'PDF built automatically from the client\'s preferred location', hasText: true,  hasImage: false, hasFile: false, hasVoice: false, autoLinkLabel: 'PDF of nearby projects', defaultMessage: DEFAULT_DAY_MESSAGES[3] },
  { kind: 'text_image', title: 'Weekly Hot Seller', subtitle: 'Uses the latest weekly sales-update image automatically', hasText: true,  hasImage: true,  hasFile: false, hasVoice: false, autoImageSource: 'picker_sales_update', defaultMessage: DEFAULT_DAY_MESSAGES[4] },
  { kind: 'voice',      title: 'Voice Note',       subtitle: 'Record a personal voice message', hasText: false, hasImage: false, hasFile: false, hasVoice: true,  whatsappOnly: true, defaultMessage: DEFAULT_DAY_MESSAGES[5] },
  { kind: 'event',      title: 'Calendly Invite',  subtitle: 'Your Calendly scheduling link is added to the message', hasText: true,  hasImage: false, hasFile: false, hasVoice: false, eventLabel: 'Uses your selected Calendly event type', defaultMessage: DEFAULT_DAY_MESSAGES[6] },
  { kind: 'image',      title: 'Wrap-Up & Name Card', subtitle: 'Close the week with your name card and a thank-you', hasText: true, hasImage: true, hasFile: false, hasVoice: false, defaultMessage: DEFAULT_DAY_MESSAGES[7] },
];

// ─── Day channel state (mirrors DB row shape) ───
interface DayChannel {
  enabled: boolean;
  message: string;
  mediaUrl: string | null;
  ctaLabel: string | null;
  ctaUrl: string | null;
}

interface EngagementDay {
  dayNumber: number;
  enabled: boolean;
  whatsapp: DayChannel;
  email: DayChannel;
}

function snapshotEngagementDay(day: EngagementDay): EngagementDay {
  return {
    dayNumber: day.dayNumber,
    enabled: day.enabled,
    whatsapp: { ...day.whatsapp },
    email: { ...day.email },
  };
}

function insertTextAtSelection(
  text: string,
  insert: string,
  start: number,
  end: number,
): { next: string; cursor: number } {
  const next = text.slice(0, start) + insert + text.slice(end);
  return { next, cursor: start + insert.length };
}

const emptyChannel = (enabled: boolean): DayChannel => ({
  enabled, message: '', mediaUrl: null, ctaLabel: null, ctaUrl: null,
});

const makeDefaultDays = (): EngagementDay[] =>
  Array.from({ length: 7 }, (_, i) => ({
    dayNumber: i + 1,
    enabled: i === 4 ? true : false,
    whatsapp: { ...emptyChannel(true), message: DAY_CONFIG[i].defaultMessage ?? '' },
    email: emptyChannel(false),
  }));

/** Overlay saved 7-Day Engagement rows onto the editor's default 7 days. */
function applyEngagementCampaignRows(days: Campaign[]): EngagementDay[] {
  const populated = makeDefaultDays();
  days.forEach((d: Campaign) => {
    const idx = d.DayNumber - 1;
    if (idx < 0 || idx >= 7) return;
    const active = d.IsActive !== false;
    const chan: DayChannel = {
      enabled: active,
      message: d.MessageTemplate || '',
      mediaUrl: d.MediaUrl || null,
      ctaLabel: d.CtaLabel || null,
      ctaUrl: d.CtaUrl || null,
    };
    if (d.Channel === 'whatsapp') {
      populated[idx].enabled = active;
      populated[idx].whatsapp = {
        ...chan,
        message: cleanEngagementMessageBody(chan.message) || DAY_CONFIG[idx].defaultMessage || '',
        mediaUrl: d.DayNumber === 4 ? null : chan.mediaUrl,
      };
    } else if (d.Channel === 'email') populated[idx].email = chan;
  });
  return populated;
}

// ─── Helpers ───
const fileToDataUrl = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/** "PDF · max 10 MB" under an upload button. */
function UploadCapCaption({ text }: { text: string }) {
  return (
    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'var(--color-text-faint)' }}>
      {text}
    </Typography>
  );
}

// ─── Picker sales-update preview (Day 4 — read-only) ───
function PickerSalesUpdatePreview() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [update, setUpdate] = useState<{ imgUrl: string; startDate: string; endDate: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const latest = await fetchLatestPickerSalesUpdate();
        if (cancelled) return;
        if (latest.imgUrl && latest.endDate) {
          setUpdate({
            imgUrl: latest.imgUrl,
            startDate: latest.startDate || latest.endDate,
            endDate: latest.endDate,
          });
        } else {
          setUpdate(null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)' }}>Loading latest sales update…</Typography>
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert>;
  }
  if (!update) {
    return (
      <Alert severity="warning" sx={{ py: 0.5 }}>
        No weekly sales update image found yet. The Day 4 message will send once the sales-update job has published an image.
      </Alert>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        component="img"
        src={update.imgUrl}
        alt="Weekly sales update"
        sx={{ width: 96, height: 96, borderRadius: 1, objectFit: 'cover', border: '1px solid var(--color-border)' }}
      />
      <Box>
        <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)', display: 'block' }}>
          Latest by end date: <strong>{update.endDate}</strong>
          {update.startDate !== update.endDate ? ` (${update.startDate} – ${update.endDate})` : ''}
        </Typography>
        <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', mt: 0.25 }}>
          Sourced from TransformationPickerSalesUpdate — updates when a new weekly image is published.
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Image Uploader ───
function ImageUploader({ url, onUploaded, slot }: { url: string | null; onUploaded: (url: string) => void; slot: string }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handlePick = async (file: File) => {
    setError(null);
    if (!/^image\/(png|jpeg|webp)$/.test(effectiveMimeType(file))) {
      setError(`"${file.name}" is not a PNG, JPG or WEBP image.`);
      return;
    }
    if (file.size > ENGAGEMENT_IMAGE_MAX_BYTES) {
      setError(fileTooLargeMessage(file.size, ENGAGEMENT_IMAGE_MAX_BYTES, `"${file.name}"`));
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await fileToDataUrl(asTypedUploadBlob(file));
      const res = await uploadConnectorMedia(slot, dataUrl);
      onUploaded(res.url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handlePick(f);
          e.target.value = '';
        }}
      />
      {url ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            component="img"
            src={url}
            sx={{ width: 72, height: 72, borderRadius: 1, objectFit: 'cover', border: '1px solid var(--color-border)' }}
          />
          <Button size="small" startIcon={<UploadIcon />} onClick={() => inputRef.current?.click()} disabled={uploading} sx={{ textTransform: 'none' }}>
            {uploading ? 'Uploading...' : 'Replace'}
          </Button>
          <Button size="small" color="error" onClick={() => onUploaded('')} sx={{ textTransform: 'none' }}>Remove</Button>
        </Box>
      ) : (
        <Button
          variant="outlined"
          size="small"
          startIcon={uploading ? <CircularProgress size={14} /> : <UploadIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          sx={{ textTransform: 'none' }}
        >
          {uploading ? 'Uploading...' : 'Upload image'}
        </Button>
      )}
      <UploadCapCaption text={uploadCapHint('image', ENGAGEMENT_IMAGE_MAX_BYTES)} />
      {error && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{error}</Typography>}
    </Box>
  );
}

// ─── Video Uploader ───
function VideoUploader({ url, onUploaded, slot }: { url: string | null; onUploaded: (url: string) => void; slot: string }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handlePick = async (file: File) => {
    setError(null);
    if (!/^video\/(mp4|quicktime)$/.test(effectiveMimeType(file))) {
      setError(`"${file.name}" is not an MP4 or MOV video.`);
      return;
    }
    if (file.size > ENGAGEMENT_VIDEO_MAX_BYTES) {
      setError(fileTooLargeMessage(file.size, ENGAGEMENT_VIDEO_MAX_BYTES, `"${file.name}"`));
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await fileToDataUrl(asTypedUploadBlob(file));
      const res = await uploadConnectorMedia(slot, dataUrl);
      onUploaded(res.url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        accept="video/mp4,video/quicktime"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handlePick(f);
          e.target.value = '';
        }}
      />
      {url ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box
            component="video"
            src={url}
            controls
            sx={{ width: 160, maxHeight: 90, borderRadius: 1, border: '1px solid var(--color-border)', bgcolor: '#000' }}
          />
          <Button size="small" startIcon={<UploadIcon />} onClick={() => inputRef.current?.click()} disabled={uploading} sx={{ textTransform: 'none' }}>
            {uploading ? 'Uploading...' : 'Replace'}
          </Button>
          <Button size="small" color="error" onClick={() => onUploaded('')} sx={{ textTransform: 'none' }}>Remove</Button>
        </Box>
      ) : (
        <Button
          variant="outlined"
          size="small"
          startIcon={uploading ? <CircularProgress size={14} /> : <VideocamIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          sx={{ textTransform: 'none' }}
        >
          {uploading ? 'Uploading...' : 'Upload video'}
        </Button>
      )}
      <UploadCapCaption text={uploadCapHint('video', ENGAGEMENT_VIDEO_MAX_BYTES)} />
      {error && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{error}</Typography>}
    </Box>
  );
}

// ─── File Uploader ───
function FileUploader({ url, onUploaded, slot }: { url: string | null; onUploaded: (url: string) => void; slot: string }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handlePick = async (file: File) => {
    setError(null);
    if (effectiveMimeType(file) !== 'application/pdf') {
      setError(`"${file.name}" is not a PDF.`);
      return;
    }
    if (file.size > ENGAGEMENT_PDF_MAX_BYTES) {
      setError(fileTooLargeMessage(file.size, ENGAGEMENT_PDF_MAX_BYTES, `"${file.name}"`));
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await fileToDataUrl(asTypedUploadBlob(file));
      const res = await uploadConnectorMedia(slot, dataUrl);
      onUploaded(res.url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handlePick(f);
          e.target.value = '';
        }}
      />
      {url ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PictureAsPdfIcon sx={{ color: '#e53935' }} />
          <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)' }}>
            PDF attached
          </Typography>
          <Button size="small" startIcon={<UploadIcon />} onClick={() => inputRef.current?.click()} disabled={uploading} sx={{ textTransform: 'none' }}>
            {uploading ? 'Uploading...' : 'Replace'}
          </Button>
          <Button size="small" color="error" onClick={() => onUploaded('')} sx={{ textTransform: 'none' }}>Remove</Button>
        </Box>
      ) : (
        <Button
          variant="outlined"
          size="small"
          startIcon={uploading ? <CircularProgress size={14} /> : <UploadIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          sx={{ textTransform: 'none' }}
        >
          {uploading ? 'Uploading...' : 'Upload PDF'}
        </Button>
      )}
      <UploadCapCaption text={uploadCapHint('document', ENGAGEMENT_PDF_MAX_BYTES)} />
      {error && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{error}</Typography>}
    </Box>
  );
}

// ─── Voice Recorder ───
function VoiceRecorder({
  url, onUploaded, slot, onLibraryItem,
}: {
  url: string | null;
  onUploaded: (url: string) => void;
  slot: string;
  /** When set, recording is saved to the Media library instead of the legacy upload path. */
  onLibraryItem?: (item: ConnectorLibraryMedia | null) => void;
}) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startRef = useRef(0);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    mediaRecorderRef.current?.stream.getTracks().forEach((t) => t.stop());
  }, []);

  const start = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // WhatsApp only renders PTT voice-note bubbles for Opus-encoded audio,
      // so prefer Opus-capable containers (ogg/webm). audio/mp4 (AAC) works
      // as a regular audio attachment but cannot be sent as a voice bubble.
      const preferredTypes = [
        'audio/ogg;codecs=opus',
        'audio/webm;codecs=opus',
        'audio/ogg',
        'audio/webm',
        'audio/mp4;codecs=mp4a.40.2',
        'audio/mp4',
      ];
      const pickedType = preferredTypes.find(
        (t) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.(t),
      );
      const mr = pickedType ? new MediaRecorder(stream, { mimeType: pickedType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        // Strip codec params from the mime so the backend whitelist (which
        // keys on the bare MIME type, e.g. "audio/mp4") accepts it.
        const rawType = mr.mimeType || pickedType || 'audio/webm';
        const baseType = rawType.split(';')[0].trim();
        const blob = new Blob(chunksRef.current, { type: baseType });
        stream.getTracks().forEach((t) => t.stop());
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        if (blob.size > VOICE_RECORDING_MAX_BYTES) {
          setError(recordingTooLargeMessage(blob.size));
          return;
        }
        setUploading(true);
        try {
          const dataUrl = await fileToDataUrl(blob);
          if (onLibraryItem) {
            const item = await uploadLibraryMedia({
              dataUrl,
              mediaType: 'voice',
              name: 'Voice note',
            });
            onLibraryItem(item);
          } else {
          const res = await uploadConnectorMedia(slot, dataUrl);
          onUploaded(res.url);
          }
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Upload failed');
        } finally {
          setUploading(false);
        }
      };
      mr.start();
      startRef.current = Date.now();
      setElapsedMs(0);
      timerRef.current = setInterval(() => setElapsedMs(Date.now() - startRef.current), 250);
      setRecording(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Microphone unavailable');
    }
  };

  const stop = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const fmt = (ms: number) => {
    const s = Math.floor(ms / 1000);
    return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {!recording ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={uploading ? <CircularProgress size={14} /> : <MicIcon />}
            onClick={start}
            disabled={uploading}
            sx={{ textTransform: 'none' }}
          >
            {url ? 'Re-record' : 'Record voice'}
          </Button>
        ) : (
          <Button
            variant="contained"
            size="small"
            startIcon={<StopIcon />}
            onClick={stop}
            color="error"
            sx={{ textTransform: 'none' }}
          >
            Stop ({fmt(elapsedMs)})
          </Button>
        )}
        {url && !recording && (
          <>
            <audio src={url} controls style={{ height: 32 }} />
            <Button
              size="small"
              color="error"
              onClick={() => {
                if (onLibraryItem) onLibraryItem(null);
                else onUploaded('');
              }}
              sx={{ textTransform: 'none' }}
            >
              Remove
            </Button>
          </>
        )}
      </Box>
      <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
        Voice notes · max {formatUploadSize(VOICE_RECORDING_MAX_BYTES)}
      </Typography>
      {error && <Typography variant="caption" color="error">{error}</Typography>}
    </Box>
  );
}

// ─── Main Settings / Campaigns component ───
// mode="campaigns" → Message Templates · 7-Day · Custom
// mode="settings"  → Integrations · Manage Tags · Access Codes
export default function Settings({
  isAdmin = false,
  initialTab = 0,
  mode = 'settings',
}: {
  isAdmin?: boolean;
  initialTab?: number;
  mode?: 'settings' | 'campaigns';
}) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(initialTab);
  const isCampaigns = mode === 'campaigns';
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [campaigns, setCampaigns] = useState<Record<string, Campaign[]>>({});
  const [loading, setLoading] = useState(true);

  // Template dialog
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);
  const [tplTitle, setTplTitle] = useState('');
  const [tplBody, setTplBody] = useState('');
  const [tplType, setTplType] = useState('intro');
  const [tplChannel, setTplChannel] = useState<string>('whatsapp');
  const [tplSaving, setTplSaving] = useState(false);
  const [tplError, setTplError] = useState<string | null>(null);
  const [refreshingMessageTemplates, setRefreshingMessageTemplates] = useState(false);
  const [messageTemplatesError, setMessageTemplatesError] = useState<string | null>(null);
  /** Official API spend per message template, keyed by local template id. Null until loaded. */
  const [messageTemplateSpend, setMessageTemplateSpend] = useState<Record<string, number> | null>(null);

  // Engagement
  const [campActive, setCampActive] = useState(false);
  const [campStartDay, setCampStartDay] = useState(1);
  // Throttle for the hourly cron — controls how many engagement messages an
  // agent's WhatsApp session is allowed to send per hour. The cron also caps
  // bursts at one message per 10s. Default 20.
  const [campMessagesPerHour, setCampMessagesPerHour] = useState(20);
  /** Empty string = no daily budget (unlimited). */
  const [dailyBudgetInput, setDailyBudgetInput] = useState('');
  const [campDays, setCampDays] = useState<EngagementDay[]>(makeDefaultDays());
  const [engagementSaving, setEngagementSaving] = useState(false);
  const [scheduleDirty, setScheduleDirty] = useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [mphDirty, setMphDirty] = useState(false);
  const [mphSaving, setMphSaving] = useState(false);
  const [budgetDirty, setBudgetDirty] = useState(false);
  const [budgetSaving, setBudgetSaving] = useState(false);
  /** Index of the engagement day open in the editor dialog, if any. */
  const [editingDay, setEditingDay] = useState<number | null>(null);

  // Access code quota (used to conditionally show the Access Codes tab)
  const [accessCodeQuota, setAccessCodeQuota] = useState(0);

  // Agent settings / integrations
  const [agentSettings, setAgentSettings] = useState<AgentSettings | null>(null);
  const [eventTypes, setEventTypes] = useState<CalendlyEventType[]>([]);
  const [calendlyKeyInput, setCalendlyKeyInput] = useState('');
  const [calendlyBusy, setCalendlyBusy] = useState(false);
  const [calendlyError, setCalendlyError] = useState<string | null>(null);
  /** Integrations sub-tab: 0 = Sending methods, 1 = Meetings */
  const [integrationsSubTab, setIntegrationsSubTab] = useState(0);

  // Clients (for the engagement test-send picker)
  const [clients, setClients] = useState<Client[]>([]);

  // Official WhatsApp API: connection state, active provider, and the Meta
  // approval status of each engagement day's template.
  const [cloudStatus, setCloudStatus] = useState<WhatsAppCloudStatus | null>(null);
  const [engagementSpend, setEngagementSpend] = useState<CloudSpendCampaign | null>(null);
  const [spendEstimated, setSpendEstimated] = useState(false);
  const [templateSync, setTemplateSync] = useState<TemplateSyncDayResult[] | null>(null);
  /** Day currently being submitted to Meta, so only its button shows a spinner. */
  const [syncingDay, setSyncingDay] = useState<number | null>(null);
  const [refreshingTemplates, setRefreshingTemplates] = useState(false);

  const reloadCloudStatus = useCallback(async () => {
    const status = await fetchWhatsAppCloudStatus().catch(() => null);
    setCloudStatus(status);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Live bridge status is skipped on localhost / transformation-dev — the
      // bridge is often unreachable there and would stall the whole page.
      // Stored agent settings still carry WhatsAppStatus from the DB.
      const liveBridge = shouldQueryLiveWhatsAppBridge();
      const [tpls, camps, settings, liveStatus, clientsResp, accessCodeData, cloud] = await Promise.all([
        fetchTemplates(),
        fetchCampaigns(),
        fetchAgentSettings().catch(() => null),
        liveBridge ? fetchWhatsAppStatus().catch(() => null) : Promise.resolve(null),
        fetchAllClients().catch(() => [] as Client[]),
        fetchMyAccessCodes().catch(() => ({ quota: 0, used: 0, codes: [] })),
        fetchWhatsAppCloudStatus().catch(() => null),
      ]);
      setTemplates(tpls);
      setCloudStatus(cloud);
      setCampaigns(camps.grouped);
      setClients(clientsResp);
      setAccessCodeQuota(accessCodeData.quota ?? 0);
      // Prefer the live bridge status when it disagrees with the stored
      // settings — persistence relies on webhooks/proxy mirroring and can lag.
      const merged = settings && liveStatus
        ? {
            ...settings,
            whatsAppStatus: liveStatus.status,
            whatsAppPhoneNumber: liveStatus.phone ?? settings.whatsAppPhoneNumber ?? null,
          }
        : settings;
      setAgentSettings(merged);
      if (settings) {
        setCampActive(settings.engagementActive);
        setCampStartDay(settings.engagementStartDayOffset);
        setCampMessagesPerHour(settings.engagementMessagesPerHour ?? 20);
        const b = settings.whatsAppCloudDailyBudgetSgd;
        setDailyBudgetInput(b != null && b > 0 ? String(b) : '');
        setBudgetDirty(false);
      }

      // Load ONLY the 7-Day Engagement group. Custom campaigns live in the same
      // grouped map (same backend table, keyed by Name), so picking the first key
      // would let a custom campaign's days bleed into the engagement tab.
      const engagementDays = camps.grouped[ENGAGEMENT_CAMPAIGN_NAME];
      if (engagementDays && engagementDays.length > 0) {
        setCampDays(applyEngagementCampaignRows(engagementDays));
      }
      setScheduleDirty(false);

      // Calendly event types are only for the Day 6 dropdown — never block the
      // page spinner on a live Calendly API round-trip (often slow / 500).
      if (settings?.hasCalendlyKey) {
        void fetchCalendlyEventTypes()
          .then((ets) => setEventTypes(ets))
          .catch(() => setEventTypes([]));
      } else {
          setEventTypes([]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Sending through Meta rather than the paired phone changes what the
  // engagement tab has to show: every message becomes a template Meta reviews.
  const usingCloud = cloudStatus?.provider === 'cloud';
  const tplBodyIssues = templateBodyIssues(tplBody);

  const sevenDayReadiness = useMemo(
    () => evaluateSevenDayReadiness({
      days: campDays,
      configs: DAY_CONFIG,
      agentSettings,
      usingCloud: !!usingCloud,
      templates: cloudStatus?.templates ?? [],
    }),
    [campDays, agentSettings, usingCloud, cloudStatus?.templates],
  );

  // Keep Running off until every day is Ready / Meta-approved (same gate as ON).
  useEffect(() => {
    if (!sevenDayReadiness.canActivate && campActive) {
      setCampActive(false);
    }
  }, [sevenDayReadiness.canActivate, campActive]);

  useEffect(() => {
    if (!usingCloud) {
      setEngagementSpend(null);
      setSpendEstimated(false);
      setMessageTemplateSpend(null);
      return;
    }
    let cancelled = false;
    fetchWhatsAppCloudSpend()
      .then((s) => {
        if (cancelled) return;
        setEngagementSpend(s.engagement);
        setSpendEstimated(s.estimated !== false);
        setMessageTemplateSpend(Object.fromEntries(
          (s.messageTemplates ?? []).map((t) => [String(t.templateId), t.cost]),
        ));
      })
      .catch(() => {
        if (!cancelled) {
          setEngagementSpend(null);
          setSpendEstimated(false);
          setMessageTemplateSpend(null);
        }
      });
    return () => { cancelled = true; };
  }, [usingCloud, cloudStatus?.account?.connected]);

  // ─── Template handlers ───
  const handleSaveTemplate = async () => {
    if (!tplTitle || !tplBody) return;
    if (tplBodyIssues.length > 0) return;
    setTplSaving(true);
    setTplError(null);
    try {
      const saved = editingTemplate
        ? await updateTemplate(editingTemplate.Id, { Title: tplTitle, Body: tplBody, Type: tplType, Channel: 'whatsapp' })
        : await createTemplate({ Title: tplTitle, Body: tplBody, Type: tplType, Channel: 'whatsapp' });
      setTemplates((prev) => [saved, ...prev.filter((t) => t.Id !== saved.Id)]);
      setShowTemplateDialog(false);
      setEditingTemplate(null);
      resetTemplateForm();
    } catch (e) {
      console.error(e);
      setTplError(e instanceof Error ? e.message : 'Could not save the template');
    } finally {
      setTplSaving(false);
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    try {
      await deleteTemplate(id);
      setTemplates((prev) => prev.filter((t) => t.Id !== id));
    } catch (e) { console.error(e); }
  };

  /** Pull Meta approval state for the message templates (Official API). */
  const handleRefreshMessageTemplates = async () => {
    setRefreshingMessageTemplates(true);
    setMessageTemplatesError(null);
    try {
      setTemplates(await refreshTemplateStatuses());
    } catch (e) {
      setMessageTemplatesError(e instanceof Error ? e.message : 'Could not refresh template status');
    } finally {
      setRefreshingMessageTemplates(false);
    }
  };

  const openEditTemplate = (t: MessageTemplate) => {
    setEditingTemplate(t);
    setTplTitle(t.Title); setTplBody(t.Body); setTplType(t.Type); setTplChannel(t.Channel);
    setTplError(null);
    setShowTemplateDialog(true);
  };

  const resetTemplateForm = () => {
    setTplTitle(''); setTplBody(''); setTplType('intro'); setTplChannel('whatsapp');
    setTplError(null);
  };

  const closeTemplateDialog = () => {
    if (tplSaving) return;
    setShowTemplateDialog(false);
    setEditingTemplate(null);
  };

  const insertPlaceholder = (ph: string, setter: React.Dispatch<React.SetStateAction<string>>) => {
    setter((prev) => (fitsBodyLimit(prev, prev + ph) ? prev + ph : prev));
  };

  // ─── Engagement handlers ───
  const patchDay = (idx: number, patch: Partial<EngagementDay>) => {
    setCampDays((prev) => {
      const next = [...prev];
      const merged = { ...next[idx], ...patch };
      if (patch.enabled === true) {
        merged.whatsapp = { ...merged.whatsapp, enabled: true };
      }
      next[idx] = merged;
      return next;
    });
  };

  const patchChannel = (idx: number, channel: 'whatsapp' | 'email', patch: Partial<DayChannel>) => {
    setCampDays((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [channel]: { ...next[idx][channel], ...patch } };
      return next;
    });
  };

  const buildDaysPayload = (days: EngagementDay[]): CampaignDayInput[] => {
    const daysPayload: CampaignDayInput[] = [];
    days.forEach((d) => {
      const cfg = DAY_CONFIG[d.dayNumber - 1];
        const ch = d.whatsapp;
      const isActive = d.enabled && ch.enabled;
      const hasAny = ch.message.trim() || ch.mediaUrl;
      // Keep inactive days in the payload when they have content so template
      // sync/save works while the day is switched off.
      if (!isActive && !hasAny && cfg.kind !== 'event') return;
        daysPayload.push({
          dayNumber: d.dayNumber,
        channel: 'whatsapp',
        isActive,
          messageTemplate: ch.message,
          mediaUrl: d.dayNumber === 4 ? null : (ch.mediaUrl || null),
        ctaLabel: null,
        ctaUrl: null,
      });
    });
    return daysPayload;
  };

  const persistCampaignDays = async (days: EngagementDay[] = campDays) => {
    await createCampaign({ Name: ENGAGEMENT_CAMPAIGN_NAME, days: buildDaysPayload(days) });
  };

  const persistSchedule = async () => {
    await updateAgentSettings({
          engagementActive: campActive,
          engagementStartDayOffset: campStartDay,
    });
  };

  const handleSaveSchedule = async () => {
    setScheduleSaving(true);
    try {
      await persistSchedule();
      setScheduleDirty(false);
      const settings = await fetchAgentSettings().catch(() => null);
      if (settings) setAgentSettings(settings);
    } catch (e) { console.error(e); }
    finally { setScheduleSaving(false); }
  };

  const handleSaveMessagesPerHour = async () => {
    setMphSaving(true);
    try {
      const updated = await updateAgentSettings({
          engagementMessagesPerHour: campMessagesPerHour,
      });
      setAgentSettings(updated);
      setMphDirty(false);
    } catch (e) { console.error(e); }
    finally { setMphSaving(false); }
  };

  const handleSaveDailyBudget = async () => {
    setBudgetSaving(true);
    try {
      const trimmed = dailyBudgetInput.trim();
      const parsed = trimmed === '' ? null : Number(trimmed);
      const value = parsed == null || !Number.isFinite(parsed) || parsed <= 0
        ? null
        : Math.round(parsed * 100) / 100;
      const updated = await updateAgentSettings({
        whatsAppCloudDailyBudgetSgd: value,
      });
      setAgentSettings(updated);
      setDailyBudgetInput(value != null ? String(value) : '');
      setBudgetDirty(false);
      await reloadCloudStatus();
    } catch (e) { console.error(e); }
    finally { setBudgetSaving(false); }
  };

  const goIntegrations = () => {
    if (isCampaigns) navigate('/super-connector/settings');
    else setTab(0);
  };
  const goEngagement = () => {
    if (!isCampaigns) navigate('/super-connector/campaigns', { state: { tab: 1 } });
    else setTab(1);
  };

  /** Save from the day editor — writes day content to our DB, then closes. */
  const handleSaveDayFromDialog = async (): Promise<void> => {
    setEngagementSaving(true);
    try {
      await persistCampaignDays();
      setTemplateSync(null);
      setEditingDay(null);
      await loadData();
    } catch (e) {
      console.error(e);
      throw e;
    } finally {
      setEngagementSaving(false);
    }
  };

  /** On/off toggles on the day list save immediately — no separate Save needed. */
  const handleDayToggle = async (idx: number, enabled: boolean) => {
    if (enabled && cloudStatus?.provider === 'cloud') {
      const status = cloudStatus?.templates?.find((t) => t.dayNumber === campDays[idx]?.dayNumber)?.status;
      if ((status || '').toUpperCase() !== 'APPROVED') return;
    }
    const nextDays = campDays.map((d, i) => {
      if (i !== idx) return d;
      const merged = { ...d, enabled };
      if (enabled) merged.whatsapp = { ...merged.whatsapp, enabled: true };
      return merged;
    });
    setCampDays(nextDays);
    setEngagementSaving(true);
    try {
      await persistCampaignDays(nextDays);
    } catch (e) {
      console.error(e);
      await loadData();
    } finally {
      setEngagementSaving(false);
    }
  };

  /**
   * Submit one day to Meta for review. Saves first so the reviewer sees exactly
   * what is on screen, then spends that day's single 24-hour edit.
   */
  const handleSyncDay = async (dayNumber: number) => {
    if (dayNumber === 6 && !(agentSettings?.hasCalendlyKey && agentSettings?.calendlyEventTypeUri)) {
      setTemplateSync([
        {
          dayNumber,
          templateName: '',
          status: null,
          action: 'failed',
          error: 'Connect Calendly and pick an event type before creating the Day 6 template.',
        },
      ]);
      return;
    }
    const day = campDays.find((d) => d.dayNumber === dayNumber);
    const bodyIssues = day ? templateBodyIssues(day.whatsapp.message) : [];
    if (bodyIssues.length > 0) {
      setTemplateSync([
        {
          dayNumber,
          templateName: '',
          status: null,
          action: 'failed',
          error: bodyIssues.join(' '),
        },
      ]);
      return;
    }
    setSyncingDay(dayNumber);
    setTemplateSync(null);
    try {
      await persistCampaignDays();
      const result = await syncEngagementTemplates(dayNumber);
      setTemplateSync(result.days);
      setCloudStatus((prev) => (prev ? { ...prev, templates: result.templates } : prev));
    } catch (e) {
      setTemplateSync([
        {
          dayNumber,
          templateName: '',
          status: null,
          action: 'failed',
          error: e instanceof Error ? e.message : 'Could not submit this day to Meta',
        },
      ]);
    } finally {
      setSyncingDay(null);
    }
  };

  /** Pull the live approval state from Meta, for when a webhook was missed. */
  const handleRefreshTemplateStatuses = async () => {
    setRefreshingTemplates(true);
    try {
      const { templates } = await refreshWhatsAppCloud();
      setCloudStatus((prev) => (prev ? { ...prev, templates } : prev));
    } catch (e) { console.error(e); }
    finally { setRefreshingTemplates(false); }
  };

  // ─── Calendly handlers ───
  const handleConnectCalendly = async () => {
    if (!calendlyKeyInput.trim()) return;
    setCalendlyBusy(true);
    setCalendlyError(null);
    try {
      const { eventTypes: ets } = await connectCalendly(calendlyKeyInput.trim());
      setEventTypes(ets);
      setCalendlyKeyInput('');
      const settings = await fetchAgentSettings();
      setAgentSettings(settings);
    } catch (e) {
      setCalendlyError(e instanceof Error ? e.message : 'Failed to connect');
    } finally {
      setCalendlyBusy(false);
    }
  };

  const handleDisconnectCalendly = async () => {
    setCalendlyBusy(true);
    try {
      await disconnectCalendly();
      setEventTypes([]);
      const settings = await fetchAgentSettings();
      setAgentSettings(settings);
    } catch (e) {
      setCalendlyError(e instanceof Error ? e.message : 'Failed to disconnect');
    } finally { setCalendlyBusy(false); }
  };

  const handleSelectEventType = async (uri: string) => {
    setCalendlyBusy(true);
    try {
      const updated = await updateAgentSettings({ calendlyEventTypeUri: uri || null });
      setAgentSettings(updated);
    } catch (e) {
      console.error(e);
    } finally { setCalendlyBusy(false); }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  // Settings mode: Integrations (0) · Manage Tags (1) · Access Codes (2)
  const manageTagsTabIdx = 1;
  const accessCodesTabIdx = 2;

  // Approval overview, counted across the days the agent actually has switched on.
  const templateCounts = (() => {
    const counts = { approved: 0, pending: 0, problem: 0, missing: 0 };
    campDays.forEach((day) => {
      if (!day.enabled) return;
      const status = cloudStatus?.templates?.find((t) => t.dayNumber === day.dayNumber)?.status;
      if (!status) counts.missing += 1;
      else if (status.toUpperCase() === 'APPROVED') counts.approved += 1;
      else if (status.toUpperCase() === 'PENDING') counts.pending += 1;
      else counts.problem += 1;
    });
    return counts;
  })();

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        {isCampaigns ? 'Campaigns' : 'Settings'}
      </Typography>

      {isCampaigns ? (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 3 }}>
        <Tab label="Message Templates" sx={{ textTransform: 'none' }} />
        <Tab label="7-Day Engagement" sx={{ textTransform: 'none' }} />
        <Tab label="Custom Campaigns" sx={{ textTransform: 'none' }} />
        </Tabs>
      ) : (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 3 }}>
        <Tab label="Integrations" sx={{ textTransform: 'none' }} />
        <Tab label="Manage Tags" sx={{ textTransform: 'none' }} />
        {accessCodeQuota > 0 && <Tab label="Access Codes" sx={{ textTransform: 'none' }} />}
      </Tabs>
      )}

      {/* ═══ Campaigns · Message Templates ═══ */}
      {isCampaigns && tab === 0 && (
        <Box>
          {usingCloud && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)', mb: 1 }}>
                On the Official API, saving a template submits it to Meta. It can be sent to clients once Meta approves it.
                Each send is estimated at S$0.10 and counts toward your daily budget.
              </Typography>
              <MessageTemplateStatusLegend />
            </Box>
          )}
          {messageTemplatesError && (
            <Alert severity="error" onClose={() => setMessageTemplatesError(null)} sx={{ mb: 2 }}>
              {messageTemplatesError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
            {usingCloud && (
              <Button
                variant="outlined"
                onClick={() => { void handleRefreshMessageTemplates(); }}
                disabled={refreshingMessageTemplates || !cloudStatus?.account.connected}
                sx={{ textTransform: 'none' }}
              >
                {refreshingMessageTemplates ? 'Checking…' : 'Refresh status'}
              </Button>
            )}
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => { resetTemplateForm(); setEditingTemplate(null); setShowTemplateDialog(true); }}
              sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
            >
              New Template
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
            {TEMPLATE_TYPES.map((t) => (
              <Chip key={t} label={TEMPLATE_TYPE_LABELS[t]} variant="outlined" size="small" />
            ))}
          </Box>

          <Grid container spacing={2}>
            {templates.filter((t) => t.Channel === 'whatsapp').map((template) => (
              <Grid item xs={12} sm={6} md={4} key={template.Id}>
                <Card sx={{ bgcolor: 'var(--color-surface)', height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', mb: 1 }}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700}>{template.Title}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          <Chip label={TEMPLATE_TYPE_LABELS[template.Type] || template.Type} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
                          {usingCloud && <MessageTemplateMetaStatus template={template} loading={refreshingMessageTemplates} />}
                          {usingCloud && messageTemplateSpend && (
                            <MessageTemplateSpendChip cost={messageTemplateSpend[String(template.Id)] ?? 0} />
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <IconButton size="small" onClick={() => openEditTemplate(template)} sx={{ color: 'var(--color-text-subtle)' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteTemplate(template.Id)} sx={{ color: 'var(--color-text-faint)' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)', whiteSpace: 'pre-wrap', fontSize: '0.8rem', maxHeight: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {template.Body}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          {templates.filter((t) => t.Channel === 'whatsapp').length === 0 && (
            <Typography sx={{ color: 'var(--color-text-faint)', textAlign: 'center', py: 4 }}>No WhatsApp templates yet</Typography>
          )}
        </Box>
      )}

      {/* ═══ Campaigns · 7-Day Engagement ═══ */}
      {isCampaigns && tab === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 1 }}>
          <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)' }}>
            A fixed seven-message sequence for every client you enrol. Save the schedule in step 1,
            edit and save each day in step {usingCloud ? 3 : 2},{usingCloud ? ' then submit each one to Meta.' : '.'}
          </Typography>

          {/* ─── Step 1: schedule ─── */}
          <EngagementSection
            step={1}
            title="Schedule"
            subtitle="When the sequence starts."
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={campActive}
                  disabled={!campActive && !sevenDayReadiness.canActivate}
                  onChange={(e) => {
                    const next = e.target.checked;
                    if (next && !sevenDayReadiness.canActivate) return;
                    setCampActive(next);
                    setScheduleDirty(true);
                  }}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#ed8936' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ed8936' } }}
                />
              }
                label={
                  <Typography variant="body2" fontWeight={700} sx={{ color: campActive ? '#22c55e' : ATTENTION_RED }}>
                    {campActive ? 'Running' : 'Paused — nothing will send'}
                  </Typography>
                }
            />
            {!campActive && !sevenDayReadiness.canActivate && (
              <Typography variant="caption" sx={{ color: ATTENTION_RED, display: 'block', ml: 6, mt: -0.5 }}>
                {readinessWarningText(sevenDayReadiness.reasons)}
              </Typography>
            )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)' }}>Start</Typography>
                <Select size="small" value={campStartDay} onChange={(e) => { setCampStartDay(Number(e.target.value)); setScheduleDirty(true); }} sx={{ minWidth: 96 }}>
                  {[0, 1, 2, 3, 5, 7, 14].map((d) => (
                    <MenuItem key={d} value={d}>{d === 0 ? 'Same day' : `${d} day${d === 1 ? '' : 's'}`}</MenuItem>
                  ))}
              </Select>
                <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)' }}>after a client is enrolled</Typography>
            </Box>
            </Box>
            <Typography variant="caption" sx={{ mt: 1.5, color: 'var(--color-text-faint)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
              Messages send between 9am and 7pm. Set the WhatsApp send rate under{' '}
              <Button size="small" onClick={goIntegrations} sx={{ textTransform: 'none', p: 0, minWidth: 0, verticalAlign: 'baseline', fontSize: 'inherit' }}>
                Settings → Integrations
              </Button>.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleSaveSchedule}
                disabled={!scheduleDirty || scheduleSaving}
                startIcon={scheduleSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
                sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none', minWidth: 120 }}
              >
                {scheduleSaving ? 'Saving…' : 'Save schedule'}
              </Button>
            </Box>
          </EngagementSection>

          {/* ─── Step 2 (Official API only): Meta approval overview ───
              Meta's template rules only apply on the Cloud API, so agents on the
              paired bridge never see this section. */}
          {usingCloud && (
            <EngagementSection
              step={2}
              title="Meta approval"
              subtitle="Every message must be approved by Meta before it can be sent."
              action={
                <Button
                size="small"
                  variant="outlined"
                  onClick={handleRefreshTemplateStatuses}
                  disabled={refreshingTemplates || !cloudStatus?.account.connected}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                  {refreshingTemplates ? 'Checking…' : 'Refresh status'}
                </Button>
              }
            >
              {!cloudStatus?.account.connected ? (
                <Alert severity="warning" icon={<CloudIcon />}>
                  <Typography variant="caption">
                    Your WhatsApp Business account isn't connected, so templates can't be created.
                    {' '}
                    <Button size="small" onClick={goIntegrations} sx={{ textTransform: 'none', p: 0, minWidth: 0, verticalAlign: 'baseline' }}>
                      Open Integrations
                    </Button>
              </Typography>
                </Alert>
              ) : (
                <>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5, alignItems: 'center' }}>
                    <TemplateCountChip label="Approved" count={templateCounts.approved} color="#22c55e" />
                    <TemplateCountChip label="In review" count={templateCounts.pending} color="#ed8936" />
                    <TemplateCountChip label="Needs attention" count={templateCounts.problem} color={ATTENTION_RED} />
                    <TemplateCountChip label="Not created yet" count={templateCounts.missing} color="#94a3b8" />
                    {engagementSpend && (
                      <Chip
                        label={spendEstimated
                          ? `Est. ${formatSgd(engagementSpend.totalCost)} spent`
                          : `${formatSgd(engagementSpend.totalCost)} spent`}
                        size="small"
                        sx={{ height: 22, fontWeight: 700, bgcolor: 'rgba(34,197,94,0.12)', color: '#16a34a' }}
                      />
                    )}
            </Box>
                  {spendEstimated && (
                    <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', mb: 1 }}>
                      {SPEND_ESTIMATE_NOTICE}
                    </Typography>
                  )}
                  <Typography variant="caption" component="div" sx={{ color: 'var(--color-text-subtle)' }}>
                    Open a day, press <b>Edit</b>, then <b>Create template</b> to send it to Meta for review.
                    Use <b>Sync to Meta</b> in the Edit dialog to push changes.
                  </Typography>
                </>
              )}
            </EngagementSection>
          )}

          {/* ─── Step 3: the seven messages ─── */}
          <EngagementSection
            step={usingCloud ? 3 : 2}
            title="The seven messages"
            subtitle="What each day sends, in order. Switch a day off to skip it."
          >
          {campDays.map((day, idx) => (
              <DayRow
              key={day.dayNumber}
              day={day}
              idx={idx}
              cfg={DAY_CONFIG[idx]}
              campStartDay={campStartDay}
                isLast={idx === campDays.length - 1}
                onPatchDay={(idx, patch) => {
                  if (Object.prototype.hasOwnProperty.call(patch, 'enabled') && patch.enabled != null) {
                    void handleDayToggle(idx, patch.enabled);
                  } else {
                    patchDay(idx, patch);
                  }
                }}
                onEdit={() => setEditingDay(idx)}
                agentSettings={agentSettings}
                usingCloud={usingCloud}
                templateState={
                  usingCloud
                    ? cloudStatus?.templates?.find((t) => t.dayNumber === day.dayNumber) ?? null
                    : null
                }
                spendCost={usingCloud
                  ? (engagementSpend?.days.find((d) => d.dayNumber === day.dayNumber)?.cost ?? 0)
                  : null}
                metaStatusLoading={refreshingTemplates}
                busy={engagementSaving || syncingDay !== null}
              />
            ))}
          </EngagementSection>

          {editingDay !== null && campDays[editingDay] && (
            <DayEditorDialog
              open
              onClose={() => {
                if (!engagementSaving && syncingDay === null) {
                  setTemplateSync(null);
                  setEditingDay(null);
                }
              }}
              onSave={handleSaveDayFromDialog}
              saving={engagementSaving}
              day={campDays[editingDay]}
              idx={editingDay}
              cfg={DAY_CONFIG[editingDay]}
              campStartDay={campStartDay}
              onPatchDay={patchDay}
              onPatchChannel={(i, channel, patch) => {
                setCampDays((prev) => {
                  const next = prev.map((d, j) => (
                    j === i ? { ...d, [channel]: { ...d[channel], ...patch } } : d
                  ));
                  if (Object.prototype.hasOwnProperty.call(patch, 'mediaUrl')) {
                    void persistCampaignDays(next).catch((e) => console.error(e));
                  }
                  return next;
                });
              }}
              agentSettings={agentSettings}
              eventTypes={eventTypes}
              onGotoIntegrations={goIntegrations}
              usingCloud={usingCloud}
              cloudConnected={!!cloudStatus?.account.connected}
              templateState={
                usingCloud
                  ? cloudStatus?.templates?.find((t) => t.dayNumber === campDays[editingDay].dayNumber) ?? null
                  : null
              }
              onSyncTemplate={() => handleSyncDay(campDays[editingDay].dayNumber)}
              syncingTemplate={syncingDay === campDays[editingDay].dayNumber}
              syncResult={templateSync?.find((d) => d.dayNumber === campDays[editingDay].dayNumber) ?? null}
            />
          )}

          {/* Test send — dry-runs a day to a real client or an ad-hoc phone/email */}
            <TestSendCard
              agentSettings={agentSettings}
            cloudStatus={cloudStatus}
              campDays={campDays}
              clients={clients}
            onGotoIntegrations={goIntegrations}
            />
        </Box>
      )}

      {/* ═══ Campaigns · Custom Campaigns ═══ */}
      {isCampaigns && tab === 2 && (
        <CampaignsTab
          onGotoIntegrations={goIntegrations}
          cloudStatus={cloudStatus}
          agentSettings={agentSettings}
        />
      )}

      {/* ═══ Settings · Integrations ═══ */}
      {!isCampaigns && tab === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Tabs
            value={integrationsSubTab}
            onChange={(_, v) => setIntegrationsSubTab(v)}
            sx={{ mb: 1, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none' } }}
          >
            <Tab label="Sending methods" />
            <Tab label="Meetings" />
          </Tabs>

          {integrationsSubTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* WhatsApp — one card: send rate + both senders */}
              <Card sx={{ bgcolor: 'var(--color-surface)' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <WhatsAppIcon sx={{ color: '#25D366' }} />
                      <Typography variant="subtitle1" fontWeight={700}>WhatsApp</Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)', display: 'block' }}>
                      Agent WhatsApp and the Official API are alternative senders — pick one. The send rate below
                      applies to all automated WhatsApp campaign sends (7-Day and Custom).
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>Messages per hour</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)' }}>Send at most</Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={campMessagesPerHour}
                        onChange={(e) => {
                          const v = Math.min(50, Math.max(1, Number(e.target.value) || 0));
                          setCampMessagesPerHour(v);
                          setMphDirty(true);
                        }}
                        onBlur={() => { if (mphDirty) void handleSaveMessagesPerHour(); }}
                        inputProps={{ min: 1, max: 50, style: { width: 56 } }}
                      />
                      <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)' }}>messages per hour</Typography>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSaveMessagesPerHour}
                        disabled={!mphDirty || mphSaving}
                        sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none', ml: { sm: 1 } }}
                      >
                        {mphSaving ? 'Saving…' : 'Save send rate'}
                      </Button>
                    </Box>
                    <Typography variant="caption" sx={{ mt: 1.5, color: 'var(--color-text-faint)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                      Shared across every automated WhatsApp campaign.
                      {usingCloud
                        ? ' Official API sends follow this hourly cap only.'
                        : ` Sends are spread between 9am and 7pm — roughly ${Math.max(1, Math.round(60 / Math.max(1, campMessagesPerHour)))} minutes apart at this setting.`}
                    </Typography>
                  </Box>

                  {usingCloud && (
                    <Box>
                      <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>Daily budget (SGD)</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)' }}>Cap Official API spend at</Typography>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="No limit"
                          value={dailyBudgetInput}
                          onChange={(e) => {
                            setDailyBudgetInput(e.target.value);
                            setBudgetDirty(true);
                          }}
                          onBlur={() => { if (budgetDirty) void handleSaveDailyBudget(); }}
                          inputProps={{ min: 0, step: 0.01, style: { width: 72 } }}
                        />
                        <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)' }}>SGD / day</Typography>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleSaveDailyBudget}
                          disabled={!budgetDirty || budgetSaving}
                          sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none', ml: { sm: 1 } }}
                        >
                          {budgetSaving ? 'Saving…' : 'Save budget'}
                        </Button>
                      </Box>
                      <Typography variant="caption" sx={{ mt: 1.5, color: 'var(--color-text-faint)', display: 'block' }}>
                        {(() => {
                          const n = Number(dailyBudgetInput);
                          if (!dailyBudgetInput.trim() || !Number.isFinite(n) || n <= 0) {
                            return 'Leave empty for no daily cap. Estimate uses S$0.10 per marketing message (Singapore).';
                          }
                          const msgs = Math.floor(n / 0.10);
                          return `About ${msgs} marketing message${msgs === 1 ? '' : 's'} per day at S$0.10 each. Automated sends pause when the next message would exceed this.`;
                        })()}
                      </Typography>
                      {cloudStatus?.dailyBudget?.paused && (
                        <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
                          <Typography variant="caption">
                            Sends are paused today — estimated S${cloudStatus.dailyBudget.estimatedSpendSgd.toFixed(2)} of
                            S${(cloudStatus.dailyBudget.limitSgd ?? 0).toFixed(2)} used ({cloudStatus.dailyBudget.sentToday} sent).
                          </Typography>
                        </Alert>
                      )}
                    </Box>
                  )}

                  <Alert severity="info" sx={{ py: 0.5 }}>
                    <Typography variant="caption">
                      Pick one WhatsApp sender. Turning one on switches the other off, and campaign messages
                      always go out through whichever is active.
                    </Typography>
                  </Alert>

                  <WhatsAppPairingCard
                    embedded
                    agentSettings={agentSettings}
                    cloudStatus={cloudStatus}
                    onSettingsReload={async () => {
                      const [s, live] = await Promise.all([
                        fetchAgentSettings().catch(() => null),
                        fetchWhatsAppStatus().catch(() => null),
                      ]);
                      if (!s) return;
                      setAgentSettings(live
                        ? { ...s, whatsAppStatus: live.status, whatsAppPhoneNumber: live.phone ?? s.whatsAppPhoneNumber ?? null }
                        : s);
                      await reloadCloudStatus();
                    }}
                  />

                  <WhatsAppCloudCard
                    embedded
                    cloudStatus={cloudStatus}
                    onReload={reloadCloudStatus}
                    onGotoEngagement={goEngagement}
                  />
                </CardContent>
              </Card>
            </Box>
          )}

          {integrationsSubTab === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Calendly */}
          <Card sx={{ bgcolor: 'var(--color-surface)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <EventIcon sx={{ color: '#006bff' }} />
                <Typography variant="subtitle1" fontWeight={700}>Calendly</Typography>
                {agentSettings?.hasCalendlyKey && (
                  <Chip icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />} label="Connected" size="small" sx={{ bgcolor: '#22c55e22', color: '#22c55e', fontWeight: 600, height: 22 }} />
                )}
              </Box>
              <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)', display: 'block', mb: 2 }}>
                Used on Day 6 of the engagement to send a scheduling link. Create a Personal Access Token at{' '}
                <Typography component="a" href="https://calendly.com/integrations/api_webhooks" target="_blank" rel="noreferrer" variant="caption" sx={{ color: '#ed8936' }}>
                  calendly.com/integrations/api_webhooks
                </Typography>.
              </Typography>

              {!agentSettings?.hasCalendlyKey ? (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    type="password"
                    placeholder="Calendly Personal Access Token"
                    value={calendlyKeyInput}
                    onChange={(e) => setCalendlyKeyInput(e.target.value)}
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    onClick={handleConnectCalendly}
                    disabled={!calendlyKeyInput.trim() || calendlyBusy}
                    sx={{ bgcolor: '#006bff', '&:hover': { bgcolor: '#0057cc' }, textTransform: 'none' }}
                  >
                    {calendlyBusy ? 'Connecting...' : 'Connect'}
                  </Button>
                </Box>
              ) : (
                <Box>
                  <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                    <InputLabel>Default event type</InputLabel>
                    <Select
                      label="Default event type"
                      value={agentSettings.calendlyEventTypeUri || ''}
                      onChange={(e) => handleSelectEventType(e.target.value as string)}
                      disabled={calendlyBusy}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {eventTypes.map((et) => (
                        <MenuItem key={et.uri} value={et.uri}>
                          {et.name} · {et.duration}min
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button size="small" color="error" onClick={handleDisconnectCalendly} disabled={calendlyBusy} sx={{ textTransform: 'none' }}>
                    Disconnect
                  </Button>
                </Box>
              )}
              {calendlyError && <Alert severity="error" sx={{ mt: 1 }}>{calendlyError}</Alert>}
            </CardContent>
          </Card>
            </Box>
          )}
        </Box>
      )}

      {/* ═══ Settings · Manage Tags ═══ */}
      {!isCampaigns && tab === manageTagsTabIdx && (
        <ManageTagsTab />
      )}

      {/* ═══ Settings · Access Codes ═══ */}
      {!isCampaigns && accessCodeQuota > 0 && tab === accessCodesTabIdx && <AccessCodesSection />}

      {/* ─── Template Dialog ─── */}
      <Dialog
        open={showTemplateDialog}
        onClose={closeTemplateDialog}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
      >
        <DialogTitle fontWeight={700}>
          {editingTemplate ? 'Edit Template' : 'New Template'}
          {editingTemplate && (<Chip label="ACTIVE" size="small" sx={{ ml: 1, bgcolor: '#22c55e22', color: '#22c55e', fontWeight: 700, height: 20 }} />)}
        </DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Template Title" value={tplTitle} onChange={(e) => setTplTitle(e.target.value)} sx={{ mt: 1, mb: 2 }} />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Type</InputLabel>
            <Select value={tplType} onChange={(e) => setTplType(e.target.value)} label="Type">
              {TEMPLATE_TYPES.map((t) => (<MenuItem key={t} value={t}>{TEMPLATE_TYPE_LABELS[t]}</MenuItem>))}
            </Select>
          </FormControl>
          <Typography variant="caption" sx={{ color: '#ed8936', mb: 0.5, display: 'block', fontWeight: 700 }}>MESSAGE BODY</Typography>
          <TemplateBodyField
            minRows={5}
            value={tplBody}
            onValueChange={(next) => setTplBody(next)}
            issues={tplBodyIssues}
            sx={{ mb: 1 }}
          />
          <Typography variant="caption" sx={{ color: '#ed8936', mb: 0.5, display: 'block', fontWeight: 700 }}>INSERT PLACEHOLDERS</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {PLACEHOLDERS.filter((ph) => !usingCloud || !CLOUD_UNSUPPORTED_PLACEHOLDERS.includes(ph)).map((ph) => (
              <Chip key={ph} label={ph.replace(/\{\{|\}\}/g, '#')} size="small" onClick={() => insertPlaceholder(ph, setTplBody)} sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(237, 137, 54, 0.15)' } }} />
            ))}
          </Box>
          {usingCloud && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'var(--color-text-faint)' }}>
              Saving submits this template to Meta for review. Meta allows one edit per template every 24 hours —
              a later change is submitted as a new template.
            </Typography>
          )}
          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'var(--color-text-faint)' }}>
            Do not start or end the message with a placeholder (a value in {`{{}}`}), keep placeholders apart,
            and use at most {TEMPLATE_BODY_MAX_EMOJIS} emojis.
          </Typography>
          {tplBodyIssues.length > 0 && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {tplBodyIssues.map((msg) => (
                  <Typography key={msg} variant="body2" component="div">{msg}</Typography>
                ))}
              </Box>
            </Alert>
          )}
          {tplError && <Alert severity="error" sx={{ mt: 1.5 }}>{tplError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeTemplateDialog} disabled={tplSaving} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveTemplate}
            disabled={!tplTitle || !tplBody || tplSaving || tplBodyIssues.length > 0}
            startIcon={tplSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
          >
            {tplSaving ? (usingCloud ? 'Submitting…' : 'Saving…') : usingCloud ? 'Save and submit to Meta' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Campaigns tab (admin-authored campaigns granted to this agent) ───
interface CustomDayState {
  dayNumber: number;
  message: string;
  imageUrl: string | null;
  videoUrl: string | null;
  documentUrl: string | null;
  voiceUrl: string | null;
  imageMediaId: string | null;
  videoMediaId: string | null;
  documentMediaId: string | null;
  voiceMediaId: string | null;
  imageSourceUnavailable: boolean;
  videoSourceUnavailable: boolean;
  documentSourceUnavailable: boolean;
  voiceSourceUnavailable: boolean;
  /** Official API: user chose None; keep media until save so switching back does not force re-upload. */
  headerNonePending: boolean;
  linkUrl: string;
  useCalendly: boolean;
  /** When true, media is sent as trackable links (not WhatsApp attachments). */
  isMediaTrackable: boolean;
  /** 'YYYY-MM-DD'; required per configured day when the campaign is one-time. */
  runDate: string;
  /** Daily sequences: extra SGT days to wait after this day before the next. */
  breakDaysAfter: number;
  status: string | null;
  templateId: string | null;
  templateName: string | null;
  templateSyncedAt: string | null;
  supersededTemplateId: string | null;
  supersededTemplateName: string | null;
  templateContentHash: string | null;
}

const CUSTOM_DAY_CONTENT_KEYS = [
  'message', 'imageUrl', 'videoUrl', 'documentUrl', 'voiceUrl',
  'imageMediaId', 'videoMediaId', 'documentMediaId', 'voiceMediaId',
  'imageSourceUnavailable', 'videoSourceUnavailable',
  'documentSourceUnavailable', 'voiceSourceUnavailable',
  'headerNonePending',
  'linkUrl', 'useCalendly',
  'isMediaTrackable',
  'status', 'templateId', 'templateName', 'templateSyncedAt',
  'supersededTemplateId', 'supersededTemplateName', 'templateContentHash',
] as const;

const formatRunDate = (isoDate: string): string => {
  const d = dayjs(isoDate, 'YYYY-MM-DD');
  return d.isValid() ? d.format('D MMM YYYY') : isoDate;
};

/** Tomorrow in Singapore. A one-time day cannot run on the current day. */
function earliestRunDate(): Dayjs {
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Singapore',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  return dayjs(today, 'YYYY-MM-DD').add(1, 'day');
}

const slotFor = (name: string, day: number, kind: string): string => {
  const safeName = name.replace(/[^a-zA-Z0-9]/g, '').slice(0, 24) || 'camp';
  return `c_${safeName}_d${day}_${kind}`.slice(0, 50);
};

const clampBreakDays = (n: number): number => Math.max(1, Math.min(30, Math.floor(Number.isFinite(n) ? n : 1)));

const dayMediaIds = (d: CustomDayState): string[] =>
  [d.imageMediaId, d.videoMediaId, d.documentMediaId, d.voiceMediaId].filter(Boolean) as string[];

/** Apply pending None: strip header/voice media for validation and save. */
const dayForPersist = (d: CustomDayState): CustomDayState => {
  if (!d.headerNonePending) return d;
  return {
    ...d,
    imageUrl: null,
    videoUrl: null,
    documentUrl: null,
    voiceUrl: null,
    imageMediaId: null,
    videoMediaId: null,
    documentMediaId: null,
    voiceMediaId: null,
    imageSourceUnavailable: false,
    videoSourceUnavailable: false,
    documentSourceUnavailable: false,
    voiceSourceUnavailable: false,
    isMediaTrackable: false,
    headerNonePending: false,
  };
};

const dayHasContent = (d: CustomDayState): boolean => {
  const effective = dayForPersist(d);
  return !!(effective.message.trim() || effective.imageUrl || effective.videoUrl || effective.documentUrl || effective.voiceUrl
    || dayMediaIds(effective).length || effective.linkUrl.trim() || effective.useCalendly);
};

/**
 * Body text Meta reviews for a custom day (mirrors backend
 * `customTemplateBodyText`): message, optional link, optional trackable-media trailer.
 */
function customCampaignMetaBodyText(day: CustomDayState): string {
  const d = dayForPersist(day);
  let text = (d.message || '').trim();
  const link = (d.linkUrl || '').trim();
  if (link) text = text ? `${text}\n\n${link}` : link;
  const hasMedia = !!(
    d.imageUrl || d.videoUrl || d.documentUrl || d.voiceUrl || dayMediaIds(d).length
  );
  if (d.isMediaTrackable && hasMedia) {
    const block = 'Open your attachment(s):\n{{tracked_media_links}}\nThank you.';
    text = text ? `${text}\n\n${block}` : block;
  }
  return text;
}

/** Template rules for a custom day, measured on the body Meta receives. */
function customDayBodyIssues(day: CustomDayState): string[] {
  const full = customCampaignMetaBodyText(day);
  const issues = templateBodyIssues(full, { checkLength: false });
  const messageLength = (day.message || '').length;
  if (messageLength > TEMPLATE_BODY_MAX_CHARS) {
    issues.push(bodyLengthIssue('Message', messageLength));
  } else if (full.length > TEMPLATE_BODY_MAX_CHARS) {
    issues.push(bodyLengthIssue('Message with its link and attachment lines', full.length));
  }
  return issues;
}

function pickCustomDayMetaFields(d: CustomDayState): Partial<CustomDayState> {
  return {
    status: d.status,
    templateId: d.templateId,
    templateName: d.templateName,
    templateSyncedAt: d.templateSyncedAt,
    supersededTemplateId: d.supersededTemplateId,
    supersededTemplateName: d.supersededTemplateName,
    templateContentHash: d.templateContentHash,
  };
}

function buildCustomDays(campaign: MyCampaign): CustomDayState[] {
  const byDay = new Map(campaign.days.map((d) => [d.DayNumber, d]));
  return Array.from({ length: campaign.numDays }, (_, i) => {
    const n = i + 1;
    const row = byDay.get(n);
    const media = row?.media || [];
    const image = media.find((m) => m.mediaType === 'image');
    const video = media.find((m) => m.mediaType === 'video');
    const document = media.find((m) => m.mediaType === 'document');
    const voice = media.find((m) => m.mediaType === 'voice');
    return {
      dayNumber: n,
      message: row?.MessageTemplate || '',
      imageUrl: image?.url || row?.ImageUrl || null,
      videoUrl: video?.url || row?.VideoUrl || null,
      documentUrl: document?.url || row?.DocumentUrl || null,
      voiceUrl: voice?.url || row?.VoiceUrl || null,
      imageMediaId: image?.id || null,
      videoMediaId: video?.id || null,
      documentMediaId: document?.id || null,
      voiceMediaId: voice?.id || null,
      imageSourceUnavailable: !!image?.sourceUnavailable,
      videoSourceUnavailable: !!video?.sourceUnavailable,
      documentSourceUnavailable: !!document?.sourceUnavailable,
      voiceSourceUnavailable: !!voice?.sourceUnavailable,
      headerNonePending: false,
      linkUrl: row?.LinkUrl || '',
      useCalendly: !!row?.UseCalendly,
      isMediaTrackable: !!row?.IsMediaTrackable,
      runDate: (row?.RunDate || '').slice(0, 10),
      breakDaysAfter: clampBreakDays(row?.BreakDaysAfter ?? 1),
      status: row?.Status || null,
      templateId: row?.TemplateId || null,
      templateName: row?.TemplateName || null,
      templateSyncedAt: row?.TemplateSyncedAt || null,
      supersededTemplateId: row?.SupersededTemplateId || null,
      supersededTemplateName: row?.SupersededTemplateName || null,
      templateContentHash: row?.TemplateContentHash || null,
    };
  });
}

/** Swap content + Meta identity between slots; dayNumber / runDate / breakDaysAfter stay put. */
function reorderCustomDayContent(days: CustomDayState[], fromIndex: number, toIndex: number): CustomDayState[] {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return days;
  if (fromIndex >= days.length || toIndex >= days.length) return days;
  const contents = days.map((d) => {
    const c: Partial<CustomDayState> = {};
    for (const k of CUSTOM_DAY_CONTENT_KEYS) c[k] = d[k] as never;
    return c;
  });
  const [moved] = contents.splice(fromIndex, 1);
  contents.splice(toIndex, 0, moved);
  return days.map((slot, i) => ({
    ...slot,
    ...contents[i],
  }));
}

function CustomDayTemplateSection({
  label, hint, children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{
      border: '1px solid var(--color-border)',
      borderRadius: 1.5,
      overflow: 'hidden',
      bgcolor: 'var(--color-surface)',
    }}>
      <Box sx={{
        px: 1.5, py: 1,
        borderBottom: '1px solid var(--color-border)',
        bgcolor: 'var(--color-bg)',
        display: 'flex', flexDirection: 'column', gap: 0.15,
      }}>
        <Typography variant="caption" fontWeight={800} sx={{
          letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--color-text-subtle)',
        }}>
          {label}
        </Typography>
        {hint && (
          <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', lineHeight: 1.35 }}>
            {hint}
          </Typography>
        )}
      </Box>
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {children}
      </Box>
    </Box>
  );
}

/** Add / replace media for a custom-campaign day slot. */
function AddMediaDialog({
  open,
  mediaType,
  onClose,
  onPick,
}: {
  open: boolean;
  mediaType: ConnectorLibraryMediaType;
  onClose: () => void;
  onPick: (item: ConnectorLibraryMedia) => void;
}) {
  const peAllowed = mediaType === 'image' || mediaType === 'document';
  const [tab, setTab] = useState<'upload' | 'library' | 'pe'>('upload');
  const [items, setItems] = useState<ConnectorLibraryMedia[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);

  const typeLabel = mediaType === 'document' ? 'PDF' : mediaType === 'voice' ? 'voice note' : mediaType;

  useEffect(() => {
    if (!open) {
      setTab('upload');
      setQ('');
      setError(null);
      return;
    }
  }, [open]);

  useEffect(() => {
    if (!open || tab !== 'library') return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchMediaLibrary({ type: mediaType, q, limit: 50 });
        if (!cancelled) setItems(res.items);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load library');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, tab, mediaType, q]);

  const onFile = async (file: File | null) => {
    if (!file) return;
    const issue = libraryUploadIssue(file, mediaType);
    if (issue) {
      setError(issue);
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const dataUrl = await fileToDataUrl(asTypedUploadBlob(file));
      const item = await uploadLibraryMedia({ dataUrl, mediaType, name: file.name });
      onPick(item);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const accept = mediaType === 'image' ? 'image/*'
    : mediaType === 'video' ? 'video/*'
      : mediaType === 'document' ? 'application/pdf'
        : 'audio/*';

  return (
    <Dialog open={open} onClose={onClose} maxWidth={tab === 'pe' ? 'md' : 'sm'} fullWidth>
      <DialogTitle>Add {typeLabel}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
        <Tabs
          value={tab}
          onChange={(_, v: 'upload' | 'library' | 'pe') => setTab(v)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{
            minHeight: 36,
            '& .MuiTab-root': { textTransform: 'none', minHeight: 36, py: 0.5 },
            '& .Mui-selected': { color: '#ed8936 !important' },
            '& .MuiTabs-indicator': { bgcolor: '#ed8936' },
          }}
        >
          <Tab value="upload" icon={<UploadIcon fontSize="small" />} iconPosition="start" label="Upload" />
          <Tab value="library" icon={<PermMediaIcon fontSize="small" />} iconPosition="start" label="From library" />
          {peAllowed && (
            <Tab
              value="pe"
              icon={<AutoStoriesOutlinedIcon fontSize="small" />}
              iconPosition="start"
              label="Project Essential"
            />
          )}
        </Tabs>

        {error && <Alert severity="error">{error}</Alert>}

        {tab === 'upload' ? (
          <Box sx={{
            border: '1px dashed var(--color-border)',
            borderRadius: 1.5,
            p: 3,
            textAlign: 'center',
            bgcolor: 'rgba(0,0,0,0.015)',
          }}>
            <Typography variant="body2" sx={{ mb: 0.5, color: 'var(--color-text-subtle)' }}>
              Upload a new {typeLabel}. It will be saved to your Media library and attached to this day.
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'var(--color-text-faint)' }}>
              {uploadCapHint(mediaType)}
            </Typography>
            <Button
              component="label"
              variant="contained"
              disabled={uploading}
              startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
              sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
            >
              {uploading ? 'Uploading…' : 'Choose file'}
              <input
                hidden
                type="file"
                accept={accept}
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  e.target.value = '';
                  void onFile(f);
                }}
              />
            </Button>
          </Box>
        ) : tab === 'library' ? (
          <>
            <TextField
              size="small"
              placeholder="Search library…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : items.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No {typeLabel}s in your library yet. Switch to Upload to add one.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 360, overflow: 'auto' }}>
                {items.map((item) => (
                  <Box
                    key={item.id}
                    component="button"
                    type="button"
                    onClick={() => { onPick(item); onClose(); }}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.25, textAlign: 'left',
                      border: '1px solid var(--color-border)', borderRadius: 1, p: 1,
                      bgcolor: 'transparent', cursor: 'pointer',
                      '&:hover': { bgcolor: 'rgba(237,137,54,0.08)', borderColor: '#ed8936' },
                    }}
                  >
                    {item.mediaType === 'image' && item.url ? (
                      <Box
                        component="img"
                        src={item.url}
                        alt=""
                        sx={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 0.5, bgcolor: 'var(--color-bg)' }}
                      />
                    ) : (
                      <Box sx={{
                        width: 40, height: 40, display: 'grid', placeItems: 'center',
                        borderRadius: 0.5, bgcolor: 'rgba(0,0,0,0.04)',
                      }}>
                        {item.mediaType === 'video' ? <VideocamIcon />
                          : item.mediaType === 'document' ? <PictureAsPdfIcon sx={{ color: '#D32F2F' }} />
                            : <MicIcon />}
                      </Box>
                    )}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" noWrap fontWeight={600}>{item.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.openCount} open{item.openCount === 1 ? '' : 's'}
                        {item.firstSentAt ? ' · sent' : ''}
                        {item.sourceUnavailable ? ' · Unavailable' : ''}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </>
        ) : peAllowed ? (
          <ProjectEssentialMediaPicker
            mediaTypeFilter={mediaType}
            onImported={(item) => {
              onPick(item);
              onClose();
            }}
          />
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

type MediaSlotKind = 'image' | 'video' | 'document' | 'voice';

/** Attach one library media item to a custom-day slot. */
function LibraryMediaSlot({
  kind,
  url,
  mediaId,
  sourceUnavailable = false,
  onChange,
  exclusiveClear,
}: {
  kind: MediaSlotKind;
  url: string | null;
  mediaId: string | null;
  sourceUnavailable?: boolean;
  onChange: (patch: Partial<CustomDayState>) => void;
  exclusiveClear?: Partial<CustomDayState>;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const hasMedia = !!(url || mediaId);
  const kindLabel = kind === 'document' ? 'PDF' : kind === 'voice' ? 'voice note' : kind;
  const unavailableKey = `${kind}SourceUnavailable` as const;

  const applyItem = (item: ConnectorLibraryMedia | null) => {
    const urlKey = `${kind}Url` as const;
    const idKey = `${kind}MediaId` as const;
    onChange({
      [urlKey]: item?.url ?? null,
      [idKey]: item?.id ?? null,
      [unavailableKey]: !!(item?.sourceUnavailable),
      ...(exclusiveClear || {}),
    } as Partial<CustomDayState>);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, flex: 1, minWidth: 0 }}>
      {hasMedia ? (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap',
          border: `1px solid ${sourceUnavailable ? ATTENTION_RED : 'var(--color-border)'}`,
          borderRadius: 1, p: 1,
          bgcolor: sourceUnavailable ? 'rgba(239,68,68,0.06)' : 'rgba(0,0,0,0.015)',
        }}>
          {kind === 'image' && url && (
            <Box
              component="img"
              src={url}
              alt=""
              sx={{
                width: 56, height: 56, objectFit: 'contain', borderRadius: 0.5,
                bgcolor: 'var(--color-bg)', border: '1px solid var(--color-border)',
              }}
            />
          )}
          {kind === 'video' && (
            <Box sx={{
              width: 56, height: 56, borderRadius: 0.5, bgcolor: 'rgba(57,73,171,0.12)',
              display: 'grid', placeItems: 'center',
            }}>
              <VideocamIcon sx={{ color: '#3949AB' }} />
            </Box>
          )}
          {kind === 'document' && (
            <Box sx={{
              width: 56, height: 56, borderRadius: 0.5, bgcolor: 'rgba(211,47,47,0.12)',
              display: 'grid', placeItems: 'center',
            }}>
              <PictureAsPdfIcon sx={{ color: '#D32F2F' }} />
            </Box>
          )}
          {kind === 'voice' && url && (
            <audio controls src={url} style={{ height: 32, maxWidth: 200 }} />
          )}
          <Box sx={{ flex: 1, minWidth: 120 }}>
            <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
              {kindLabel} attached
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {sourceUnavailable
                ? 'Project Essential item unavailable — replace before sending'
                : 'Saved in Media library'}
            </Typography>
          </Box>
          {sourceUnavailable && (
          <Chip
            size="small"
              label="Unavailable"
              sx={{ height: 22, bgcolor: 'rgba(239,68,68,0.15)', color: ATTENTION_RED, fontWeight: 700 }}
            />
          )}
          <Button size="small" variant="outlined" onClick={() => setDialogOpen(true)} sx={{ textTransform: 'none' }}>
            Replace
          </Button>
          <Button size="small" color="error" onClick={() => applyItem(null)} sx={{ textTransform: 'none' }}>
            Remove
          </Button>
      </Box>
      ) : kind === 'voice' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <VoiceRecorder
            url={null}
            slot="library_voice"
            onUploaded={() => undefined}
            onLibraryItem={(item) => applyItem(item)}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<PermMediaIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
          >
            Or choose from Media library
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
          >
            Add {kindLabel}
          </Button>
        </Box>
      )}

      <AddMediaDialog
        open={dialogOpen}
        mediaType={kind}
        onClose={() => setDialogOpen(false)}
        onPick={(item) => applyItem(item)}
      />
    </Box>
  );
}

function CampaignDayEditorBody({
  day, onChange, campaignName, hasCalendly, scheduleType, usingCloud, onGotoIntegrations,
}: {
  day: CustomDayState;
  onChange: (patch: Partial<CustomDayState>) => void;
  campaignName: string;
  hasCalendly: boolean;
  scheduleType: CampaignScheduleType;
  usingCloud: boolean;
  onGotoIntegrations: () => void;
}) {
  const isOneTime = scheduleType === 'one_time';
  const missingRunDate = isOneTime && dayHasContent(day) && !day.runDate;
  const bodyIssues = customDayBodyIssues(day);

  useEffect(() => {
    if (!isOneTime) return;
    const earliest = earliestRunDate().format('YYYY-MM-DD');
    if (!day.runDate || day.runDate < earliest) {
      onChange({ runDate: earliest });
    }
  }, [isOneTime, day.runDate, day.dayNumber]);
  type HeaderMode = 'none' | 'image' | 'video' | 'document';
  const deriveHeaderMode = (d: CustomDayState): HeaderMode => {
    if (d.imageUrl || d.imageMediaId) return 'image';
    if (d.videoUrl || d.videoMediaId) return 'video';
    if (d.documentUrl || d.documentMediaId) return 'document';
    return 'none';
  };
  // Controlled by explicit choice — not by whether an upload exists yet —
  // otherwise picking a type with an empty slot snaps straight back to None.
  const [headerMode, setHeaderMode] = useState<HeaderMode>(() => (
    day.headerNonePending ? 'none' : deriveHeaderMode(day)
  ));

  useEffect(() => {
    if (day.headerNonePending) {
      setHeaderMode('none');
      return;
    }
    setHeaderMode(deriveHeaderMode(day));
  }, [
    day.dayNumber,
    day.headerNonePending,
    day.imageUrl, day.videoUrl, day.documentUrl,
    day.imageMediaId, day.videoMediaId, day.documentMediaId,
  ]);

  const voiceBlocked = usingCloud && !day.headerNonePending && !!(day.voiceUrl || day.voiceMediaId) && !day.isMediaTrackable;
  const multiHeader = usingCloud && !day.headerNonePending && [
    day.imageUrl || day.imageMediaId,
    day.videoUrl || day.videoMediaId,
    day.documentUrl || day.documentMediaId,
  ].filter(Boolean).length > 1;
  const hasMedia = !day.headerNonePending && !!(day.imageUrl || day.videoUrl || day.documentUrl || day.voiceUrl
    || day.imageMediaId || day.videoMediaId || day.documentMediaId || day.voiceMediaId);
  const trackLocked = usingCloud && !!day.templateId;

  /** Patch media fields and keep isMediaTrackable in sync (default on when first media added). */
  const patchMedia = (patch: Partial<CustomDayState>) => {
    const next = { ...day, ...patch, headerNonePending: false };
    const nextHas = !!(next.imageUrl || next.videoUrl || next.documentUrl || next.voiceUrl
      || next.imageMediaId || next.videoMediaId || next.documentMediaId || next.voiceMediaId);
    if (!nextHas) {
      onChange({ ...patch, headerNonePending: false, isMediaTrackable: false });
      return;
    }
    if (trackLocked) {
      onChange({ ...patch, headerNonePending: false });
      return;
    }
    const had = !!(day.imageUrl || day.videoUrl || day.documentUrl || day.voiceUrl
      || day.imageMediaId || day.videoMediaId || day.documentMediaId || day.voiceMediaId);
    if (!had && nextHas && patch.isMediaTrackable === undefined) {
      onChange({ ...patch, headerNonePending: false, isMediaTrackable: true });
      return;
    }
    onChange({ ...patch, headerNonePending: false });
  };

  const applyHeaderMode = (mode: HeaderMode) => {
    setHeaderMode(mode);
    // Never wipe media on toggle. None marks a pending clear (committed on save);
    // Image/Video/PDF just show that slot — old file stays until a new one is picked.
    onChange({ headerNonePending: mode === 'none' });
  };

  const exclusiveFor = (keep: MediaSlotKind): Partial<CustomDayState> => ({
    ...(keep !== 'image' ? { imageUrl: null, imageMediaId: null, imageSourceUnavailable: false } : {}),
    ...(keep !== 'video' ? { videoUrl: null, videoMediaId: null, videoSourceUnavailable: false } : {}),
    ...(keep !== 'document' ? { documentUrl: null, documentMediaId: null, documentSourceUnavailable: false } : {}),
    voiceUrl: null,
    voiceMediaId: null,
    voiceSourceUnavailable: false,
  });

  const attachedHeaderMode = deriveHeaderMode(day);
  const pendingHeaderReplace = headerMode !== 'none'
    && attachedHeaderMode !== 'none'
    && headerMode !== attachedHeaderMode;
  const attachedHeaderLabel = attachedHeaderMode === 'document' ? 'PDF' : attachedHeaderMode;

  const hasPeUnavailable = day.imageSourceUnavailable
    || day.videoSourceUnavailable
    || day.documentSourceUnavailable
    || day.voiceSourceUnavailable;

  return (
    <Box sx={{ pt: 1.25, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {isOneTime && (
        <CustomDayTemplateSection
          label="Schedule"
          hint="This day sends only on the date you pick."
        >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Run date"
                  value={day.runDate ? dayjs(day.runDate, 'YYYY-MM-DD') : earliestRunDate()}
                  onChange={(v: Dayjs | null) => onChange({ runDate: v && v.isValid() ? v.format('YYYY-MM-DD') : '' })}
                  minDate={earliestRunDate()}
                  referenceDate={earliestRunDate()}
                  format="DD-MMM-YYYY"
                  slotProps={{
                    textField: {
                      size: 'small',
                      required: true,
                      error: missingRunDate,
                      helperText: missingRunDate ? 'Pick the date this day is sent' : undefined,
                  sx: { maxWidth: 240 },
                    },
                  }}
                />
              </LocalizationProvider>
        </CustomDayTemplateSection>
      )}

      <CustomDayTemplateSection
        label={usingCloud ? 'Header' : 'Media'}
        hint={usingCloud
          ? 'Optional. One of image, video, or PDF.'
          : 'Optional. You can add image, video, PDF, and voice.'}
      >
        {hasPeUnavailable && (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            A Project Essential item on this day is unavailable. Replace or remove it before sending — WhatsApp will not use a stale copy.
          </Alert>
        )}
        {usingCloud ? (
          <>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={headerMode}
              onChange={(_, v: HeaderMode | null) => {
                if (!v) return;
                applyHeaderMode(v);
              }}
              sx={{
                flexWrap: 'wrap',
                '& .MuiToggleButton-root': { textTransform: 'none', px: 1.25, py: 0.4 },
                '& .Mui-selected': { color: '#ed8936 !important', bgcolor: 'rgba(237,137,54,0.15) !important' },
              }}
            >
              <ToggleButton value="none">None</ToggleButton>
              <ToggleButton value="image">Image</ToggleButton>
              <ToggleButton value="video">Video</ToggleButton>
              <ToggleButton value="document">PDF</ToggleButton>
            </ToggleButtonGroup>
            {pendingHeaderReplace && (
              <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                Current {attachedHeaderLabel} stays until you add a {headerMode === 'document' ? 'PDF' : headerMode}. Tracking is unchanged.
              </Typography>
            )}
            {headerMode === 'none' && attachedHeaderMode !== 'none' && (
              <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                Current {attachedHeaderLabel} stays until you save. Switch back to {attachedHeaderLabel === 'PDF' ? 'PDF' : attachedHeaderLabel} to keep it without re-uploading.
              </Typography>
            )}
            {headerMode === 'image' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <ImageIcon sx={{ color: 'var(--color-text-subtle)', fontSize: 18 }} />
                <LibraryMediaSlot
                  kind="image"
                  url={day.imageUrl}
                  mediaId={day.imageMediaId}
                  sourceUnavailable={day.imageSourceUnavailable}
                  onChange={patchMedia}
                  exclusiveClear={exclusiveFor('image')}
                />
            </Box>
          )}
            {headerMode === 'video' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <VideocamIcon sx={{ color: 'var(--color-text-subtle)', fontSize: 18 }} />
                <LibraryMediaSlot
                  kind="video"
                  url={day.videoUrl}
                  mediaId={day.videoMediaId}
                  sourceUnavailable={day.videoSourceUnavailable}
                  onChange={patchMedia}
                  exclusiveClear={exclusiveFor('video')}
                />
          </Box>
            )}
            {headerMode === 'document' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <PictureAsPdfIcon sx={{ color: '#e53935', fontSize: 18 }} />
                <LibraryMediaSlot
                  kind="document"
                  url={day.documentUrl}
                  mediaId={day.documentMediaId}
                  sourceUnavailable={day.documentSourceUnavailable}
                  onChange={patchMedia}
                  exclusiveClear={exclusiveFor('document')}
                />
              </Box>
            )}
            {voiceBlocked && (
              <Alert
                severity="warning"
                sx={{ py: 0.5 }}
                action={
                  <Button color="inherit" size="small" onClick={() => patchMedia({ voiceUrl: null, voiceMediaId: null, voiceSourceUnavailable: false })}>
                    Remove voice
                  </Button>
                }
              >
                Voice notes aren&apos;t supported on the Official API — remove the voice note to save.
              </Alert>
            )}
            {multiHeader && (
              <Alert severity="error" sx={{ py: 0.5 }}>
                Keep only one header media type (image, video, or PDF).
              </Alert>
            )}
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <ImageIcon sx={{ color: 'var(--color-text-subtle)', fontSize: 18 }} />
              <Typography variant="body2" sx={{ minWidth: 56 }}>Image</Typography>
              <LibraryMediaSlot
                kind="image"
                url={day.imageUrl}
                mediaId={day.imageMediaId}
                sourceUnavailable={day.imageSourceUnavailable}
                onChange={patchMedia}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <VideocamIcon sx={{ color: 'var(--color-text-subtle)', fontSize: 18 }} />
              <Typography variant="body2" sx={{ minWidth: 56 }}>Video</Typography>
              <LibraryMediaSlot
                kind="video"
                url={day.videoUrl}
                mediaId={day.videoMediaId}
                sourceUnavailable={day.videoSourceUnavailable}
                onChange={patchMedia}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <PictureAsPdfIcon sx={{ color: '#e53935', fontSize: 18 }} />
              <Typography variant="body2" sx={{ minWidth: 56 }}>PDF</Typography>
              <LibraryMediaSlot
                kind="document"
                url={day.documentUrl}
                mediaId={day.documentMediaId}
                sourceUnavailable={day.documentSourceUnavailable}
                onChange={patchMedia}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <MicIcon sx={{ color: 'var(--color-text-subtle)', fontSize: 18 }} />
              <Typography variant="body2" sx={{ minWidth: 56 }}>Voice</Typography>
              <LibraryMediaSlot
                kind="voice"
                url={day.voiceUrl}
                mediaId={day.voiceMediaId}
                sourceUnavailable={day.voiceSourceUnavailable}
                onChange={patchMedia}
              />
            </Box>
          </>
        )}

        <Box sx={{ pt: 0.5 }}>
          <FormControlLabel
            control={
              <Switch
                checked={hasMedia && day.isMediaTrackable}
                disabled={!hasMedia || trackLocked}
                onChange={(e) => onChange({ isMediaTrackable: e.target.checked })}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#ed8936' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ed8936' },
                }}
              />
            }
            label={<Typography variant="body2" fontWeight={600}>Track media viewed</Typography>}
          />
          {trackLocked ? (
            <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', ml: 6 }}>
              Locked after this day&apos;s Meta template was created — turn it on or off only before the first Official API sync.
            </Typography>
          ) : !hasMedia ? (
            <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', ml: 6 }}>
              Add an image, video, PDF, or voice note first.
            </Typography>
          ) : day.isMediaTrackable ? (
            <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)', display: 'block', ml: 6 }}>
              Clients get a personal link instead of a WhatsApp file. When they open it, we record that the media was viewed.
              {usingCloud ? ' On the Official API, this choice is fixed once the Meta template exists.' : ''}
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', ml: 6 }}>
              Media is sent as a normal WhatsApp attachment — views are not tracked.
            </Typography>
          )}
        </Box>
      </CustomDayTemplateSection>

      <CustomDayTemplateSection
        label="Body"
        hint={`Message text. An optional link is appended at the end. Do not start or end with a placeholder (a value in {{}}), keep placeholders apart, and use at most ${TEMPLATE_BODY_MAX_EMOJIS} emojis.`}
      >
          <TemplateBodyField
          minRows={3}
            label="Message"
            value={day.message}
            onValueChange={(next) => onChange({ message: next })}
            issues={bodyIssues}
        />
        <Box>
          <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', mb: 0.5 }}>
            Insert variable
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {PLACEHOLDERS.map((ph) => (
              <Chip
                key={ph}
                label={ph.replace(/\{\{|\}\}/g, '#')}
                size="small"
                onClick={() => {
                  const next = `${day.message}${ph}`;
                  if (fitsBodyLimit(day.message, next)) onChange({ message: next });
                }}
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(237, 137, 54, 0.15)' } }}
              />
            ))}
          </Box>
          </Box>
            <TextField
              size="small"
              fullWidth
          label="Link (optional)"
              value={day.linkUrl}
              onChange={(e) => onChange({ linkUrl: e.target.value })}
              placeholder="https://..."
          helperText="Appended to the end of the message."
          InputProps={{
            startAdornment: <LinkIcon sx={{ mr: 1, color: 'var(--color-text-faint)', fontSize: 18 }} />,
          }}
            />
      </CustomDayTemplateSection>

      <CustomDayTemplateSection
        label="Buttons"
        hint="Optional. Add a Calendly booking action."
      >
          <FormControlLabel
          sx={{ m: 0, alignItems: 'flex-start', opacity: hasCalendly ? 1 : 0.75 }}
            control={
              <Switch
              checked={hasCalendly ? day.useCalendly : false}
              disabled={!hasCalendly}
                onChange={(e) => onChange({ useCalendly: e.target.checked })}
              sx={{
                mt: -0.25,
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#ed8936' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ed8936' },
              }}
              />
            }
            label={
            <Box sx={{ pl: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EventIcon sx={{ fontSize: 18, color: '#006bff' }} />
                <Typography variant="body2" fontWeight={600}>Calendly booking button</Typography>
              </Box>
              <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block' }}>
                {hasCalendly ? (
                  'Uses your connected Calendly event when this day sends.'
                ) : (
                  <>
                    Uses your connected Calendly event when this day sends.{' '}
                    <Typography
                      component="span"
                      variant="caption"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onGotoIntegrations();
                      }}
                      sx={{ color: '#ed8936', cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
                    >
                      Setup your Calendly
                    </Typography>
                  </>
                )}
              </Typography>
              </Box>
            }
          />
      </CustomDayTemplateSection>
    </Box>
  );
}

function BreakGapControl({
  value, onChange,
}: {
  dayNumber: number;
  value: number;
  onChange: (n: number) => void;
}) {
  return (
    <Box sx={{
      ml: 5.5, my: 0.75, p: 1.5, borderRadius: 1,
      bgcolor: 'var(--color-bg)',
      border: '1px solid var(--color-border)',
    }}>
      {/* Match SortableCustomDayRow: drag-handle column width + content column */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box
          sx={{
            width: 34, height: 34, flexShrink: 0, mt: 0.25,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--color-text-faint)',
          }}
        >
          <AccessTimeIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Delay
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.75 }}>
            <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)' }}>
              Wait
            </Typography>
            <TextField
              size="small"
              type="number"
              value={value}
              onChange={(e) => onChange(clampBreakDays(Number(e.target.value)))}
              inputProps={{ min: 1, max: 30, step: 1 }}
              sx={{ width: 72, '& input': { py: 0.5, textAlign: 'center' } }}
            />
            <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)' }}>
              day(s)
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function SortableCustomDayRow({
  day, isLast, expanded, onExpand, onChange, campaignName, hasCalendly, scheduleType, usingCloud, onGotoIntegrations,
  spendCost, onRemove, removeDisabled, metaStatusLoading, onTestSend, testSendBlockedReason,
}: {
  day: CustomDayState;
  isLast: boolean;
  expanded: boolean;
  onExpand: () => void;
  onChange: (patch: Partial<CustomDayState>) => void;
  campaignName: string;
  hasCalendly: boolean;
  scheduleType: CampaignScheduleType;
  usingCloud: boolean;
  onGotoIntegrations: () => void;
  spendCost?: number | null;
  onRemove?: () => void;
  removeDisabled?: boolean;
  /** Meta approval statuses are being refreshed from Meta. */
  metaStatusLoading?: boolean;
  onTestSend: () => void;
  /** Why this day can't be test-sent yet; null when it can. */
  testSendBlockedReason: string | null;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(day.dayNumber),
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const isOneTime = scheduleType === 'one_time';
  const summaryBits: string[] = [];
  if (usingCloud) {
    if (day.imageUrl) summaryBits.push('Header · Image');
    else if (day.videoUrl) summaryBits.push('Header · Video');
    else if (day.documentUrl) summaryBits.push('Header · PDF');
    if (day.message.trim() || day.linkUrl.trim()) summaryBits.push('Body');
    if (day.useCalendly) summaryBits.push('Button · Calendly');
  } else {
    if (day.message.trim()) summaryBits.push('Message');
    if (day.imageUrl) summaryBits.push('Image');
    if (day.videoUrl) summaryBits.push('Video');
    if (day.documentUrl) summaryBits.push('PDF');
    if (day.voiceUrl) summaryBits.push('Voice');
    if (day.linkUrl.trim()) summaryBits.push('Link');
    if (day.useCalendly) summaryBits.push('Calendly');
  }

  const templateState: EngagementTemplateState | null = usingCloud
    ? {
        dayNumber: day.dayNumber,
        status: day.status,
        templateName: day.templateName,
        syncedAt: day.templateSyncedAt,
        nextEditableAt: null,
        editLocked: false,
      }
    : null;
  const metaIssue = metaTemplateIssue(usingCloud, templateState);
  const metaLabel = usingCloud
    ? (metaIssue?.label ?? (day.status ? templateStatusStyle(day.status).label : 'Not created'))
    : '—';
  const metaSeverity: 'ok' | 'warning' | 'error' | 'muted' = !usingCloud
    ? 'muted'
    : metaIssue?.severity === 'error'
      ? 'error'
      : metaIssue?.severity === 'warning'
        ? 'warning'
        : 'ok';
  const metaHint = usingCloud && day.status
    ? templateStatusStyle(day.status).hint
    : usingCloud
      ? 'No template yet — Save & Sync to submit this day.'
      : undefined;

  return (
    <Box ref={setNodeRef} style={style} sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch', opacity: isDragging ? 0.85 : 1 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 32, flexShrink: 0 }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontSize: '0.78rem',
          bgcolor: dayHasContent(day) ? 'rgba(237, 137, 54, 0.12)' : 'var(--color-bg)',
          color: dayHasContent(day) ? '#ed8936' : 'var(--color-text-faint)',
          border: '2px solid', borderColor: dayHasContent(day) ? '#ed8936' : 'var(--color-border)',
        }}>
          {day.dayNumber}
        </Box>
        {!isLast && <Box sx={{ flex: 1, width: 2, bgcolor: 'var(--color-border)', my: 0.5, borderRadius: 1, minHeight: 12 }} />}
      </Box>

      <Box sx={{
        flex: 1, minWidth: 0, mb: 1.5, p: { xs: 1.25, sm: 1.5 }, borderRadius: 1,
        bgcolor: 'var(--color-bg)',
        border: '1px solid var(--color-border)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          gap: 1.5,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minWidth: 0 }}>
            <IconButton size="small" sx={{ cursor: 'grab', color: 'var(--color-text-faint)', mt: 0.25 }} {...attributes} {...listeners}>
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
            <Box onClick={onExpand} sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Day {day.dayNumber}
                </Typography>
                {onRemove && (
                  <Tooltip title={removeDisabled ? 'A campaign needs at least one day' : 'Remove this day'}>
                    <span>
                      <IconButton
                        size="small"
                        aria-label={`Remove day ${day.dayNumber}`}
                        disabled={removeDisabled}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemove();
                        }}
                        sx={{ color: 'var(--color-text-faint)' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {isOneTime && dayHasContent(day) && (
                  <Chip
                    size="small"
                    icon={<EventIcon sx={{ fontSize: 14 }} />}
                    label={day.runDate ? formatRunDate(day.runDate) : 'Run date required'}
                    color={day.runDate ? 'default' : 'error'}
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                )}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  overflow: 'hidden', mt: 0.75,
                  color: summaryBits.length ? 'var(--color-text-subtle)' : 'var(--color-text-faint)',
                  fontStyle: summaryBits.length ? 'normal' : 'italic',
                }}
              >
                {summaryBits.length ? summaryBits.join(' · ') : 'Nothing configured yet — expand to add content'}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={onExpand}
              aria-label={expanded ? 'Collapse day' : 'Edit day'}
              sx={{
                display: { xs: 'inline-flex', sm: 'none' },
                color: '#ed8936',
                mt: 0.1,
                flexShrink: 0,
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            gap: 1.25,
            flexShrink: 0,
            pl: { xs: '42px', sm: 0 },
            pt: { xs: 0.5, sm: 0 },
            borderTop: { xs: '1px solid var(--color-border)', sm: 'none' },
            alignSelf: { sm: 'center' },
          }}>
            {usingCloud && (
              <StatusColumn
                title="Meta"
                label={metaLabel}
                severity={metaSeverity}
                hint={metaHint}
                loading={metaStatusLoading}
              />
            )}
            {usingCloud && spendCost != null && (
              <StatusColumn
                title="Cost"
                label={formatSgd(spendCost)}
                severity="muted"
                hint={SPEND_ESTIMATE_HINT}
              />
            )}
            {/* Tooltips don't fire on disabled buttons, so wrap in a plain span. */}
            <Tooltip title={testSendBlockedReason ?? 'Send this day to a number you choose'}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<SendIcon sx={{ fontSize: 16 }} />}
                  disabled={!!testSendBlockedReason}
                  onClick={onTestSend}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    py: 0.35,
                    minHeight: 32,
                    whiteSpace: 'nowrap',
                    color: '#ed8936',
                    borderColor: '#ed8936',
                    '&:hover': { borderColor: '#dd7826', bgcolor: 'rgba(237, 137, 54, 0.08)' },
                  }}
                >
                  Test send
                </Button>
              </span>
            </Tooltip>
            <Button
              size="small"
              variant="contained"
              onClick={onExpand}
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                bgcolor: '#ed8936',
                '&:hover': { bgcolor: '#dd7826' },
                textTransform: 'none',
                fontWeight: 800,
                py: 0.35,
                px: 1.75,
                boxShadow: '0 2px 8px rgba(237, 137, 54, 0.45)',
                flexShrink: 0,
                minHeight: 32,
              }}
            >
              {expanded ? 'Collapse' : 'Edit'}
            </Button>
          </Box>
        </Box>
        {expanded && (
          <CampaignDayEditorBody
            day={day}
            onChange={onChange}
            campaignName={campaignName}
            hasCalendly={hasCalendly}
            scheduleType={scheduleType}
            usingCloud={usingCloud}
            onGotoIntegrations={onGotoIntegrations}
          />
        )}
      </Box>
    </Box>
  );
}

const TAG_CHIP_GAP = 4;

function tagChipSx(color?: string | null) {
  const c = color || '#ed8936';
  return {
    height: 18,
    maxWidth: 120,
    fontSize: '0.65rem',
    fontWeight: 700,
    bgcolor: `${c}22`,
    color: c,
    '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
  } as const;
}

/** Tags on a recipient row. Chips that do not fit collapse into +N. */
function RecipientRowTags({ tags }: { tags: Array<{ Id: string; Name: string; Color?: string | null }> }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(tags.length);

  useLayoutEffect(() => {
    const host = hostRef.current;
    const measure = measureRef.current;
    if (!host || !measure) return;

    const fit = () => {
      const available = host.clientWidth;
      if (available <= 0) return;
      const widths = Array.from(measure.querySelectorAll<HTMLElement>('[data-tag-chip]'), (el) => el.offsetWidth);
      const overflowEl = measure.querySelector<HTMLElement>('[data-overflow-chip]');
      const overflowWidth = (hidden: number) => {
        const label = overflowEl?.querySelector('.MuiChip-label');
        if (!overflowEl || !label) return 28;
        label.textContent = `+${hidden}`;
        return overflowEl.offsetWidth;
      };
      const total = widths.reduce((sum, w, i) => sum + w + (i > 0 ? TAG_CHIP_GAP : 0), 0);
      let count = widths.length;
      if (total > available) {
        let used = 0;
        count = 0;
        for (let i = 0; i < widths.length; i++) {
          const hidden = widths.length - (i + 1);
          const chip = widths[i] + (count > 0 ? TAG_CHIP_GAP : 0);
          const extra = hidden > 0 ? TAG_CHIP_GAP + overflowWidth(hidden) : 0;
          if (used + chip + extra > available) break;
          used += chip;
          count += 1;
        }
      }
      setVisibleCount((prev) => (prev === count ? prev : count));
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(host);
    return () => ro.disconnect();
  }, [tags]);

  if (tags.length === 0) return null;
  const hidden = tags.slice(visibleCount);

  return (
    <Box ref={hostRef} sx={{ position: 'relative', flex: '1 1 0', minWidth: 72 }}>
      <Box
        ref={measureRef}
        aria-hidden
        sx={{ position: 'fixed', left: 0, top: 0, visibility: 'hidden', pointerEvents: 'none', display: 'flex', width: 'max-content' }}
      >
        {tags.map((t) => (
          <Box key={t.Id} data-tag-chip sx={{ display: 'inline-flex' }}>
            <Chip label={t.Name} size="small" sx={tagChipSx(t.Color)} />
          </Box>
        ))}
        <Box data-overflow-chip sx={{ display: 'inline-flex' }}>
          <Chip label={`+${tags.length}`} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: `${TAG_CHIP_GAP}px`, overflow: 'hidden', flexWrap: 'nowrap' }}>
        {tags.slice(0, visibleCount).map((t) => (
          <Chip key={t.Id} label={t.Name} size="small" sx={{ ...tagChipSx(t.Color), flexShrink: 0 }} />
        ))}
        {hidden.length > 0 && (
          <Tooltip title={hidden.map((t) => t.Name).join(', ')}>
            <Chip label={`+${hidden.length}`} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, flexShrink: 0 }} />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

// Dialog to pick which clients receive a campaign (per-campaign enrollment).
const RECIPIENTS_PAGE_SIZE = 50;

function RecipientsDialog({
  open, campaignName, clients, onClose, onSaved,
}: {
  open: boolean;
  campaignName: string;
  clients: Client[];
  onClose: () => void;
  onSaved: (count: number) => void;
}) {
  const { formatMobile } = useMobileMask();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [tags, setTags] = useState<ClientTag[]>([]);
  const [filterTagIds, setFilterTagIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setSearch('');
    setFilterTagIds([]);
    setPage(1);
    Promise.all([
      fetchCampaignEnrollment(campaignName),
      fetchTags().catch(() => [] as ClientTag[]),
    ])
      .then(([enrollment, tagList]) => {
        if (cancelled) return;
        setSelected(new Set(enrollment.clientIds));
        setTags(tagList);
      })
      .catch(() => {
        if (cancelled) return;
        setSelected(new Set());
        setTags([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, campaignName]);

  const toggle = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const selectedFilterTags = useMemo(
    () => tags.filter((t) => filterTagIds.includes(t.Id)),
    [tags, filterTagIds],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter((c) => {
      if (filterTagIds.length > 0) {
        const clientTagIds = new Set((c.Tags || []).map((t) => t.Id));
        if (!filterTagIds.some((id) => clientTagIds.has(id))) return false;
      }
      if (!q) return true;
      return c.Name?.toLowerCase().includes(q) || (c.Mobile || '').toLowerCase().includes(q);
    });
  }, [clients, search, filterTagIds]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / RECIPIENTS_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * RECIPIENTS_PAGE_SIZE;
  const pageClients = filtered.slice(pageStart, pageStart + RECIPIENTS_PAGE_SIZE);

  useEffect(() => { setPage(1); }, [search, filterTagIds]);
  useEffect(() => { listRef.current?.scrollTo({ top: 0 }); }, [currentPage]);

  // The checkbox only adds or removes clients matching the current search and
  // tags (across every page). Filter changes never touch the selection, so
  // hidden recipients stay selected.
  const listNarrowed = search.trim() !== '' || filterTagIds.length > 0;
  const filteredIds = useMemo(() => new Set(filtered.map((c) => c.Id)), [filtered]);
  const shownSelectedCount = filtered.filter((c) => selected.has(c.Id)).length;
  const allShownSelected = filtered.length > 0 && shownSelectedCount === filtered.length;
  const someShownSelected = shownSelectedCount > 0 && !allShownSelected;
  const hiddenSelectedCount = Array.from(selected).filter((id) => !filteredIds.has(id)).length;
  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      filtered.forEach((c) => { if (allShownSelected) next.delete(c.Id); else next.add(c.Id); });
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await setCampaignEnrollment(campaignName, Array.from(selected));
      onSaved(selected.size);
      onClose();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
    >
      <DialogTitle fontWeight={700}>Recipients · {campaignName}</DialogTitle>
      <DialogContent>
        <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)', display: 'block', mb: 1 }}>
          Pick the clients who should receive this campaign. This is independent of the global engagement toggle.
        </Typography>
        <TextField
          fullWidth size="small" placeholder="Search clients..."
          value={search} onChange={(e) => setSearch(e.target.value)} sx={{ mb: 1 }}
        />
        <Autocomplete
          multiple
          disableCloseOnSelect
          size="small"
          limitTags={3}
          options={tags}
          value={selectedFilterTags}
          onChange={(_, val) => setFilterTagIds(val.map((t) => t.Id))}
          getOptionLabel={(opt) => opt.Name || ''}
          isOptionEqualToValue={(opt, val) => opt.Id === val.Id}
          noOptionsText={tags.length === 0 ? 'No tags yet' : 'No tags match'}
          clearText="Clear all tags"
          ListboxProps={{ style: { maxHeight: 280 } }}
          renderOption={(props, option, { selected: optionSelected }) => {
            const { key: _key, ...liProps } = props as React.HTMLAttributes<HTMLLIElement> & { key?: string };
            return (
              <li {...liProps} key={option.Id}>
                <Checkbox
                  size="small"
                  checked={optionSelected}
                  sx={{ p: 0.5, mr: 1, '&.Mui-checked': { color: option.Color || '#ed8936' } }}
                />
                <Chip
                  label={option.Name}
                  size="small"
                  sx={{ bgcolor: `${option.Color || '#ed8936'}22`, color: option.Color || '#ed8936', fontWeight: 700 }}
                />
              </li>
            );
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.Id}
                label={option.Name}
                size="small"
                sx={{ bgcolor: `${option.Color || '#ed8936'}22`, color: option.Color || '#ed8936', fontWeight: 700 }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Tags"
              placeholder={filterTagIds.length === 0 ? 'All tags — type to search' : 'Search tags'}
              InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
            />
          )}
          sx={{ mb: filterTagIds.length > 0 ? 0.5 : 1 }}
        />
        {filterTagIds.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, px: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)' }}>
              {filterTagIds.length === 1 ? '1 tag selected' : `${filterTagIds.length} tags selected`}
            </Typography>
            <Button
              size="small"
              onClick={() => setFilterTagIds([])}
              sx={{ textTransform: 'none', minWidth: 0, py: 0, color: '#ed8936' }}
            >
              Clear all
            </Button>
          </Box>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
        ) : clients.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'var(--color-text-faint)', py: 2, textAlign: 'center' }}>
            No clients yet. Add clients first.
          </Typography>
        ) : (
          <>
          <Box
            onClick={filtered.length > 0 ? toggleSelectAll : undefined}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1, mb: 0.5, borderRadius: 1,
              cursor: filtered.length > 0 ? 'pointer' : 'default',
              '&:hover': filtered.length > 0 ? { bgcolor: 'rgba(237,137,54,0.08)' } : undefined,
            }}
          >
            <Checkbox
              checked={allShownSelected}
              indeterminate={someShownSelected}
              disabled={filtered.length === 0}
              size="small"
              sx={{ p: 0.5, '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#ed8936' } }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600}>
                {listNarrowed ? 'Select all matching' : 'Select all'}{filtered.length > 0 ? ` (${filtered.length})` : ''}
              </Typography>
              <Typography variant="caption" component="div" sx={{ color: 'var(--color-text-subtle)', lineHeight: 1.3 }}>
                {selected.size} selected
              </Typography>
            </Box>
          </Box>
          {hiddenSelectedCount > 0 && (
            <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', px: 1, mb: 0.5 }}>
              {hiddenSelectedCount === 1
                ? '1 selected client is hidden by this filter and stays selected.'
                : `${hiddenSelectedCount} selected clients are hidden by this filter and stay selected.`}
            </Typography>
          )}
          <Box ref={listRef} sx={{ maxHeight: 360, overflowY: 'auto' }}>
            {filtered.length === 0 && (
              <Typography variant="body2" sx={{ color: 'var(--color-text-faint)', py: 2, textAlign: 'center' }}>
                No clients match.
              </Typography>
            )}
            {pageClients.map((c) => (
              <Box key={c.Id}
                onClick={() => toggle(c.Id)}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1, borderRadius: 1, cursor: 'pointer',
                  '&:hover': { bgcolor: 'rgba(237,137,54,0.08)' } }}>
                <Checkbox checked={selected.has(c.Id)} size="small" sx={{ p: 0.5,
                  '&.Mui-checked': { color: '#ed8936' } }} />
                <Box sx={{ minWidth: 0, flex: '1.2 1 0' }}>
                  <Typography variant="body2" fontWeight={600} noWrap>{c.Name}</Typography>
                  <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }} noWrap>{formatMobile(c.Mobile) || 'no mobile'}</Typography>
                </Box>
                {c.Tags && c.Tags.length > 0 && <RecipientRowTags tags={c.Tags} />}
              </Box>
            ))}
          </Box>
          {filtered.length > RECIPIENTS_PAGE_SIZE && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)' }}>
                {pageStart + 1}–{pageStart + pageClients.length} of {filtered.length}
              </Typography>
              <Pagination
                count={pageCount}
                page={currentPage}
                onChange={(_, p) => setPage(p)}
                size="small"
                shape="rounded"
                siblingCount={0}
              />
            </Box>
          )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)', mr: 'auto', ml: 1 }}>
          {selected.size} selected
        </Typography>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}
          sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}>
          {saving ? 'Saving...' : 'Save Recipients'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CampaignHeaderActions({
  campaign, active, scheduleType, recipientCount, spendTotal, spendEstimated, onToggleActive, onOpenRecipients,
  canActivateOn = true, activateBlockedReason,
}: {
  campaign: MyCampaign;
  active: boolean;
  scheduleType: CampaignScheduleType;
  recipientCount: number | null;
  spendTotal?: number | null;
  spendEstimated?: boolean;
  onToggleActive: (next: boolean) => void;
  onOpenRecipients: () => void;
  canActivateOn?: boolean;
  activateBlockedReason?: string;
}) {
  const blockOn = !active && !canActivateOn;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 1.25, sm: 1.5 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0, flex: { sm: '1 1 auto' } }}>
        <CampaignIcon sx={{ color: '#ed8936', mt: 0.35, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ lineHeight: 1.3, wordBreak: 'break-word' }}
          >
            {campaign.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
            <Chip
              label={`${campaign.numDays} day${campaign.numDays === 1 ? '' : 's'}`}
              size="small"
              sx={{ height: 20, fontSize: '0.65rem' }}
            />
            <Chip
              label={scheduleType === 'one_time' ? 'One-time' : 'Daily'}
              size="small"
              sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(237,137,54,0.15)', color: '#ed8936' }}
            />
            {spendTotal != null && (
              <Chip
                label={spendEstimated
                  ? `Est. ${formatSgd(spendTotal)} spent`
                  : `${formatSgd(spendTotal)} spent`}
                size="small"
                title={spendEstimated ? SPEND_ESTIMATE_HINT : undefined}
                sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(34,197,94,0.12)', color: '#16a34a', fontWeight: 700 }}
              />
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: { xs: 'stretch', sm: 'flex-end' },
          gap: 0.25,
          flexShrink: 0,
          width: { xs: '100%', sm: 'auto' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'space-between', sm: 'flex-end' },
            gap: 1,
          }}
        >
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => { e.stopPropagation(); onOpenRecipients(); }}
            sx={{ textTransform: 'none', borderColor: '#ed8936', color: '#ed8936', '&:hover': { borderColor: '#dd7826', bgcolor: '#ed893611' } }}
          >
            Recipients{recipientCount !== null ? ` (${recipientCount})` : ''}
          </Button>
          <FormControlLabel
            onClick={(e) => e.stopPropagation()}
            sx={{ mr: 0 }}
            control={
              <Switch
                checked={active}
                disabled={blockOn}
                onChange={(e) => {
                  const next = e.target.checked;
                  if (next && !canActivateOn) return;
                  onToggleActive(next);
                }}
                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#ed8936' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ed8936' } }}
              />
            }
            label={<Typography variant="body2" fontWeight={600}>{active ? 'Active' : 'Inactive'}</Typography>}
          />
        </Box>
        {blockOn && activateBlockedReason && (
          <Typography variant="caption" sx={{ color: ATTENTION_RED, textAlign: { xs: 'left', sm: 'right' } }}>
            {activateBlockedReason}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function formatCampaignTimestamp(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-SG', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function CampaignListCard({
  campaign, clients, spendTotal, spendEstimated, onOpen, usingCloud, hasCalendly,
}: {
  campaign: MyCampaign;
  clients: Client[];
  spendTotal?: number | null;
  spendEstimated?: boolean;
  onOpen: () => void;
  usingCloud: boolean;
  hasCalendly: boolean;
}) {
  const [active, setActive] = useState(campaign.active);
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const scheduleType = campaign.scheduleType || 'daily';
  const readiness = useMemo(
    () => evaluateCustomCampaignReadiness({
      usingCloud,
      hasCalendly,
      days: campaign.days.map((d) => ({
        dayNumber: d.DayNumber,
        status: d.Status,
        hasContent: campaignRowHasContent(d),
        useCalendly: !!d.UseCalendly,
      })),
      numDays: campaign.numDays,
    }),
    [usingCloud, hasCalendly, campaign.days, campaign.numDays],
  );

  useEffect(() => {
    setActive(campaign.active);
  }, [campaign.active]);

  useEffect(() => {
    if (readiness.canActivate || !active) return;
    setActive(false);
    void setMyCampaignActive(campaign.name, false).catch(() => {});
  }, [readiness.canActivate, active, campaign.name]);

  useEffect(() => {
    let cancelled = false;
    fetchCampaignEnrollment(campaign.name)
      .then((r) => { if (!cancelled) setRecipientCount(r.clientIds.length); })
      .catch(() => { if (!cancelled) setRecipientCount(null); });
    return () => { cancelled = true; };
  }, [campaign.name]);

  const toggleActive = async (next: boolean) => {
    if (next && !readiness.canActivate) return;
    setActive(next);
    try { await setMyCampaignActive(campaign.name, next); } catch { setActive(!next); }
  };

  return (
    <Card
      onClick={() => {
        if (recipientsOpen) return;
        onOpen();
      }}
      sx={{
        bgcolor: 'var(--color-surface)',
        border: '1px solid var(--color-border-strongest)',
        boxShadow: 'var(--color-shadow-card)',
        mb: 2,
        cursor: 'pointer',
        '&:hover': {
          borderColor: 'var(--color-accent)',
          bgcolor: 'var(--color-surface-elevated)',
        },
      }}
    >
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <CampaignHeaderActions
          campaign={campaign}
          active={active}
          scheduleType={scheduleType}
          recipientCount={recipientCount}
          spendTotal={spendTotal}
          spendEstimated={spendEstimated}
          onToggleActive={toggleActive}
          onOpenRecipients={() => setRecipientsOpen(true)}
          canActivateOn={readiness.canActivate}
          activateBlockedReason={readinessWarningText(readiness.reasons)}
        />
        <Box
          sx={{
            mt: 1,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            flexWrap: 'wrap',
            columnGap: 1.5,
            rowGap: 0.25,
          }}
        >
          <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
            Created On {formatCampaignTimestamp(campaign.createdAt)}
          </Typography>
          <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
            Last modified {formatCampaignTimestamp(campaign.updatedAt)}
          </Typography>
          {spendTotal != null && spendEstimated && (
            <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
              {SPEND_ESTIMATE_NOTICE}
            </Typography>
          )}
        </Box>
      </CardContent>
      <RecipientsDialog
        open={recipientsOpen}
        campaignName={campaign.name}
        clients={clients}
        onClose={() => setRecipientsOpen(false)}
        onSaved={(count) => setRecipientCount(count)}
      />
    </Card>
  );
}

function CampaignDetail({
  campaign, hasCalendly, clients, onBack, onSaved, usingCloud, cloudConnected, bridgeConnected, onGotoIntegrations,
  campaignSpend, onStatusesRefreshed,
}: {
  campaign: MyCampaign;
  hasCalendly: boolean;
  clients: Client[];
  onBack: () => void;
  onSaved: () => void | Promise<void>;
  onStatusesRefreshed: () => Promise<void>;
  usingCloud: boolean;
  cloudConnected: boolean;
  /** Agent WhatsApp (bridge) session is paired. */
  bridgeConnected: boolean;
  onGotoIntegrations: () => void;
  campaignSpend?: CloudSpendCampaign | null;
}) {
  const [days, setDays] = useState<CustomDayState[]>(() => buildCustomDays(campaign));
  const [active, setActive] = useState(campaign.active);
  const [scheduleType, setScheduleType] = useState<CampaignScheduleType>(campaign.scheduleType || 'daily');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [syncResults, setSyncResults] = useState<TemplateSyncDayResult[] | null>(null);
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const [refreshingTemplates, setRefreshingTemplates] = useState(false);
  const [structureBusy, setStructureBusy] = useState(false);
  const [testSendDay, setTestSendDay] = useState<CustomDayState | null>(null);

  /** Test send uses the saved day: bridge needs a paired session; Official API an approved template. */
  const testSendBlockedReason = (day: CustomDayState): string | null => {
    if (!dayHasContent(day)) return 'Add content to this day first';
    if (dirty) return 'Save your changes first — test send uses the saved version';
    if (usingCloud) {
      if (!cloudConnected) return 'Connect your WhatsApp Business account in Integrations first';
      if ((day.status || '').toUpperCase() !== 'APPROVED') {
        return 'Available once Meta approves this day\'s template';
      }
      return null;
    }
    if (!bridgeConnected) return 'Pair your WhatsApp in Integrations first';
    return null;
  };

  const customReadiness = useMemo(
    () => evaluateCustomCampaignReadiness({
      usingCloud,
      hasCalendly,
      days: days.map((d) => ({
        dayNumber: d.dayNumber,
        status: d.status,
        hasContent: dayHasContent(d),
        useCalendly: d.useCalendly,
      })),
      numDays: campaign.numDays,
    }),
    [usingCloud, hasCalendly, days, campaign.numDays],
  );

  const scheduleError = (() => {
    if (scheduleType !== 'one_time') return null;
    const configured = days.filter(dayHasContent);
    if (configured.length === 0) return null;
    if (configured.some((d) => !d.runDate)) return 'Every configured day needs a run date.';
    const dates = configured.map((d) => d.runDate);
    if (new Set(dates).size !== dates.length) return 'Two days share the same run date — give each day its own date.';
    return null;
  })();

  const cloudMediaError = (() => {
    if (!usingCloud) return null;
    const bad = days.find((raw) => {
      const d = dayForPersist(raw);
      // Trackable media goes in the body as links — header rules do not apply.
      if (d.isMediaTrackable) return false;
      if (d.voiceUrl || d.voiceMediaId) return true;
      const n = [
        d.imageUrl || d.imageMediaId,
        d.videoUrl || d.videoMediaId,
        d.documentUrl || d.documentMediaId,
      ].filter(Boolean).length;
      return n > 1;
    });
    return bad || null;
  })();

  /** Bridge and Official API: every template rule each configured day breaks. */
  const templateBodyErrors = days
    .filter(dayHasContent)
    .flatMap((raw) => customDayBodyIssues(raw).map((issue) => `Day ${raw.dayNumber}: ${issue}`));

  useEffect(() => {
    let cancelled = false;
    fetchCampaignEnrollment(campaign.name)
      .then((r) => { if (!cancelled) setRecipientCount(r.clientIds.length); })
      .catch(() => { if (!cancelled) setRecipientCount(null); });
    return () => { cancelled = true; };
  }, [campaign.name]);

  const statusOnlyReloadRef = useRef(false);

  useEffect(() => {
    if (statusOnlyReloadRef.current) {
      statusOnlyReloadRef.current = false;
      const fresh = new Map(buildCustomDays(campaign).map((d) => [d.dayNumber, d]));
      setDays((prev) => prev.map((d) => {
        const f = fresh.get(d.dayNumber);
        return f ? { ...d, ...pickCustomDayMetaFields(f) } : d;
      }));
      return;
    }
    setDays(buildCustomDays(campaign));
    setActive(campaign.active);
    setScheduleType(campaign.scheduleType || 'daily');
    setDirty(false);
    setSaveError(null);
    // Keep saveSuccess after Save/Refresh reload — cleared when switching campaigns.
  }, [campaign]);

  useEffect(() => {
    if (customReadiness.canActivate || !active) return;
    setActive(false);
    void setMyCampaignActive(campaign.name, false).catch(() => {});
  }, [customReadiness.canActivate, active, campaign.name]);

  useEffect(() => {
    setSyncResults(null);
    setSaveSuccess(null);
  }, [campaign.name]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const patchDay = (idx: number, patch: Partial<CustomDayState>) => {
    setDays((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
    setDirty(true);
    setSaveSuccess(null);
    setSyncResults(null);
  };

  const toggleActive = async (next: boolean) => {
    if (next && !customReadiness.canActivate) return;
    setActive(next);
    try { await setMyCampaignActive(campaign.name, next); } catch { setActive(!next); }
  };

  const handleBack = () => {
    if (dirty && !window.confirm('Discard unsaved changes to this campaign?')) return;
    onBack();
  };

  const changeStructure = async (body: { numDays?: number; removeDayNumber?: number }) => {
    if (dirty && !window.confirm('You have unsaved edits. Changing the days will reload this campaign and discard them. Continue?')) return;
    if (body.removeDayNumber != null && !window.confirm(
      `Remove day ${body.removeDayNumber}? Later days shift up by one. This cannot be undone.`,
    )) return;
    setStructureBusy(true);
    setSaveError(null);
    try {
      await updateMyCampaignStructure(campaign.name, body);
      await onSaved();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Could not update days');
    } finally {
      setStructureBusy(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active: a, over } = event;
    if (!over || a.id === over.id) return;
    const fromIndex = days.findIndex((d) => String(d.dayNumber) === String(a.id));
    const toIndex = days.findIndex((d) => String(d.dayNumber) === String(over.id));
    if (fromIndex < 0 || toIndex < 0) return;
    setDays((prev) => reorderCustomDayContent(prev, fromIndex, toIndex));
    setExpanded(null);
    setDirty(true);
    setSaveSuccess(null);
    setSyncResults(null);
  };

  const templateCounts = (() => {
    const counts = { approved: 0, pending: 0, problem: 0, missing: 0 };
    days.filter(dayHasContent).forEach((day) => {
      const status = (day.status || '').toUpperCase();
      if (!status) counts.missing += 1;
      else if (status === 'APPROVED') counts.approved += 1;
      else if (status === 'PENDING') counts.pending += 1;
      else counts.problem += 1;
    });
    return counts;
  })();

  const handleRefreshTemplateStatuses = async () => {
    if (!cloudConnected) return;
    setRefreshingTemplates(true);
    setSaveError(null);
    try {
      await refreshWhatsAppCloud();
      statusOnlyReloadRef.current = true;
      await onStatusesRefreshed();
      setSaveSuccess('Template statuses refreshed from Meta.');
    } catch (e) {
      statusOnlyReloadRef.current = false;
      console.error(e);
      setSaveError(e instanceof Error ? e.message : 'Failed to refresh template statuses');
    } finally {
      setRefreshingTemplates(false);
    }
  };

  const save = async () => {
    if (cloudMediaError) {
      setSaveError(
        cloudMediaError.voiceUrl || cloudMediaError.voiceMediaId
          ? `Day ${cloudMediaError.dayNumber}: voice notes aren't supported on the Official API — remove the voice note.`
          : `Day ${cloudMediaError.dayNumber}: keep only one header media type (image, video, or PDF).`,
      );
      return;
    }
    if (templateBodyErrors.length > 0) return;
    if (usingCloud && !cloudConnected) {
      setSaveError('Connect your WhatsApp Business account in Integrations before saving for the Official API.');
      return;
    }

    const missingLibrary = days.filter(dayHasContent).find((raw) => {
      const d = dayForPersist(raw);
      const pairs: Array<[string | null, string | null]> = [
        [d.imageUrl, d.imageMediaId],
        [d.videoUrl, d.videoMediaId],
        [d.documentUrl, d.documentMediaId],
        [d.voiceUrl, d.voiceMediaId],
      ];
      return pairs.some(([url, id]) => url && !id);
    });
    if (missingLibrary) {
      setSaveError(
        `Day ${missingLibrary.dayNumber}: re-attach media from the library or upload again so each file has a Media id.`,
      );
      return;
    }

    const payload: CampaignDayInput[] = days.filter(dayHasContent).map((raw) => {
      const d = dayForPersist(raw);
      const mediaIds = dayMediaIds(d);
      return {
      dayNumber: d.dayNumber,
      channel: 'whatsapp',
      messageTemplate: d.message,
        mediaIds,
      linkUrl: d.linkUrl.trim() || null,
        useCalendly: hasCalendly && d.useCalendly,
        isMediaTrackable: mediaIds.length > 0 && d.isMediaTrackable,
      runDate: scheduleType === 'one_time' ? (d.runDate || null) : null,
        breakDaysAfter: scheduleType === 'daily' ? clampBreakDays(d.breakDaysAfter) : 1,
        status: d.status,
        templateId: d.templateId,
        templateName: d.templateName,
        templateSyncedAt: d.templateSyncedAt,
        supersededTemplateId: d.supersededTemplateId,
        supersededTemplateName: d.supersededTemplateName,
        templateContentHash: d.templateContentHash,
      };
    });
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);
    setSyncResults(null);
    try {
      await createCampaign({
        Name: campaign.name,
        days: payload,
        scheduleType,
      });
      let syncFailed = false;
      if (usingCloud && cloudConnected) {
        const sync = await syncCustomCampaignTemplates({ campaignName: campaign.name });
        setSyncResults(sync.days);
        setDays((prev) => prev.map((d) => {
          const r = sync.days.find((x) => x.dayNumber === d.dayNumber);
          if (!r || r.action === 'failed') return d;
          return {
            ...d,
            status: r.status ?? d.status,
            templateName: r.templateName || d.templateName,
          };
        }));
        const failed = sync.days.filter((d) => d.action === 'failed');
        if (failed.length > 0) {
          syncFailed = true;
          setSaveError(failed.map((d) => `Day ${d.dayNumber}: ${d.error || 'Meta refused'}`).join(' · '));
        }
      }
      setDirty(false);
      setSaveSuccess(
        usingCloud && cloudConnected
          ? (syncFailed
            ? 'Campaign saved. Some days could not be synced with Meta — see details below.'
            : 'Campaign saved and synced with Meta.')
          : 'Campaign saved.',
      );
      await onSaved();
    } catch (e) {
      console.error(e);
      setSaveError(e instanceof Error ? e.message : 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ textTransform: 'none', color: 'var(--color-text-subtle)' }}
        >
          All campaigns
          </Button>
        {dirty && (
          <Chip label="Unsaved changes" size="small" sx={{ height: 20, fontSize: '0.65rem', color: '#ed8936', bgcolor: 'rgba(237,137,54,0.15)' }} />
        )}
        </Box>

      <Card sx={{ bgcolor: 'var(--color-surface)' }}>
        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
          <CampaignHeaderActions
            campaign={campaign}
            active={active}
            scheduleType={scheduleType}
            recipientCount={recipientCount}
            spendTotal={usingCloud ? (campaignSpend?.totalCost ?? 0) : null}
            spendEstimated={usingCloud}
            onToggleActive={toggleActive}
            onOpenRecipients={() => setRecipientsOpen(true)}
            canActivateOn={customReadiness.canActivate}
            activateBlockedReason={readinessWarningText(customReadiness.reasons)}
          />
          {usingCloud && (
            <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', mt: 1 }}>
              {SPEND_ESTIMATE_NOTICE}
            </Typography>
          )}
        </CardContent>
      </Card>

      <EngagementSection
        step={1}
        title="Schedule"
        subtitle="How this campaign advances for each recipient."
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={scheduleType}
            onChange={(_, v: CampaignScheduleType | null) => {
              if (!v || v === scheduleType) return;
              setScheduleType(v);
              setDirty(true);
              setSaveSuccess(null);
            }}
            sx={{
              '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, py: 0.5 },
              '& .Mui-selected': { color: '#ed8936 !important', bgcolor: 'rgba(237,137,54,0.15) !important' },
            }}
          >
            <ToggleButton value="daily">Daily sequence</ToggleButton>
            <ToggleButton value="one_time">One-time (fixed dates)</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
            {scheduleType === 'daily'
              ? 'One day at a time in order. Set optional waits between days below.'
              : 'Set a run date inside each day — a day sends only on that exact date.'}
          </Typography>
        </Box>
      </EngagementSection>

      {usingCloud && (
        <EngagementSection
          step={2}
          title="Meta approval"
          subtitle="Every configured day must be approved by Meta before it can be sent."
          action={
            <Button
              size="small"
              variant="outlined"
              onClick={() => { void handleRefreshTemplateStatuses(); }}
              disabled={refreshingTemplates || !cloudConnected}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              {refreshingTemplates ? 'Checking…' : 'Refresh status'}
            </Button>
          }
        >
          {!cloudConnected ? (
            <Alert severity="warning" icon={<CloudIcon />}>
              <Typography variant="caption">
                Your WhatsApp Business account isn&apos;t connected, so templates can&apos;t be created.
                {' '}
                <Button
                  size="small"
                  onClick={onGotoIntegrations}
                  sx={{ textTransform: 'none', p: 0, minWidth: 0, verticalAlign: 'baseline' }}
                >
                  Open Integrations
                </Button>
              </Typography>
            </Alert>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                <TemplateCountChip label="Approved" count={templateCounts.approved} color="#22c55e" />
                <TemplateCountChip label="In review" count={templateCounts.pending} color="#ed8936" />
                <TemplateCountChip label="Needs attention" count={templateCounts.problem} color={ATTENTION_RED} />
                <TemplateCountChip label="Not created yet" count={templateCounts.missing} color="#94a3b8" />
              </Box>
              <Typography variant="caption" component="div" sx={{ color: 'var(--color-text-subtle)' }}>
                Use <b>Save &amp; Sync</b> to submit changed days to Meta. Use <b>Refresh status</b> if a webhook was missed and a day still shows In review after Meta approved it.
              </Typography>
            </>
          )}
        </EngagementSection>
      )}

      <EngagementSection
        step={usingCloud ? 3 : 2}
        title="Messages"
        subtitle="Drag the handle to reorder message content. Day numbers and wait times stay on their slots."
      >
        {usingCloud && (
          <Alert severity="warning" sx={{ mb: 1.5, py: 0.5 }}>
            Removing a day shifts later days. Official API templates on those days stay out of date until you Save &amp; Sync again.
          </Alert>
        )}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={days.map((d) => String(d.dayNumber))} strategy={verticalListSortingStrategy}>
        {days.map((d, idx) => (
              <React.Fragment key={d.dayNumber}>
                <SortableCustomDayRow
            day={d}
                  isLast={idx === days.length - 1}
            expanded={expanded === idx}
            onExpand={() => setExpanded(expanded === idx ? null : idx)}
            onChange={(patch) => patchDay(idx, patch)}
            campaignName={campaign.name}
            hasCalendly={hasCalendly}
            scheduleType={scheduleType}
                  usingCloud={usingCloud}
                  onGotoIntegrations={onGotoIntegrations}
                  spendCost={usingCloud
                    ? (campaignSpend?.days.find((x) => x.dayNumber === d.dayNumber)?.cost ?? 0)
                    : null}
                  onRemove={() => { void changeStructure({ removeDayNumber: d.dayNumber }); }}
                  removeDisabled={structureBusy || days.length <= 1}
                  metaStatusLoading={refreshingTemplates}
                  onTestSend={() => setTestSendDay(d)}
                  testSendBlockedReason={testSendBlockedReason(d)}
                />
                {scheduleType === 'daily' && idx < days.length - 1 && (
                  <BreakGapControl
                    dayNumber={d.dayNumber}
                    value={d.breakDaysAfter}
                    onChange={(n) => patchDay(idx, { breakDaysAfter: n })}
                  />
                )}
              </React.Fragment>
            ))}
          </SortableContext>
        </DndContext>
        <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
            <Box sx={{ width: 32, flexShrink: 0 }} />
            <Button
              fullWidth
              variant="outlined"
              startIcon={structureBusy ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
              disabled={structureBusy || campaign.numDays >= 60}
              onClick={() => { void changeStructure({ numDays: campaign.numDays + 1 }); }}
              sx={{ textTransform: 'none', borderStyle: 'dashed' }}
            >
            Add day
          </Button>
        </Box>
      </EngagementSection>

      {usingCloud && (
        <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
          Save writes your days, then syncs only days whose content changed.
        </Typography>
      )}
        {dirty && scheduleError && (
        <Alert severity="warning" sx={{ py: 0.5 }}>{scheduleError}</Alert>
      )}
      {cloudMediaError && (
        <Alert severity="error" sx={{ py: 0.5 }}>
          {cloudMediaError.voiceUrl || cloudMediaError.voiceMediaId
            ? `Day ${cloudMediaError.dayNumber}: voice notes aren't supported on the Official API — remove the voice note.`
            : `Day ${cloudMediaError.dayNumber}: keep only one header media type (image, video, or PDF).`}
        </Alert>
      )}
      {templateBodyErrors.length > 0 && (
        <Alert severity="error" sx={{ py: 0.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {templateBodyErrors.map((msg) => (
              <Typography key={msg} variant="body2" component="div">{msg}</Typography>
            ))}
          </Box>
        </Alert>
      )}
      {saveSuccess && (
        <Alert severity="success" sx={{ py: 0.5 }} onClose={() => setSaveSuccess(null)}>{saveSuccess}</Alert>
        )}
        {saveError && (
        <Alert severity="error" sx={{ py: 0.5 }} onClose={() => setSaveError(null)}>{saveError}</Alert>
      )}
      {syncResults && syncResults.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {syncResults.map((r) => (
            <Box key={r.dayNumber}>
              <Typography variant="caption" fontWeight={700} sx={{ color: 'var(--color-text-subtle)' }}>
                Day {r.dayNumber}
              </Typography>
              <TemplateSyncResultAlert result={r} />
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={save}
          disabled={!dirty || saving || !!scheduleError || !!cloudMediaError || templateBodyErrors.length > 0}
            sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none', minWidth: 140 }}
          >
          {saving
            ? (usingCloud ? 'Saving & syncing...' : 'Saving...')
            : (usingCloud ? 'Save & Sync' : 'Save Campaign')}
          </Button>
        </Box>

      <RecipientsDialog
        open={recipientsOpen}
        campaignName={campaign.name}
        clients={clients}
        onClose={() => setRecipientsOpen(false)}
        onSaved={(count) => setRecipientCount(count)}
      />

      <TestSendDayDialog
        campaignName={campaign.name}
        day={testSendDay}
        usingCloud={usingCloud}
        onClose={() => setTestSendDay(null)}
      />
    </Box>
  );
}

const TEST_SEND_MOBILE_KEY = 'superConnector.testSendMobile';

function TestSendDayDialog({
  campaignName, day, usingCloud, onClose,
}: {
  campaignName: string;
  /** Day being tested; null closes the dialog. */
  day: CustomDayState | null;
  usingCloud: boolean;
  onClose: () => void;
}) {
  const [mobile, setMobile] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<TestSendResult | null>(null);

  useEffect(() => {
    if (!day) return;
    setResult(null);
    try { setMobile(localStorage.getItem(TEST_SEND_MOBILE_KEY) || ''); } catch { /* storage unavailable */ }
  }, [day]);

  const hasTrackableMedia = !!day?.isMediaTrackable
    && !!(day.imageUrl || day.videoUrl || day.documentUrl || day.voiceUrl);

  const send = async () => {
    if (!day) return;
    const target = mobile.trim();
    setBusy(true);
    setResult(null);
    try {
      try { localStorage.setItem(TEST_SEND_MOBILE_KEY, target); } catch { /* storage unavailable */ }
      setResult(await testSendCustomCampaignDay(campaignName, day.dayNumber, target));
    } catch (e) {
      setResult({ ok: false, error: e instanceof Error ? e.message : 'Send failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!day} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WhatsAppIcon sx={{ color: '#25D366' }} />
        Test send · Day {day?.dayNumber}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)', mb: 2 }}>
          Sends the saved Day {day?.dayNumber} message
          {usingCloud ? ' (its approved Meta template) from your WhatsApp Business number' : ' from your paired WhatsApp'}.
          {' '}{'{{client_name}}'} is filled as &ldquo;Test Client&rdquo;. Nothing is recorded against your clients.
        </Typography>
        {usingCloud && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Meta bills test sends like live marketing messages (est. S$0.10 each).
          </Alert>
        )}
        {(hasTrackableMedia || day?.useCalendly) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {hasTrackableMedia && 'Attachment links in a test open the file directly and aren\'t tracked. '}
            {day?.useCalendly && (usingCloud
              ? 'The booking button in a test is a placeholder and won\'t open your calendar.'
              : 'The booking link in a test goes straight to Calendly and isn\'t tracked.')}
          </Alert>
        )}
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Send to"
          placeholder="+6591234567"
          helperText="8-digit numbers are treated as Singapore (+65)"
          value={mobile}
          onChange={(e) => setMobile(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && mobile.trim() && !busy) void send(); }}
          inputProps={{ inputMode: 'tel' }}
        />
        {result && (result.ok ? (
          <Alert severity="success" sx={{ mt: 2 }}>
            Sent to {mobile.trim()}.
            {result.rendered?.body && (
              <Typography variant="caption" component="pre" sx={{ m: 0, mt: 0.5, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                {result.rendered.body}
              </Typography>
            )}
          </Alert>
        ) : (
          <Alert severity="error" sx={{ mt: 2 }}>{result.error || 'Failed'}</Alert>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>Close</Button>
        <Button
          variant="contained"
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          disabled={busy || !mobile.trim()}
          onClick={() => { void send(); }}
          sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1ebe57' }, textTransform: 'none' }}
        >
          {busy ? 'Sending…' : 'Send test'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
// ─── Auto-reply triggers (phrase → tag + campaign enrolment) ───
// Matching runs on the WhatsApp bridge; message content never reaches the
// backend. This section only manages the trigger configuration.
const TRIGGERS_PAGE_SIZE = 10;
const CUSTOM_CAMPAIGNS_PAGE_SIZE = 10;

function TriggersSection({ campaigns }: { campaigns: MyCampaign[] }) {
  const [triggers, setTriggers] = useState<InboundTrigger[]>([]);
  const [tags, setTags] = useState<ClientTag[]>([]);
  const [loading, setLoading] = useState(true);

  // Dialog state (create or edit)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<InboundTrigger | null>(null);
  const [phrase, setPhrase] = useState('');
  const [tagId, setTagId] = useState('');
  const [campaignDefId, setCampaignDefId] = useState('');
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [trigs, tagList] = await Promise.all([
        fetchInboundTriggers(),
        fetchTags().catch(() => [] as ClientTag[]),
      ]);
      setTriggers(trigs);
      setTags(tagList);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pageCount = Math.max(1, Math.ceil(triggers.length / TRIGGERS_PAGE_SIZE));
  useEffect(() => {
    if (page > 0 && page >= pageCount) setPage(Math.max(0, pageCount - 1));
  }, [page, pageCount]);

  const pagedTriggers = useMemo(
    () => triggers.slice(page * TRIGGERS_PAGE_SIZE, (page + 1) * TRIGGERS_PAGE_SIZE),
    [triggers, page],
  );

  const tagById = new Map(tags.map((t) => [String(t.Id), t]));
  const campaignByDefId = new Map(campaigns.map((c) => [String(c.defId), c]));

  const openCreate = () => {
    if (isDemoMode()) {
      try {
        blockDemoMutation();
      } catch (e) {
        setDialogError(e instanceof Error ? e.message : 'This is a demo. Register your interest to unlock full access.');
      }
      return;
    }
    setEditing(null);
    setPhrase('');
    setTagId(tags.length > 0 ? String(tags[0].Id) : '');
    setCampaignDefId('');
    setDialogError(null);
    setDialogOpen(true);
  };

  const openEdit = (t: InboundTrigger) => {
    setEditing(t);
    setPhrase(t.Phrase);
    setTagId(String(t.TagId));
    setCampaignDefId(t.CampaignDefId ? String(t.CampaignDefId) : '');
    setDialogError(null);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!editing && isDemoMode()) {
      try {
        blockDemoMutation();
      } catch (e) {
        setDialogError(e instanceof Error ? e.message : 'This is a demo. Register your interest to unlock full access.');
      }
      return;
    }
    setSaving(true);
    setDialogError(null);
    try {
      const payload = {
        Phrase: phrase.trim(),
        TagId: tagId,
        CampaignDefId: campaignDefId || null,
      };
      if (editing) await updateInboundTrigger(editing.Id, payload);
      else await createInboundTrigger(payload);
      setDialogOpen(false);
      await load();
    } catch (e) {
      setDialogError(e instanceof Error ? e.message : 'Failed to save trigger');
    } finally { setSaving(false); }
  };

  const toggleActive = async (t: InboundTrigger, next: boolean) => {
    setTriggers((prev) => prev.map((row) => (row.Id === t.Id ? { ...row, IsActive: next } : row)));
    try { await updateInboundTrigger(t.Id, { IsActive: next }); }
    catch { setTriggers((prev) => prev.map((row) => (row.Id === t.Id ? { ...row, IsActive: !next } : row))); }
  };

  const remove = async (t: InboundTrigger) => {
    try {
      await deleteInboundTrigger(t.Id);
      setTriggers((prev) => prev.filter((row) => row.Id !== t.Id));
    } catch (e) { console.error(e); }
  };

  const canSave = phrase.trim().length >= 3 && !!tagId;

  return (
    <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
          <WhatsAppIcon sx={{ color: '#25D366' }} />
          <Typography variant="h6" fontWeight={700}>Auto-reply Triggers</Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ textTransform: 'none', borderColor: '#ed8936', color: '#ed8936', '&:hover': { borderColor: '#dd7826', bgcolor: '#ed893611' } }}
          >
            Add Trigger
          </Button>
        </Box>
        <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)', display: 'block', mb: 2 }}>
          When a client replies on WhatsApp with a phrase below, they are tagged automatically and
          (optionally) enrolled into a campaign. Matching is case-insensitive and happens on your
          paired WhatsApp — message content is never stored.
        </Typography>

        {dialogError && !dialogOpen && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setDialogError(null)}>
            {dialogError}
          </Alert>
        )}

      <Card sx={{
        bgcolor: 'var(--color-surface)',
        border: '1px solid var(--color-border-strongest)',
        boxShadow: 'var(--color-shadow-card)',
      }}>
        <CardContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>
        ) : triggers.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'var(--color-text-faint)', textAlign: 'center', py: 2 }}>
            No triggers yet. Add one — e.g. “I would like to join the July Tech showcase”.
          </Typography>
        ) : (
            <>
              {pagedTriggers.map((t) => {
            const tag = tagById.get(String(t.TagId));
            const camp = t.CampaignDefId ? campaignByDefId.get(String(t.CampaignDefId)) : undefined;
            return (
                  <Box
                    key={t.Id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      py: 1,
                      px: 1.5,
                      mb: 1,
                      borderRadius: 1,
                      bgcolor: 'var(--color-surface-elevated)',
                      border: '1px solid var(--color-border-strongest)',
                      '&:last-of-type': { mb: 0 },
                    }}
                  >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>“{t.Phrase}”</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      label={tag ? tag.Name : 'Tag missing'}
                      sx={{ height: 20, fontSize: '0.65rem', bgcolor: tag?.Color || undefined, color: tag?.Color ? '#fff' : undefined }}
                    />
                    <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                      {camp ? `+ enrol in “${camp.name}”` : t.CampaignDefId ? '+ enrol (campaign unavailable)' : 'tag only'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 0.25, mt: 0.25 }}>
                <Switch
                  size="small"
                  checked={t.IsActive}
                  onChange={(e) => toggleActive(t, e.target.checked)}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#ed8936' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ed8936' } }}
                />
                <IconButton size="small" onClick={() => openEdit(t)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => remove(t)}><DeleteIcon fontSize="small" /></IconButton>
                </Box>
              </Box>
            );
              })}
              {triggers.length > TRIGGERS_PAGE_SIZE && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                  <Pagination
                    count={pageCount}
                    page={page + 1}
                    onChange={(_, p) => setPage(p - 1)}
                    color="primary"
                    shape="rounded"
                    size="small"
                    siblingCount={1}
                    boundaryCount={1}
                  />
                  <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                    {page * TRIGGERS_PAGE_SIZE + 1}–{Math.min((page + 1) * TRIGGERS_PAGE_SIZE, triggers.length)} of {triggers.length}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm"
          PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}>
          <DialogTitle fontWeight={700}>{editing ? 'Edit Trigger' : 'New Trigger'}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Phrase to watch for"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              placeholder="I would like to join the July Tech showcase"
              helperText="Matched anywhere in the reply, ignoring case and extra spaces (min 3 characters)."
              sx={{ mt: 1, mb: 2 }}
            />
            {tags.length === 0 ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                You have no tags yet — create one under the Manage Tags tab first.
              </Alert>
            ) : (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Tag to assign</InputLabel>
                <Select value={tagId} label="Tag to assign" onChange={(e) => setTagId(String(e.target.value))}>
                  {tags.map((t) => (
                    <MenuItem key={t.Id} value={String(t.Id)}>{t.Name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth>
              <InputLabel>Enrol into campaign (optional)</InputLabel>
              <Select
                value={campaignDefId}
                label="Enrol into campaign (optional)"
                onChange={(e) => setCampaignDefId(String(e.target.value))}
              >
                <MenuItem value="">None — tag only</MenuItem>
                {campaigns.map((c) => (
                  <MenuItem key={c.defId} value={String(c.defId)}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {dialogError && <Alert severity="error" sx={{ mt: 2 }}>{dialogError}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button variant="contained" onClick={save} disabled={!canSave || saving}
              sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}>
              {saving ? 'Saving...' : 'Save Trigger'}
            </Button>
          </DialogActions>
        </Dialog>
    </Box>
  );
}

function CampaignsTab({
  onGotoIntegrations,
  cloudStatus,
  agentSettings,
}: {
  onGotoIntegrations: () => void;
  cloudStatus: WhatsAppCloudStatus | null;
  agentSettings: AgentSettings | null;
}) {
  const [campaigns, setCampaigns] = useState<MyCampaign[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [hasCalendly, setHasCalendly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'updatedAt' | 'createdAt'>('updatedAt');
  const [page, setPage] = useState(0);
  const [cloudSpend, setCloudSpend] = useState<CloudSpendResponse | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDays, setNewDays] = useState('7');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const openCreate = () => {
    if (isDemoMode()) {
      try {
        blockDemoMutation();
      } catch (e) {
        setCreateError(e instanceof Error ? e.message : 'This is a demo. Register your interest to unlock full access.');
      }
      return;
    }
    setNewName('');
    setNewDays('7');
    setCreateError(null);
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    if (isDemoMode()) {
      try {
        blockDemoMutation();
      } catch (e) {
        setCreateError(e instanceof Error ? e.message : 'This is a demo. Register your interest to unlock full access.');
      }
      return;
    }
    const name = newName.trim();
    const numDays = Number(newDays);
    if (!name) {
      setCreateError('Enter a campaign name.');
      return;
    }
    if (!Number.isInteger(numDays) || numDays < 1 || numDays > 60) {
      setCreateError('Number of days must be between 1 and 60.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const created = await createMyCampaign({ name, numDays });
      setCreateOpen(false);
      await load();
      setSelectedName(created.name);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Failed to create campaign');
    } finally {
      setCreating(false);
    }
  };

  // Same source of truth as the Engagement / Integrations tabs — do not
  // re-fetch cloud status here (a failed secondary fetch used to hide Meta chips).
  const cloudActive = (
    cloudStatus?.provider
    ?? agentSettings?.whatsAppProvider
    ?? 'bridge'
  ) === 'cloud';
  const cloudConnected = !!cloudStatus?.account?.connected;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [camps, settings, clientsResp] = await Promise.all([
        fetchMyCampaigns(),
        fetchAgentSettings().catch(() => null),
        fetchAllClients().catch(() => [] as Client[]),
      ]);
      setCampaigns(camps);
      setHasCalendly(!!(settings?.hasCalendlyKey ?? agentSettings?.hasCalendlyKey));
      setClients(clientsResp);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [agentSettings?.hasCalendlyKey]);

  useEffect(() => { load(); }, [load]);

  /** Re-fetch campaigns without the tab-wide spinner, which would unmount the open campaign. */
  const reloadCampaignsQuietly = useCallback(async () => {
    setCampaigns(await fetchMyCampaigns());
  }, []);

  useEffect(() => {
    if (!cloudActive) {
      setCloudSpend(null);
      return;
    }
    let cancelled = false;
    fetchWhatsAppCloudSpend()
      .then((s) => { if (!cancelled) setCloudSpend(s); })
      .catch(() => { if (!cancelled) setCloudSpend(null); });
    return () => { cancelled = true; };
  }, [cloudActive, campaigns.length]);

  const spendByName = useMemo(() => {
    const map = new Map<string, CloudSpendCampaign>();
    cloudSpend?.campaigns.forEach((c) => map.set(c.name, c));
    return map;
  }, [cloudSpend]);

  const visibleCampaigns = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...campaigns]
      .filter((c) => !q || c.name.toLowerCase().includes(q))
      .sort((a, b) => {
        const aIso = sortBy === 'createdAt' ? a.createdAt : a.updatedAt;
        const bIso = sortBy === 'createdAt' ? b.createdAt : b.updatedAt;
        const ta = aIso ? new Date(aIso).getTime() : 0;
        const tb = bIso ? new Date(bIso).getTime() : 0;
        if (tb !== ta) return tb - ta;
        return a.name.localeCompare(b.name);
      });
  }, [campaigns, search, sortBy]);

  const pageCount = Math.max(1, Math.ceil(visibleCampaigns.length / CUSTOM_CAMPAIGNS_PAGE_SIZE));
  useEffect(() => { setPage(0); }, [search, sortBy]);
  useEffect(() => {
    if (page > 0 && page >= pageCount) setPage(Math.max(0, pageCount - 1));
  }, [page, pageCount]);

  const pagedCampaigns = useMemo(
    () => visibleCampaigns.slice(page * CUSTOM_CAMPAIGNS_PAGE_SIZE, (page + 1) * CUSTOM_CAMPAIGNS_PAGE_SIZE),
    [visibleCampaigns, page],
  );

  const selected = selectedName
    ? campaigns.find((c) => c.name === selectedName) || null
    : null;

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  if (selected) {
    return (
      <CampaignDetail
        campaign={selected}
        hasCalendly={hasCalendly}
        clients={clients}
        onBack={() => setSelectedName(null)}
        onSaved={async () => { await load(); }}
        onStatusesRefreshed={reloadCampaignsQuietly}
        usingCloud={cloudActive}
        cloudConnected={cloudConnected}
        bridgeConnected={agentSettings?.whatsAppStatus === 'connected'}
        onGotoIntegrations={onGotoIntegrations}
        campaignSpend={spendByName.get(selected.name) || null}
      />
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
          <CampaignIcon sx={{ color: '#ed8936' }} />
          <Typography variant="h6" fontWeight={700}>Custom Campaigns</Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ ml: { sm: 'auto' }, bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
          >
            New campaign
          </Button>
        </Box>
      <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)', mb: 2 }}>
        Configure each day freely — any mix of a message, image, voice note, link or Calendly invite.
        Run a campaign as a daily sequence (one day per day) or as a one-time send where each day
        fires on the exact date you set inside it.{' '}
          {cloudActive
            ? 'With the Official API, Save also syncs changed days (one header: image, video, or PDF). '
            : null}
        <Typography component="span" variant="body2" onClick={onGotoIntegrations}
          sx={{ color: '#ed8936', cursor: 'pointer', textDecoration: 'underline' }}>
          Connect Calendly
        </Typography> to use the Calendly option.
      </Typography>

        {createError && !createOpen && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setCreateError(null)}>
            {createError}
          </Alert>
        )}

        {campaigns.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              mb: 2,
            }}
          >
            <TextField
              size="small"
              placeholder="Search campaigns by name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: { sm: '1 1 220px' }, width: { xs: '100%', sm: 'auto' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'var(--color-text-faint)' }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 180 } }}>
              <InputLabel>Sort by</InputLabel>
              <Select
                label="Sort by"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'updatedAt' | 'createdAt')}
              >
                <MenuItem value="updatedAt">Last modified</MenuItem>
                <MenuItem value="createdAt">Creation Date</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        {campaigns.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 5, px: 2, border: '1px dashed var(--color-border)', borderRadius: 1 }}>
            <CampaignIcon sx={{ fontSize: 40, color: 'var(--color-text-faint)', mb: 1 }} />
            <Typography sx={{ color: 'var(--color-text-subtle)' }}>
              No campaigns yet.
            </Typography>
            <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
              Create one with New campaign, or wait for an admin to grant a campaign to your CEA number.
            </Typography>
          </Box>
        ) : visibleCampaigns.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'var(--color-text-faint)', textAlign: 'center', py: 3 }}>
            No campaigns match “{search.trim()}”.
          </Typography>
        ) : (
          <>
            {pagedCampaigns.map((c) => (
              <CampaignListCard
                key={c.name}
                campaign={c}
                clients={clients}
                spendTotal={cloudActive ? (spendByName.get(c.name)?.totalCost ?? 0) : null}
                spendEstimated={cloudActive && cloudSpend?.estimated !== false}
                usingCloud={cloudActive}
                hasCalendly={hasCalendly}
                onOpen={() => setSelectedName(c.name)}
              />
            ))}
            {visibleCampaigns.length > CUSTOM_CAMPAIGNS_PAGE_SIZE && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 1, mt: 1, mb: 1 }}>
                <Pagination
                  count={pageCount}
                  page={page + 1}
                  onChange={(_, p) => setPage(p - 1)}
                  color="primary"
                  shape="rounded"
                  size="small"
                  siblingCount={1}
                  boundaryCount={1}
                />
                <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                  {page * CUSTOM_CAMPAIGNS_PAGE_SIZE + 1}–{Math.min((page + 1) * CUSTOM_CAMPAIGNS_PAGE_SIZE, visibleCampaigns.length)} of {visibleCampaigns.length}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      <TriggersSection campaigns={campaigns} />

      <Dialog open={createOpen} onClose={() => { if (!creating) setCreateOpen(false); }} fullWidth maxWidth="xs">
        <DialogTitle fontWeight={700}>New campaign</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField
            autoFocus
            label="Campaign name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            inputProps={{ maxLength: 250 }}
            fullWidth
          />
          <TextField
            label="Number of days"
            type="number"
            value={newDays}
            onChange={(e) => setNewDays(e.target.value)}
            inputProps={{ min: 1, max: 60 }}
            fullWidth
          />
          {createError && <Alert severity="error">{createError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => { void submitCreate(); }}
            disabled={creating}
            sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
          >
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── TestSendCard ───
function TestSendCard({
  agentSettings,
  cloudStatus,
  campDays,
  clients,
  onGotoIntegrations,
}: {
  agentSettings: AgentSettings | null;
  cloudStatus: WhatsAppCloudStatus | null;
  campDays: EngagementDay[];
  clients: Client[];
  onGotoIntegrations: () => void;
}) {
  const [clientId, setClientId] = useState<string>('');
  const [mobile, setMobile] = useState('');
  const [day, setDay] = useState(1);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<TestSendResult | null>(null);

  // Readiness depends on which transport is active: a paired session for the
  // bridge, a connected Meta account for the Official WhatsApp API.
  const usingCloud = cloudStatus?.provider === 'cloud';
  const whatsAppOnline = usingCloud
    ? !!cloudStatus?.account.connected
    : agentSettings?.whatsAppStatus === 'connected';
  const enabledDays = campDays
    .filter((d) => d.enabled && d.whatsapp.enabled)
    .map((d) => d.dayNumber);

  const selectedClient = clientId ? clients.find((c) => c.Id === clientId) ?? null : null;

  useEffect(() => {
    if (selectedClient?.Mobile) setMobile(selectedClient.Mobile);
  }, [selectedClient]);

  const canSend = !busy && whatsAppOnline && !!(selectedClient?.Mobile || mobile.trim());

  const handleSend = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await testSendEngagement({
        channel: 'whatsapp',
        dayNumber: day,
        clientId: clientId || undefined,
        mobile: mobile.trim() || undefined,
      });
      setResult(r);
    } catch (e) {
      setResult({ ok: false, error: e instanceof Error ? e.message : 'Send failed' });
    } finally { setBusy(false); }
  };

  return (
    <Card sx={{ bgcolor: 'var(--color-surface)' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <WhatsAppIcon sx={{ color: '#25D366' }} />
          <Typography variant="subtitle1" fontWeight={700}>Test-send a day</Typography>
        </Box>
        <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)', display: 'block', mb: 2 }}>
          Dry-run any day&apos;s WhatsApp content. Pick a client from your list or override
          with a phone number. Sends a real message
          {usingCloud
            ? ' via the Official WhatsApp API — the day\'s template must already be approved by Meta.'
            : ' via the bridge.'}
        </Typography>

        {usingCloud && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Test sends via the Official API are billed by Meta like live marketing messages (est. S$0.10 each). Prefer a number you control.
          </Alert>
        )}

        {!whatsAppOnline && (
          <Alert severity="warning" sx={{ mb: 2 }}
            action={<Button size="small" onClick={onGotoIntegrations} sx={{ textTransform: 'none' }}>Connect</Button>}>
            Connect your WhatsApp in Integrations first.
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
          <FormControl size="small" sx={{ flex: '1 1 240px' }}>
            <InputLabel>Client (optional)</InputLabel>
            <Select
              label="Client (optional)"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            >
              <MenuItem value=""><em>— Use phone below —</em></MenuItem>
              {clients.map((c) => {
                const suffix = !c.Mobile ? ' (no mobile on file)' : '';
                return (
                  <MenuItem key={c.Id} value={c.Id}>
                    {c.Name}{suffix}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Phone (E.164)"
            placeholder="+6591234567"
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            sx={{ flex: '1 1 180px' }}
          />

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Day</InputLabel>
            <Select label="Day" value={day} onChange={(e) => setDay(Number(e.target.value))}>
              {[1, 2, 3, 4, 5, 6, 7].map((n) => {
                const cfg = DAY_CONFIG[n - 1];
                const configured = enabledDays.includes(n);
                return (
                  <MenuItem key={n} value={n} disabled={!configured}>
                    Day {n} · {cfg.title}{!configured ? ' (not configured)' : ''}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            disabled={!canSend}
            onClick={handleSend}
            sx={{
              bgcolor: '#25D366',
              '&:hover': { bgcolor: '#1ebe57' },
              textTransform: 'none',
            }}
          >
            {busy ? 'Sending…' : 'Send test'}
          </Button>
        </Box>
        {selectedClient?.Mobile && (
          <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', mb: 1 }}>
            Phone overrides the selected client&apos;s mobile when different.
          </Typography>
        )}

        {result && (result.ok ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            Sent{result.messageId ? ` (id ${result.messageId})` : ''}.
            {result.rendered?.body && (
              <Typography variant="caption" component="pre" sx={{ m: 0, mt: 0.5, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                {result.rendered.body}
              </Typography>
            )}
            {result.rendered?.mediaUrl && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                Media: <a href={result.rendered.mediaUrl} target="_blank" rel="noreferrer">{result.rendered.mediaUrl}</a>
              </Typography>
            )}
          </Alert>
        ) : (
          <Alert severity="error" sx={{ mt: 1 }}>{result.error || 'Failed'}</Alert>
        ))}
      </CardContent>
    </Card>
  );
}

// ─── Shared provider toggle ───
// The two WhatsApp integrations write to a single stored provider value, so
// switching one on necessarily switches the other off. Rendering both toggles
// from the same component keeps that contract obvious.
function ProviderToggle({
  active,
  disabled,
  disabledReason,
  busy,
  onActivate,
}: {
  active: boolean;
  disabled: boolean;
  disabledReason?: string;
  busy: boolean;
  onActivate: () => void;
}) {
  const control = (
    <FormControlLabel
      control={
        <Switch
          checked={active}
          disabled={disabled || busy || active}
          onChange={(e) => { if (e.target.checked) onActivate(); }}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: '#25D366' },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#25D366' },
          }}
        />
      }
      label={
        <Typography variant="body2" fontWeight={600} sx={{ color: active ? '#25D366' : 'var(--color-text-subtle)' }}>
          {active ? 'Sending with this' : 'Use this to send'}
        </Typography>
      }
    />
  );
  // Tooltips don't fire on disabled controls, so wrap in a plain span.
  return disabled && disabledReason
    ? <Tooltip title={disabledReason}><span>{control}</span></Tooltip>
    : control;
}

// ─── WhatsAppCloudCard (Official WhatsApp API) ───
// Onboards the agent's own WhatsApp Business number through Meta Embedded
// Signup in Coexistence mode: they keep using the WhatsApp Business app, and
// we send approved templates from the same number on their behalf.
function WhatsAppCloudCard({
  cloudStatus,
  onReload,
  onGotoEngagement,
  embedded = false,
}: {
  cloudStatus: WhatsAppCloudStatus | null;
  onReload: () => Promise<void> | void;
  onGotoEngagement: () => void;
  embedded?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const account = cloudStatus?.account;
  const connected = !!account?.connected;
  const active = cloudStatus?.provider === 'cloud';
  const available = !!cloudStatus?.available;
  const canSwitchAccountSource = !!cloudStatus?.canSwitchCloudAccountSource;
  const accountSource = cloudStatus?.accountSource === 'company' ? 'company' : 'personal';
  const usingCompanyAccount = accountSource === 'company';
  const paymentConfirmedMissing = connected && account?.paymentMethodLinked === false;
  // Tech Provider apps cannot read primary_funding_id (BSP-only). Still prompt.
  const paymentUnverifiable = connected && account?.paymentMethodVerifiable === false;
  const paymentAckKey = account?.wabaId ? `sc-cloud-payment-ack:${account.wabaId}` : null;
  const [paymentAcked, setPaymentAcked] = useState(false);
  useEffect(() => {
    if (!paymentAckKey || typeof window === 'undefined') {
      setPaymentAcked(false);
      return;
    }
    setPaymentAcked(window.localStorage.getItem(paymentAckKey) === '1');
  }, [paymentAckKey]);
  const showPaymentPrompt = paymentConfirmedMissing || (paymentUnverifiable && !paymentAcked);
  const metaPaymentSetupUrl = 'https://business.facebook.com/billing_hub/payment_methods';

  const dismissPaymentPrompt = () => {
    if (paymentAckKey && typeof window !== 'undefined') {
      window.localStorage.setItem(paymentAckKey, '1');
    }
    setPaymentAcked(true);
  };

  const handleAccountSourceToggle = async (useCompany: boolean) => {
    const next = useCompany ? 'company' : 'personal';
    if (next === accountSource) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (useCompany) {
        await setWhatsAppCloudAccountSource('company');
        if (!active) await setWhatsAppProvider('cloud');
        setNotice('Sending through the company WhatsApp Business account.');
      } else {
        // Turn Official API off first so switching to personal does not require
        // a personal WABA to already be connected.
        if (active) await setWhatsAppProvider('bridge');
        await setWhatsAppCloudAccountSource('personal');
        setNotice('Company account off. Official API sending is off — pair Agent WhatsApp or connect a personal Business account.');
      }
      await onReload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not switch account');
    } finally {
      setBusy(false);
    }
  };

  const handleConnect = async () => {
    if (isDemoMode()) {
      setBusy(true);
      setError(null);
      setNotice(null);
      try {
        blockDemoMutation();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not connect your WhatsApp Business account');
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!cloudStatus?.appId || !cloudStatus?.configId) {
      setError('WhatsApp API setup is not configured. Contact your administrator.');
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await launchWhatsAppEmbeddedSignup({
        appId: cloudStatus.appId,
        configId: cloudStatus.configId,
      });
      const onboarded = await onboardWhatsAppCloud(result);
      await onReload();
      if (onboarded.account.paymentMethodLinked === true) {
        setNotice('WhatsApp Business account connected. Turn on the toggle to start sending through it.');
      } else {
        // Missing or unverifiable — the payment banner below explains next steps.
        setNotice(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not connect your WhatsApp Business account');
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await disconnectWhatsAppCloud();
      setNotice('Disconnected. Engagement messages will go out through Agent WhatsApp again.');
      await onReload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Disconnect failed');
    } finally { setBusy(false); }
  };

  const handleRefresh = async () => {
    setBusy(true);
    setError(null);
    try {
      await refreshWhatsAppCloud();
      await onReload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Refresh failed');
    } finally { setBusy(false); }
  };

  const handleActivate = async () => {
    if (paymentConfirmedMissing) {
      setError('Add a payment method in Meta Business Suite before sending through the Official API.');
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await setWhatsAppProvider('cloud');
      await onReload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not switch provider');
    } finally { setBusy(false); }
  };

  const statusChip = connected
    ? paymentConfirmedMissing
      ? { label: 'Payment required', color: '#ed8936', bg: '#ed893622' }
      : paymentUnverifiable && !paymentAcked
        ? { label: 'Confirm billing', color: '#ed8936', bg: '#ed893622' }
        : { label: 'Connected', color: '#22c55e', bg: '#22c55e22' }
    : account?.status === 'error'
      ? { label: 'Error', color: '#ef4444', bg: '#ef444422' }
      : { label: 'Not connected', color: 'var(--color-text-faint)', bg: 'var(--color-bg)' };

  const shellSx = {
    bgcolor: embedded ? 'var(--color-bg)' : 'var(--color-surface)',
    border: active ? '1px solid #25D366' : '1px solid var(--color-border)',
    borderRadius: 1,
    p: 1.5,
    ...(embedded ? { boxShadow: 'none' } : {}),
  };

  return (
    <Box sx={shellSx}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
            flexWrap: 'wrap',
          }}
        >
          <CloudIcon sx={{ color: '#25D366' }} />
          <Typography variant="subtitle1" fontWeight={700}>Official WhatsApp API</Typography>
          <Chip label={statusChip.label} size="small" sx={{ bgcolor: statusChip.bg, color: statusChip.color, fontWeight: 600, height: 22 }} />
          {active && (
            <Chip label="Active" size="small" sx={{ bgcolor: '#25D36622', color: '#25D366', fontWeight: 700, height: 22 }} />
          )}
          <Box sx={{ flex: 1 }} />
          <ProviderToggle
            active={active}
            disabled={!connected || paymentConfirmedMissing}
            disabledReason={
              paymentConfirmedMissing
                ? 'Add a payment method in Meta Business Suite first — otherwise Meta accepts sends but never delivers them.'
                : 'Connect your WhatsApp Business account first.'
            }
            busy={busy}
            onActivate={handleActivate}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 1.5,
            mb: 1.5,
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)', flex: 1, minWidth: 0 }}>
            Send the 7-day engagement as Meta-approved templates from your WhatsApp Business number — no QR
            pairing, and your phone does not need to stay online. You can still chat with clients normally
            in the WhatsApp Business app.
            {canSwitchAccountSource && (
              <> Turning off company account also turns off Official API sending.</>
            )}
          </Typography>
          {canSwitchAccountSource && (
            <FormControlLabel
              control={
                <Switch
                  checked={usingCompanyAccount}
                  disabled={busy || (!usingCompanyAccount && cloudStatus?.companyAccountConnected === false)}
                  onChange={(e) => { void handleAccountSourceToggle(e.target.checked); }}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#25D366' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#25D366' },
                  }}
                />
              }
              label={
                <Typography variant="body2" fontWeight={600} sx={{ color: usingCompanyAccount ? '#25D366' : 'var(--color-text-subtle)', whiteSpace: 'nowrap' }}>
                  {usingCompanyAccount ? 'Using company account' : 'Use company account'}
                </Typography>
              }
              sx={{ m: 0, flexShrink: 0, ml: { sm: 'auto' } }}
            />
          )}
        </Box>
        {canSwitchAccountSource && !cloudStatus?.companyAccountConnected && (
          <Alert severity="warning" sx={{ mb: 1.5, py: 0.5 }}>
            <Typography variant="caption">
              Company WhatsApp Business account is not connected in the database.
            </Typography>
          </Alert>
        )}

        {!usingCompanyAccount && (
          <Alert severity="warning" sx={{ mb: 2, py: 1, border: '1px solid rgba(237, 137, 54, 0.55)' }}>
            <Typography variant="body2" fontWeight={800} sx={{ mb: 0.5, color: 'inherit' }}>
              Template message charges are billed to you — not the company
            </Typography>
            <Typography variant="caption" component="div" sx={{ color: 'inherit', lineHeight: 1.5 }}>
              When you send WhatsApp templates through the Official API, Meta bills those conversation /
              template fees to <b>your</b> WhatsApp Business / Meta account. The company does <b>not</b> pay
              these sending charges on your behalf.
            </Typography>
          </Alert>
        )}

        {!available && !isDemoMode() && (
          <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>
            <Typography variant="caption">
              This integration isn't configured on the server yet. Contact your administrator.
            </Typography>
          </Alert>
        )}

        {!connected && available && !usingCompanyAccount && (
          <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
            <Typography variant="caption" component="div" fontWeight={700} sx={{ mb: 0.5 }}>
              Before you start
            </Typography>
            <Typography variant="caption" component="div">
              1. Install the <b>WhatsApp Business app</b> and make sure your number is registered in it.
            </Typography>
            <Typography variant="caption" component="div">
              2. Have your <b>Facebook login</b> ready — Meta will ask you to sign in and approve access.
            </Typography>
            <Typography variant="caption" component="div">
              3. Pick the option saying the number is <b>already in use with the WhatsApp Business app</b>,
              then verify it by SMS or call.
            </Typography>
            <Typography variant="caption" component="div">
              4. After connecting, add a <b>payment method</b> on that WhatsApp Business account in Meta —
              without it, Meta accepts sends but clients never receive them.
            </Typography>
          </Alert>
        )}

        {showPaymentPrompt && (
          <Alert severity={paymentConfirmedMissing ? 'error' : 'warning'} sx={{ mb: 2, py: 1 }}>
            <Typography variant="body2" fontWeight={800} sx={{ mb: 0.5 }}>
              {paymentConfirmedMissing
                ? 'Payment method missing on this WhatsApp Business account'
                : 'Add a payment method so messages can deliver'}
            </Typography>
            <Typography variant="caption" component="div" sx={{ mb: 1, lineHeight: 1.5 }}>
              Meta will report sends as successful, but messages will not reach clients until a payment
              method is linked to <b>this</b> WhatsApp Business account (not only an ad account).
              Open Meta Business Suite → Payment methods, add a card, then attach it under WhatsApp
              Accounts → Settings → Payment settings.
              {!paymentConfirmedMissing && (
                <>
                  {' '}
                  (We can&apos;t verify this automatically as a Tech Provider — confirm once you&apos;ve set it up.)
                </>
              )}
            </Typography>
            <Button
              size="small"
              variant="contained"
              href={metaPaymentSetupUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textTransform: 'none', mr: 1, bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' } }}
            >
              Open Meta payment methods
            </Button>
            {paymentConfirmedMissing ? (
              <Button size="small" variant="outlined" onClick={handleRefresh} disabled={busy} sx={{ textTransform: 'none' }}>
                {busy ? 'Checking…' : 'Refresh status'}
              </Button>
            ) : (
              <Button size="small" variant="outlined" onClick={dismissPaymentPrompt} sx={{ textTransform: 'none' }}>
                I&apos;ve added a payment method
              </Button>
            )}
          </Alert>
        )}

        {connected && (
          <Box sx={{ mb: 2 }}>
            {usingCompanyAccount && (
              <Chip
                label="Company account"
                size="small"
                sx={{ bgcolor: '#6366f122', color: '#6366f1', fontWeight: 700, height: 22, mb: 0.75 }}
              />
            )}
            {account?.displayPhoneNumber && (
              <Typography variant="body2">
                Business number: <b>{account.displayPhoneNumber}</b>
                {account.verifiedName ? ` (${account.verifiedName})` : ''}
              </Typography>
            )}
            {account?.connectedAt && (
              <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block' }}>
                Connected {new Date(account.connectedAt).toLocaleString()}
              </Typography>
            )}
            {account?.isCoexistence === false && (
              <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                <Typography variant="caption">
                  Meta reports this number is not shared with the WhatsApp Business app, so you may not be
                  able to chat from the app while the API is connected.
                </Typography>
              </Alert>
            )}
            {account?.qualityRating && (
              <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block' }}>
                Quality rating: {account.qualityRating}
              </Typography>
            )}
          </Box>
        )}

        {connected && (
          <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
            <Typography variant="caption">
              Messages sent this way must use templates Meta has approved. Open each day in the{' '}
              <Box
                component="span"
                onClick={onGotoEngagement}
                sx={{ color: '#ed8936', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}
              >
                7-Day Engagement
              </Box>{' '}
              tab and press <b>Create template</b> to submit it for review.
            </Typography>
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {!connected && !usingCompanyAccount && (
            <Button
              variant="contained"
              onClick={handleConnect}
              disabled={busy || (!available && !isDemoMode())}
              sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1ebe57' }, textTransform: 'none' }}
            >
              {busy ? 'Opening Meta…' : 'Connect WhatsApp Business'}
            </Button>
          )}
          {connected && (
            <>
              <Button variant="outlined" onClick={handleRefresh} disabled={busy} sx={{ textTransform: 'none' }}>
                {busy ? 'Refreshing…' : 'Refresh status'}
              </Button>
              {!usingCompanyAccount && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDisconnect}
                  disabled={busy}
                  sx={{ textTransform: 'none' }}
                >
                  Disconnect
                </Button>
              )}
            </>
          )}
        </Box>

        {connected && !usingCompanyAccount && (
          <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', mt: 1 }}>
            Disconnecting here stops us sending. To fully revoke access, also use WhatsApp Business app →
            Settings → Account → Business Platform → Disconnect.
          </Typography>
        )}
        {usingCompanyAccount && (
          <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', mt: 1 }}>
            The company account cannot be disconnected from here. Switch back to your personal account to
            manage your own connection.
          </Typography>
        )}

        {notice && <Alert severity="success" sx={{ mt: 1 }}>{notice}</Alert>}
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Box>
  );
}

// ─── WhatsAppPairingCard ───
function WhatsAppPairingCard({
  agentSettings,
  cloudStatus,
  onSettingsReload,
  embedded = false,
}: {
  agentSettings: AgentSettings | null;
  cloudStatus: WhatsAppCloudStatus | null;
  onSettingsReload: () => Promise<void> | void;
  embedded?: boolean;
}) {
  const [liveState, setLiveState] = useState<WhatsAppState | null>(null);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const closeRef = useRef<(() => void) | null>(null);

  useEffect(() => () => closeRef.current?.(), []);

  const effectiveStatus = liveState?.status
    ?? (agentSettings?.whatsAppStatus as WhatsAppState['status'] | undefined)
    ?? 'disconnected';
  const effectivePhone = liveState?.phone || agentSettings?.whatsAppPhoneNumber || null;

  const startPairing = () => {
    setError(null);
    setLiveState({ status: 'initializing', qrDataUrl: null, phone: null });
    setStreaming(true);
    closeRef.current?.();
    closeRef.current = openWhatsAppPairingStream(
      (state) => {
        setLiveState(state);
        if (state.status === 'connected') {
          onSettingsReload();
          // Auto-close stream on connect to free the SSE; user can reopen if needed.
          setTimeout(() => { closeRef.current?.(); closeRef.current = null; setStreaming(false); }, 1000);
        }
      },
      (msg) => { setError(msg); },
    );
  };

  // Purge the bridge session (logout + delete auth store). This is the only way
  // to force a fresh QR: a merely-local "Cancel" leaves the bridge session alive,
  // so the next pair attempt reuses the stuck session and never re-emits a QR.
  // When `restart` is set, we immediately reopen pairing so a new QR appears.
  const handleDisconnect = async (restart = false) => {
    setBusy(true);
    setError(null);
    try {
      closeRef.current?.();
      closeRef.current = null;
      setStreaming(false);
      await disconnectWhatsApp();
      setLiveState({ status: 'disconnected', qrDataUrl: null, phone: null });
      await onSettingsReload();
      if (restart) startPairing();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Disconnect failed');
    } finally { setBusy(false); }
  };

  const statusLabel: Record<string, { label: string; color: string; bg: string }> = {
    initializing: { label: 'Initializing…', color: '#ed8936', bg: '#ed893622' },
    pairing:      { label: 'Scan QR code',  color: '#ed8936', bg: '#ed893622' },
    authenticated:{ label: 'Authenticated', color: '#3b82f6', bg: '#3b82f622' },
    connected:    { label: 'Connected',     color: '#22c55e', bg: '#22c55e22' },
    disconnected: { label: 'Not connected', color: 'var(--color-text-faint)', bg: 'var(--color-bg)' },
    error:        { label: 'Error',         color: '#ef4444', bg: '#ef444422' },
  };
  const s = statusLabel[effectiveStatus] || statusLabel.disconnected;

  // Provider selection lives on the agent settings row; 'bridge' is the default
  // and covers agents who predate the Official WhatsApp API integration.
  const providerActive = (cloudStatus?.provider ?? agentSettings?.whatsAppProvider ?? 'bridge') === 'bridge';

  const handleActivate = async () => {
    setBusy(true);
    setError(null);
    try {
      await setWhatsAppProvider('bridge');
      await onSettingsReload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not switch provider');
    } finally { setBusy(false); }
  };

  const shellSx = {
    bgcolor: embedded ? 'var(--color-bg)' : 'var(--color-surface)',
    border: providerActive ? '1px solid #25D366' : '1px solid var(--color-border)',
    borderRadius: 1,
    p: 1.5,
    ...(embedded ? { boxShadow: 'none' } : {}),
  };

  return (
    <Box sx={shellSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <WhatsAppIcon sx={{ color: '#25D366' }} />
          <Typography variant="subtitle1" fontWeight={700}>Agent WhatsApp</Typography>
          <Chip
            label={s.label}
            size="small"
            sx={{ bgcolor: s.bg, color: s.color, fontWeight: 600, height: 22 }}
          />
          {providerActive && (
            <Chip label="Active" size="small" sx={{ bgcolor: '#25D36622', color: '#25D366', fontWeight: 700, height: 22 }} />
          )}
          {liveState?.mock && (
            <Chip label="MOCK" size="small" sx={{ bgcolor: '#6366f122', color: '#6366f1', fontWeight: 700, height: 22 }} />
          )}
          <Box sx={{ flex: 1 }} />
          <ProviderToggle
            active={providerActive}
            disabled={false}
            busy={busy}
            onActivate={handleActivate}
          />
        </Box>
        <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)', display: 'block', mb: 2 }}>
          Connect your WhatsApp by scanning a QR code. Once paired, the 7-day engagement will be sent from your number.
          Messages go out as normal chats — no Meta template approval needed.
        </Typography>

        {effectiveStatus === 'connected' && (
          <Box sx={{ mb: 2 }}>
            {effectivePhone && (
              <Typography variant="body2">Paired number: <b>{effectivePhone}</b></Typography>
            )}
            {agentSettings?.whatsAppPairedAt && (
              <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                Since {new Date(agentSettings.whatsAppPairedAt).toLocaleString()}
              </Typography>
            )}
          </Box>
        )}

        {(effectiveStatus === 'pairing' || effectiveStatus === 'initializing') && streaming && (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{
              width: 220, height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid var(--color-border)', borderRadius: 1, bgcolor: 'white',
            }}>
              {liveState?.qrDataUrl
                ? <Box component="img" src={liveState.qrDataUrl} sx={{ width: '100%', height: '100%' }} alt="WhatsApp QR" />
                : liveState?.mock
                  ? <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>Mock mode —<br/>simulating pairing…</Typography>
                  : <CircularProgress size={32} />}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>Steps</Typography>
              <Typography variant="caption" component="div" sx={{ color: 'var(--color-text-subtle)', mb: 0.5 }}>
                1. Open WhatsApp on your phone
              </Typography>
              <Typography variant="caption" component="div" sx={{ color: 'var(--color-text-subtle)', mb: 0.5 }}>
                2. Tap <b>Settings → Linked Devices → Link a Device</b>
              </Typography>
              <Typography variant="caption" component="div" sx={{ color: 'var(--color-text-subtle)' }}>
                3. Point your camera at the QR on the left
              </Typography>
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {effectiveStatus !== 'connected' && !streaming && (
            <Button
              variant="contained"
              onClick={startPairing}
              sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1ebe57' }, textTransform: 'none' }}
            >
              {effectiveStatus === 'disconnected' ? 'Connect WhatsApp' : 'Retry pairing'}
            </Button>
          )}
          {streaming && effectiveStatus !== 'connected' && (
            <Button
              variant="outlined"
              onClick={() => { closeRef.current?.(); closeRef.current = null; setStreaming(false); setLiveState(null); }}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
          )}
          {effectiveStatus === 'connected' && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => handleDisconnect(false)}
              disabled={busy}
              sx={{ textTransform: 'none' }}
            >
              {busy ? 'Disconnecting…' : 'Disconnect'}
            </Button>
          )}
          {/* Stuck mid-pairing (e.g. session reached "authenticated" but the SSE
              timed out with a 524 before "connected"): purge the bridge session
              and reopen pairing so a fresh QR is generated. A plain Cancel/Retry
              would reuse the stuck session and never show a new QR. */}
          {(effectiveStatus === 'authenticated' || effectiveStatus === 'error') && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => handleDisconnect(true)}
              disabled={busy}
              sx={{ textTransform: 'none' }}
            >
              {busy ? 'Resetting…' : 'Disconnect & show new QR'}
            </Button>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Box>
  );
}

// ─── Engagement tab scaffolding ───

/** Numbered section wrapper that gives the engagement tab its 1-2-3 reading order. */
function EngagementSection({ step, title, subtitle, action, children }: {
  step: number;
  title: string;
  subtitle: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card sx={{ bgcolor: 'var(--color-surface)' }}>
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
          <Box sx={{
            width: 24, height: 24, borderRadius: '50%', flexShrink: 0, mt: '2px',
            bgcolor: 'rgba(237, 137, 54, 0.15)', color: '#ed8936',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: '0.75rem',
          }}>
            {step}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={800}>{title}</Typography>
            <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)' }}>{subtitle}</Typography>
          </Box>
          {action}
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

/** "3 Approved" style pill in the Meta approval overview. Greyed out at zero. */
function TemplateCountChip({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <Chip
      label={`${count} ${label}`}
      size="small"
      sx={{
        height: 24,
        fontWeight: 700,
        fontSize: '0.68rem',
        color: count > 0 ? color : 'var(--color-text-faint)',
        bgcolor: count > 0 ? `${color}1f` : 'var(--color-bg)',
      }}
    />
  );
}

// ─── Day list ───

function StatusColumn({
  title,
  label,
  severity,
  hint,
  loading,
}: {
  title: string;
  label: string;
  severity: 'ok' | 'warning' | 'error' | 'muted';
  hint?: string;
  loading?: boolean;
}) {
  const colors = {
    ok: { color: '#22c55e', bg: 'rgba(34, 197, 94, 0.1)' },
    warning: { color: '#ed8936', bg: 'rgba(237, 137, 54, 0.12)' },
    error: { color: ATTENTION_RED, bg: 'rgba(239, 68, 68, 0.12)' },
    muted: { color: 'var(--color-text-faint)', bg: 'var(--color-bg)' },
  };
  const style = colors[severity];
  const content = (
    <Box sx={{ minWidth: 0, flex: { xs: '1 1 0', sm: '0 0 auto' }, width: { xs: 'auto', sm: 132 } }}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: 'var(--color-text-faint)',
          fontSize: '0.7rem',
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          mb: 0.4,
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: loading ? 'center' : 'flex-start',
          fontWeight: 800,
          fontSize: { xs: '0.75rem', sm: '0.88rem' },
          lineHeight: 1.25,
          color: loading ? 'var(--color-text-faint)' : style.color,
          px: 1,
          py: 0.6,
          borderRadius: 1,
          bgcolor: loading ? 'var(--color-bg)' : style.bg,
          minHeight: 32,
          overflow: 'hidden',
          whiteSpace: { xs: 'normal', sm: 'nowrap' },
          textOverflow: { sm: 'ellipsis' },
        }}
      >
        {loading ? <CircularProgress size={16} sx={{ color: '#ed8936' }} /> : label}
      </Typography>
    </Box>
  );
  return hint && !loading ? <Tooltip title={hint}>{content}</Tooltip> : content;
}

function dayKindIcon(kind: DayKind) {
  switch (kind) {
    case 'text': return <EditIcon sx={{ fontSize: 15 }} />;
    case 'image': return <ImageIcon sx={{ fontSize: 15 }} />;
    case 'text_link': return <PictureAsPdfIcon sx={{ fontSize: 15 }} />;
    case 'text_image': return <ImageIcon sx={{ fontSize: 15 }} />;
    case 'voice': return <MicIcon sx={{ fontSize: 15 }} />;
    case 'event': return <EventIcon sx={{ fontSize: 15 }} />;
  }
}

/** One-line description of what rides along with the text on this day. */
function dayAttachmentLabel(cfg: DayConfig, usingCloud = false): string {
  if (cfg.autoImageSource) return 'Weekly sales image, attached automatically';
  if (cfg.autoLinkLabel) return 'PDF built for each client automatically';
  if (cfg.kind === 'event') return 'Your Calendly scheduling link, added to the message';
  if (cfg.hasVoice) return usingCloud ? 'Tracked voice link' : 'Your recorded voice note';
  if (cfg.hasImage) return 'Your uploaded image';
  return 'Text only';
}

function daySubtitle(cfg: DayConfig, usingCloud: boolean): string {
  if (usingCloud && cfg.hasVoice) return 'Voice is sent as a tracked link';
  return cfg.subtitle;
}

interface DayRowProps {
  day: EngagementDay;
  idx: number;
  cfg: DayConfig;
  campStartDay: number;
  isLast: boolean;
  onPatchDay: (idx: number, patch: Partial<EngagementDay>) => void;
  onEdit: () => void;
  agentSettings: AgentSettings | null;
  /** True when this agent sends through Meta, which makes each day a template. */
  usingCloud: boolean;
  /** Meta template state for this day. Null when not on the Official WhatsApp API. */
  templateState: EngagementTemplateState | null;
  /** Meta approval statuses are being refreshed from Meta. */
  metaStatusLoading?: boolean;
  /** Official API Meta spend for this day (SGD), when available. */
  spendCost?: number | null;
  /** Another save or submission is in flight — block Edit while busy. */
  busy: boolean;
}

/**
 * A day as a row on a vertical timeline: what it sends, whether it is ready,
 * and — on the Official API — its Meta approval status. Editing and template
 * submission happen in the Edit dialog.
 */
function DayRow({
  day, idx, cfg, campStartDay, isLast, onPatchDay, onEdit, agentSettings,
  usingCloud, templateState, metaStatusLoading, spendCost, busy,
}: DayRowProps) {
  const connector = connectorIssue(day, cfg, agentSettings, usingCloud);
  const metaIssue = metaTemplateIssue(usingCloud, templateState);
  const off = !day.enabled;
  const preview = day.whatsapp.message.trim();

  const metaLabel = usingCloud
    ? (metaIssue?.label ?? (templateState?.status ? templateStatusStyle(templateState.status).label : 'Not created'))
    : '—';
  const metaSeverity: 'ok' | 'warning' | 'error' | 'muted' = !usingCloud
    ? 'muted'
    : metaIssue?.severity === 'error'
      ? 'error'
      : metaIssue?.severity === 'warning'
        ? 'warning'
        : 'ok';
  const metaHint = usingCloud && templateState?.status
    ? templateStatusStyle(templateState.status).hint
    : usingCloud
      ? 'No template on Meta yet — open Edit and create one.'
      : undefined;
  const canEnable = metaTemplateApproved(usingCloud, templateState);
  const enableBlocked = !day.enabled && !canEnable;
  const switchTitle = day.enabled
    ? 'Switch this day off'
    : enableBlocked
      ? 'Meta must approve this day\'s template before you can switch it on.'
      : 'Switch this day on';

  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch' }}>
      {/* Timeline rail */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 32, flexShrink: 0 }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontSize: '0.78rem',
          bgcolor: off ? 'var(--color-bg)' : 'rgba(237, 137, 54, 0.12)',
          color: off ? 'var(--color-text-faint)' : '#ed8936',
          border: '2px solid', borderColor: off ? 'var(--color-border)' : '#ed8936',
        }}>
          {campStartDay + day.dayNumber - 1}
        </Box>
        {!isLast && <Box sx={{ flex: 1, width: 2, bgcolor: 'var(--color-border)', my: 0.5, borderRadius: 1 }} />}
      </Box>

      {/* Card — stacks on mobile so status / switch / Edit never overlap the title */}
      <Box sx={{
        flex: 1, minWidth: 0, mb: 1.5, p: { xs: 1.25, sm: 1.5 }, borderRadius: 1,
        bgcolor: 'var(--color-bg)',
        border: '1px solid var(--color-border)',
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'flex-start' },
        gap: { xs: 1.25, sm: 1.5 },
      }}>
        <Box sx={{ flex: 1, minWidth: 0, opacity: off ? 0.65 : 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: off ? 'var(--color-text-subtle)' : 'inherit' }}>
              {cfg.title}
            </Typography>
            {off && (
              <Chip label="Off" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'var(--color-surface)', color: 'var(--color-text-faint)' }} />
            )}
            <Chip
              icon={dayKindIcon(cfg.kind)}
              label={dayAttachmentLabel(cfg, usingCloud)}
              size="small"
      sx={{
                height: 20,
                fontSize: '0.62rem',
                bgcolor: 'var(--color-surface)',
                maxWidth: '100%',
                '& .MuiChip-label': { px: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' },
              }}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden', mt: 0.75,
              color: preview ? 'var(--color-text-subtle)' : 'var(--color-text-faint)',
              fontStyle: preview ? 'normal' : 'italic',
            }}
          >
            {preview || 'No message written yet.'}
          </Typography>
        </Box>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: { sm: 'wrap' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1.25,
          flexShrink: 0,
          pt: { xs: 1, sm: 0 },
          borderTop: { xs: '1px solid var(--color-border)', sm: 'none' },
        }}>
          <Box sx={{ display: 'flex', gap: 1, flex: '1 1 auto', minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
            <StatusColumn
              title="Connector"
              label={connector?.label ?? 'Ready'}
              severity={connector ? (connector.severity === 'error' ? 'error' : 'warning') : 'ok'}
            />
            {usingCloud && (
              <StatusColumn
                title="Meta"
                label={metaLabel}
                severity={metaSeverity}
                hint={metaHint}
                loading={metaStatusLoading}
              />
            )}
            {usingCloud && spendCost != null && (
              <StatusColumn
                title="Cost"
                label={formatSgd(spendCost)}
                severity="muted"
                hint={SPEND_ESTIMATE_HINT}
              />
            )}
          </Box>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 0.75,
            width: { xs: '100%', sm: 'auto' },
            flexShrink: 0,
          }}>
            <Tooltip title={switchTitle}>
              <span>
        <Switch
          size="small"
          checked={day.enabled}
                  disabled={enableBlocked}
          onChange={(e) => onPatchDay(idx, { enabled: e.target.checked })}
          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#ed8936' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ed8936' } }}
        />
              </span>
            </Tooltip>
            <Button
            size="small"
              variant="contained"
              startIcon={<EditIcon sx={{ fontSize: '16px !important' }} />}
              onClick={onEdit}
              disabled={busy}
              sx={{
                bgcolor: '#ed8936',
                '&:hover': { bgcolor: '#dd7826' },
                textTransform: 'none',
                fontWeight: 800,
                py: 0.35,
                px: 1.75,
                boxShadow: '0 2px 8px rgba(237, 137, 54, 0.45)',
                flexShrink: 0,
              }}
            >
              Edit
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

interface DayEditorDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => Promise<void>;
  saving: boolean;
  day: EngagementDay;
  idx: number;
  cfg: DayConfig;
  campStartDay: number;
  onPatchDay: (idx: number, patch: Partial<EngagementDay>) => void;
  onPatchChannel: (idx: number, channel: 'whatsapp' | 'email', patch: Partial<DayChannel>) => void;
  agentSettings: AgentSettings | null;
  eventTypes: CalendlyEventType[];
  onGotoIntegrations: () => void;
  usingCloud: boolean;
  cloudConnected: boolean;
  templateState: EngagementTemplateState | null;
  onSyncTemplate: () => void;
  syncingTemplate: boolean;
  syncResult: TemplateSyncDayResult | null;
}

/** Full editor for one day. Save writes to the database; Meta actions live here too. */
function DayEditorDialog({
  open, onClose, onSave, saving, day, idx, cfg, campStartDay, onPatchDay, onPatchChannel,
  agentSettings, eventTypes, onGotoIntegrations, usingCloud, cloudConnected, templateState,
  onSyncTemplate, syncingTemplate, syncResult,
}: DayEditorDialogProps) {
  const selectedEvent = eventTypes.find((e) => e.uri === agentSettings?.calendlyEventTypeUri) || null;
  const [saveError, setSaveError] = useState<string | null>(null);
  const openSnapshotRef = useRef<EngagementDay | null>(null);

  useEffect(() => {
    if (open) {
      openSnapshotRef.current = snapshotEngagementDay(day);
    }
  }, [open, idx]);

  const hasTemplate = !!templateState?.status;
  const showCreateTemplate = usingCloud && cloudConnected && !hasTemplate;
  /** On the Official API, sync once Meta already has a template. Bridge keeps a local Save. */
  const showSyncToMeta = usingCloud && hasTemplate;
  const showSave = !usingCloud;
  const metaBusy = saving || syncingTemplate;
  const canEnable = metaTemplateApproved(usingCloud, templateState);
  const enableBlocked = !day.enabled && !canEnable;
  const calendlyReady = !!(agentSettings?.hasCalendlyKey && agentSettings?.calendlyEventTypeUri);
  const day6NeedsCalendly = cfg.kind === 'event' && !calendlyReady;
  const calendlyBlockHint = 'Connect Calendly and pick an event type in Integrations before creating this template.';
  // Same visibility rule as ChannelEditor: bridge Day 5 has no text field to fix.
  const bodyIssues = (cfg.hasText || usingCloud) ? templateBodyIssues(day.whatsapp.message) : [];
  const bodyBlocked = bodyIssues.length > 0;

  const handleSave = async () => {
    if (bodyBlocked) return;
    setSaveError(null);
    try {
      await onSave();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Could not save this day');
    }
  };

  const handleSyncToMeta = () => {
    if (bodyBlocked) return;
    setSaveError(null);
    onSyncTemplate();
  };

  const handleCancel = () => {
    const snap = openSnapshotRef.current;
    if (snap) {
      // Keep uploaded media — it is already in storage. Cancel only reverts
      // the message text (and on/off) as the agent was typing.
      onPatchChannel(idx, 'whatsapp', { message: snap.whatsapp.message });
      onPatchDay(idx, { enabled: snap.enabled });
    }
    setSaveError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={metaBusy ? () => {} : handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" fontWeight={800}>
            Day {campStartDay + day.dayNumber - 1} · {cfg.title}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Tooltip title={enableBlocked ? 'Meta must approve this day\'s template before you can switch it on.' : ''}>
            <span>
              <FormControlLabel
                sx={{ mr: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={day.enabled}
                    disabled={enableBlocked}
                    onChange={(e) => onPatchDay(idx, { enabled: e.target.checked })}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#ed8936' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ed8936' } }}
                  />
                }
                label={<Typography variant="caption" fontWeight={700}>{day.enabled ? 'On' : 'Off'}</Typography>}
              />
            </span>
          </Tooltip>
      </Box>
        <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)' }}>{daySubtitle(cfg, usingCloud)}</Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {cfg.autoLinkLabel && (
            <Alert severity="info" icon={<LinkIcon />} sx={{ py: 0.5 }}>
              <Typography variant="caption">
                <b>{cfg.autoLinkLabel}</b> — generated automatically from the client's preferred location when this message is sent.
              </Typography>
            </Alert>
          )}
          {cfg.kind === 'event' && (
            <Alert
              severity={agentSettings?.hasCalendlyKey && agentSettings?.calendlyEventTypeUri ? 'success' : 'warning'}
              icon={<EventIcon />}
              sx={{ py: 0.5 }}
              action={
                !(agentSettings?.hasCalendlyKey && agentSettings?.calendlyEventTypeUri) && (
                  <Button size="small" onClick={onGotoIntegrations} sx={{ textTransform: 'none' }}>Connect</Button>
                )
              }
            >
              <Typography variant="caption">
                {agentSettings?.hasCalendlyKey
                  ? (agentSettings.calendlyEventTypeUri
                    ? <>Your Calendly scheduling link for <b>{selectedEvent?.name || 'your selected event type'}</b> ({selectedEvent ? `${selectedEvent.duration}min` : '—'}) is added to the message when it sends.</>
                      : 'Calendly connected. Select a default event type in Integrations.')
                  : 'Connect Calendly in Integrations to enable this day.'}
              </Typography>
            </Alert>
          )}

          {/* Shared media (upload or auto-sourced image) */}
          {(cfg.hasImage || cfg.hasFile || cfg.hasVoice) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1, border: '1px dashed var(--color-border)' }}>
              <Typography variant="caption" fontWeight={700} sx={{ color: 'var(--color-text-subtle)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {cfg.autoImageSource ? 'Weekly Sales Update Image' : cfg.hasImage ? 'Image' : cfg.hasFile ? 'File' : 'Voice Note'}
              </Typography>
              {cfg.autoImageSource === 'picker_sales_update' ? (
                <>
                  <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                  Attached automatically from the latest weekly sales update. There is nothing to upload for this day.
                  </Typography>
                  <PickerSalesUpdatePreview />
                </>
              ) : (
                <>
                  <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                    {cfg.hasVoice && usingCloud
                      ? 'Recorded once — sent as a tracked link in the message on the Official API.'
                      : 'Uploaded once — attached to the WhatsApp message for this day.'}
                  </Typography>
                  {cfg.hasImage && (
                    <ImageUploader
                      url={day.whatsapp.mediaUrl}
                      slot={`day${day.dayNumber}`}
                      onUploaded={(url) => onPatchChannel(idx, 'whatsapp', { mediaUrl: url || null })}
                    />
                  )}
                </>
              )}
              {cfg.hasFile && (
                <FileUploader
                  url={day.whatsapp.mediaUrl}
                  slot={`day${day.dayNumber}`}
                  onUploaded={(url) => onPatchChannel(idx, 'whatsapp', { mediaUrl: url || null })}
                />
              )}
              {cfg.hasVoice && (
                <VoiceRecorder
                  url={day.whatsapp.mediaUrl}
                  slot={`day${day.dayNumber}`}
                  onUploaded={(url) => onPatchChannel(idx, 'whatsapp', { mediaUrl: url || null })}
                />
              )}
            </Box>
          )}

            <ChannelEditor
              day={day}
              cfg={cfg}
          usingCloud={usingCloud}
              onPatch={(patch) => onPatchChannel(idx, 'whatsapp', patch)}
            />

        {bodyBlocked && (
          <Alert severity="error" sx={{ py: 0.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              {bodyIssues.map((msg) => (
                <Typography key={msg} variant="caption" component="div">{msg}</Typography>
              ))}
            </Box>
          </Alert>
        )}
        {saveError && (
          <Alert severity="error" sx={{ py: 0.5 }}>
            <Typography variant="caption">{saveError}</Typography>
          </Alert>
        )}
        {syncResult && <TemplateSyncResultAlert result={syncResult} />}
        {usingCloud && !cloudConnected && (
          <Alert severity="warning" icon={<CloudIcon />} sx={{ py: 0.5 }}>
            <Typography variant="caption">
              Connect your WhatsApp Business account in{' '}
              <Button size="small" onClick={onGotoIntegrations} sx={{ textTransform: 'none', p: 0, minWidth: 0, verticalAlign: 'baseline' }}>
                Integrations
              </Button>
              {' '}before creating a template.
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={handleCancel} disabled={metaBusy} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        {showCreateTemplate && (
          <Tooltip title={day6NeedsCalendly ? calendlyBlockHint : (bodyIssues[0] || '')}>
            <span>
              <Button
                onClick={handleSyncToMeta}
                disabled={metaBusy || day6NeedsCalendly || bodyBlocked}
                variant="outlined"
                startIcon={syncingTemplate ? <CircularProgress size={16} /> : <CloudIcon />}
                sx={{ textTransform: 'none', borderColor: '#ed8936', color: '#ed8936' }}
              >
                {syncingTemplate ? 'Sending to Meta…' : 'Create template'}
              </Button>
            </span>
          </Tooltip>
        )}
        {showSyncToMeta && (
          <Tooltip title={day6NeedsCalendly ? calendlyBlockHint : (bodyIssues[0] || '')}>
            <span>
              <Button
                onClick={handleSyncToMeta}
                disabled={metaBusy || day6NeedsCalendly || bodyBlocked}
                variant="contained"
                startIcon={syncingTemplate ? <CircularProgress size={16} color="inherit" /> : <CloudIcon />}
                sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none', minWidth: 120 }}
              >
                {syncingTemplate ? 'Syncing to Meta…' : 'Sync to Meta'}
              </Button>
            </span>
          </Tooltip>
        )}
        {showSave && (
          <Button
            onClick={handleSave}
            disabled={metaBusy || bodyBlocked}
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none', minWidth: 120 }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** What Meta said about the last submission of one day. */
function TemplateSyncResultAlert({ result }: { result: TemplateSyncDayResult }) {
  const failed = result.action === 'failed';
  const cooldown = result.action === 'cooldown';
  return (
    <Alert severity={failed ? 'error' : cooldown ? 'warning' : 'success'} sx={{ py: 0.25 }}>
      <Typography variant="caption">
        {result.action === 'created' && (
          <>Submitted to Meta — Status <b>{result.status || 'PENDING'}</b>.</>
        )}
        {result.action === 'updated' && <>Edit submitted to Meta — now <b>{result.status || 'PENDING'}</b>.</>}
        {result.action === 'unchanged' && 'Nothing to send — this day already matches Meta.'}
        {cooldown && (result.nextEditableAt
          ? <>Skipped — Meta allows the next edit after <b>{new Date(result.nextEditableAt).toLocaleString()}</b>.</>
          : 'Skipped — this template is still inside Meta\'s 24-hour edit window.')}
        {failed && (result.error || 'Meta refused the request.')}
      </Typography>
    </Alert>
  );
}

// ─── ChannelEditor (WhatsApp content for a day) ───
function ChannelEditor({ day, cfg, usingCloud, onPatch }: {
  day: EngagementDay;
  cfg: DayConfig;
  /** Meta requires a body on every template, even on days the bridge sends silently. */
  usingCloud: boolean;
  onPatch: (patch: Partial<DayChannel>) => void;
}) {
  const ch = day.whatsapp;
  const messageInputRef = useRef<HTMLTextAreaElement | null>(null);
  const selectionRef = useRef({ start: 0, end: 0 });
  // Day 5 is a bare voice note over the bridge, but its Meta template still
  // needs body text, so the field appears once the Official API is in use.
  const showText = cfg.hasText || usingCloud;

  const captureSelection = (el: HTMLTextAreaElement) => {
    selectionRef.current = { start: el.selectionStart, end: el.selectionEnd };
  };

  const insertPlaceholder = (ph: string) => {
    const el = messageInputRef.current;
    const start = el?.selectionStart ?? selectionRef.current.start;
    const end = el?.selectionEnd ?? selectionRef.current.end;
    const { next, cursor } = insertTextAtSelection(ch.message, ph, start, end);
    if (!fitsBodyLimit(ch.message, next)) return;
    onPatch({ message: next });
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(cursor, cursor);
        captureSelection(el);
      } else {
        selectionRef.current = { start: cursor, end: cursor };
      }
    });
  };

  return (
    <Box sx={{ borderLeft: '3px solid #25D366', pl: 1.5, py: 0.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <WhatsAppIcon sx={{ fontSize: 16, color: '#25D366' }} />
        <Typography variant="caption" fontWeight={700} sx={{ color: '#25D366', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Message
        </Typography>
      </Box>

      {showText && (
        <Box>
          {!cfg.hasText && (
            <Typography variant="caption" component="div" sx={{ color: 'var(--color-text-faint)', mb: 0.5 }}>
              Meta needs wording for every template. On the Official API this text is sent with a tracked link to the voice note.
            </Typography>
          )}
          <TemplateBodyField
            minRows={cfg.kind === 'text' ? 3 : 2}
            maxRows={8}
            value={ch.message}
            inputRef={messageInputRef}
            onValueChange={(next, el) => {
              onPatch({ message: next });
              captureSelection(el);
            }}
            issues={templateBodyIssues(ch.message)}
            inputProps={{
              onSelect: (e: React.SyntheticEvent<HTMLTextAreaElement>) => captureSelection(e.currentTarget),
              onClick: (e: React.MouseEvent<HTMLTextAreaElement>) => captureSelection(e.currentTarget),
              onKeyUp: (e: React.KeyboardEvent<HTMLTextAreaElement>) => captureSelection(e.currentTarget),
            }}
            placeholder={cfg.defaultMessage || 'Hi {{client_name}}! ...'}
            size="small"
          />
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5, alignItems: 'center' }}>
            {PLACEHOLDERS.map((ph) => (
              <Chip
                key={ph}
                label={ph.replace(/\{\{|\}\}/g, '#')}
                size="small"
                onClick={() => insertPlaceholder(ph)}
                sx={{ cursor: 'pointer', height: 20, fontSize: '0.65rem', '&:hover': { bgcolor: 'rgba(237, 137, 54, 0.15)' } }}
              />
            ))}
            {cfg.defaultMessage && ch.message !== cfg.defaultMessage && (
              <Button
                size="small"
                onClick={() => onPatch({ message: cfg.defaultMessage as string })}
                sx={{ textTransform: 'none', fontSize: '0.65rem', minWidth: 0, py: 0, color: 'var(--color-text-subtle)' }}
              >
                Reset to suggested wording
              </Button>
            )}
          </Box>
          <Typography variant="caption" component="div" sx={{ color: 'var(--color-text-faint)', mt: 0.75 }}>
            {usingCloud
              ? `Meta reviews this wording. Keep it specific about who you are and what you are sending, do not start or end with a placeholder (a value in {{}}), keep placeholders apart, and use at most ${TEMPLATE_BODY_MAX_EMOJIS} emojis.`
              : `Do not start or end the message with a placeholder (a value in {{}}), keep placeholders apart, and use at most ${TEMPLATE_BODY_MAX_EMOJIS} emojis.`}
          </Typography>
        </Box>
      )}

    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Access Codes Section
// ─────────────────────────────────────────────────────────────────────────
const EXPIRY_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '14 days', value: 14 },
  { label: '30 days', value: 30 },
  { label: '60 days', value: 60 },
  { label: '90 days', value: 90 },
];

function AccessCodesSection() {
  const [data, setData] = React.useState<MyAccessCodesResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [generating, setGenerating] = React.useState(false);
  const [genDialogOpen, setGenDialogOpen] = React.useState(false);
  const [expiryDate, setExpiryDate] = React.useState<Dayjs | null>(dayjs().add(30, 'day'));
  const [genError, setGenError] = React.useState<string | null>(null);

  const [waDialogCodeId, setWaDialogCodeId] = React.useState<string | null>(null);
  const [waMobile, setWaMobile] = React.useState('');
  const [waMessage, setWaMessage] = React.useState('');
  const [waSending, setWaSending] = React.useState(false);
  const [waError, setWaError] = React.useState<string | null>(null);
  const [waSuccess, setWaSuccess] = React.useState(false);

  const [copiedId, setCopiedId] = React.useState<string | null>(null);
  const [copiedLinkId, setCopiedLinkId] = React.useState<string | null>(null);
  const [toast, setToast] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchMyAccessCodes());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    setGenError(null);
    try {
      const days = Math.max(1, expiryDate ? expiryDate.diff(dayjs().startOf('day'), 'day') : 30);
      await generateAccessCode(days);
      setGenDialogOpen(false);
      await load();
    } catch (e) {
      setGenError(e instanceof Error ? e.message : 'Failed to generate');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (code: AccessCode) => {
    if (!window.confirm(`Delete code ${code.Code}? This cannot be undone.`)) return;
    try {
      await deleteAccessCode(code.Id);
      await load();
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Failed to delete');
    }
  };

  const handleCopyCode = (code: AccessCode) => {
    navigator.clipboard.writeText(code.Code).catch(() => {});
    setCopiedId(code.Id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCopyLink = async (code: AccessCode) => {
    try {
      const { link } = await fetchAccessCodeShareLink(code.Id);
      navigator.clipboard.writeText(link).catch(() => {});
      setCopiedLinkId(code.Id);
      setTimeout(() => setCopiedLinkId(null), 2000);
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Failed to get link');
    }
  };

  const handleSendWhatsApp = async () => {
    if (!waDialogCodeId || !waMobile.trim()) return;
    setWaSending(true);
    setWaError(null);
    setWaSuccess(false);
    try {
      await sendAccessCodeWhatsApp(waDialogCodeId, waMobile.trim(), waMessage.trim() || undefined);
      setWaSuccess(true);
      setTimeout(() => { setWaDialogCodeId(null); setWaMobile(''); setWaSuccess(false); }, 2000);
    } catch (e) {
      setWaError(e instanceof Error ? e.message : 'Failed to send');
    } finally {
      setWaSending(false);
    }
  };

  const getCodeStatus = (code: AccessCode): { label: string; color: string } => {
    if (code.ActivatedBy) return { label: 'Activated', color: '#22c55e' };
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const expiry = new Date(code.ExpiryDate); expiry.setHours(0, 0, 0, 0);
    if (expiry < today) return { label: 'Expired', color: '#ef4444' };
    return { label: 'Active', color: '#3b82f6' };
  };

  const quota = data?.quota ?? 0;
  const used = data?.codes?.length ?? 0;
  const canGenerate = used < quota;

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <VpnKeyIcon sx={{ color: '#ed8936' }} />
        <Typography variant="h6" fontWeight={700}>Access Codes</Typography>
      </Box>
      <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)', mb: 3 }}>
        Generate access codes to invite users to Super Connector. Each redeemed code grants the recipient Agent access.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Quota bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, bgcolor: 'var(--color-surface)', borderRadius: 1, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)' }}>
            Quota: <strong>{used} / {quota}</strong> code{quota !== 1 ? 's' : ''} used
          </Typography>
          {quota === 0 && (
            <Typography variant="caption" sx={{ color: '#ef4444' }}>
              You have no quota. Contact an admin to get access code quota assigned.
            </Typography>
          )}
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <Button
            variant="contained"
            startIcon={<VpnKeyIcon />}
            disabled={!canGenerate}
            onClick={() => { setGenError(null); setGenDialogOpen(true); }}
            sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
          >
            Generate Code
          </Button>
        </Box>
      </Box>

      {/* Code list */}
      {(!data?.codes || data.codes.length === 0) ? (
        <Box sx={{ textAlign: 'center', py: 6, color: 'var(--color-text-faint)' }}>
          <VpnKeyIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography>No access codes generated yet.</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {data.codes.map((code) => {
            const status = getCodeStatus(code);
            return (
              <Box
                key={code.Id}
                sx={{
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid var(--color-border)',
                  bgcolor: 'var(--color-surface)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: 'monospace', letterSpacing: 2 }}>
                      {code.Code}
                    </Typography>
                    <Chip
                      label={status.label}
                      size="small"
                      sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: `${status.color}22`, color: status.color }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)' }}>
                    Expires: {new Date(code.ExpiryDate).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {code.ActivatedBy && (
                      <span> · Activated by {code.ActivatedBy} on {code.ActivatedAt ? new Date(code.ActivatedAt).toLocaleDateString('en-SG') : '–'}</span>
                    )}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, flexWrap: 'wrap' }}>
                  <Tooltip title={copiedId === code.Id ? 'Copied!' : 'Copy code'}>
                    <IconButton size="small" onClick={() => handleCopyCode(code)}>
                      <ContentCopyIcon fontSize="small" sx={{ color: copiedId === code.Id ? '#22c55e' : undefined }} />
                    </IconButton>
                  </Tooltip>
                  {!code.ActivatedBy && (
                    <>
                      <Tooltip title={copiedLinkId === code.Id ? 'Link copied!' : 'Copy share link'}>
                        <IconButton size="small" onClick={() => handleCopyLink(code)}>
                          <ShareIcon fontSize="small" sx={{ color: copiedLinkId === code.Id ? '#22c55e' : undefined }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Send via WhatsApp">
                        <IconButton size="small" onClick={() => {
                            const baseUrl = window.location.origin;
                            const link = `${baseUrl}/super-connector?join-code=${encodeURIComponent(code.Code)}`;
                            const expiry = new Date(code.ExpiryDate).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' });
                            const defaultMsg = `Hi! You have been invited to join Super Connector.\n\nUse this access code to get started:\n\n*${code.Code}*\n\nOr click the link below to join directly:\n${link}\n\nThis code expires on ${expiry}.\n\nWelcome aboard!`;
                            setWaDialogCodeId(code.Id); setWaMobile(''); setWaMessage(defaultMsg); setWaError(null); setWaSuccess(false);
                          }}>
                          <WhatsAppIcon fontSize="small" sx={{ color: '#22c55e' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(code)} sx={{ color: 'var(--color-text-faint)' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Generate dialog */}
      <Dialog open={genDialogOpen} onClose={() => setGenDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle fontWeight={700}>Generate Access Code</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'var(--color-text-subtle)' }}>
            Pick the date this code will expire.
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Expiry Date"
              value={expiryDate}
              onChange={(v) => setExpiryDate(v)}
              minDate={dayjs().add(1, 'day')}
              format="DD-MMM-YYYY"
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
          {genError && <Alert severity="error" sx={{ mt: 2 }}>{genError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={generating}
            sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
          >
            {generating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* WhatsApp send dialog */}
      <Dialog open={!!waDialogCodeId} onClose={() => setWaDialogCodeId(null)} fullWidth maxWidth="sm">
        <DialogTitle fontWeight={700}>Send via WhatsApp</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'var(--color-text-subtle)' }}>
            Enter the recipient's mobile number. They must be a registered active user.
          </Typography>
          <TextField
            fullWidth
            label="Mobile Number"
            value={waMobile}
            onChange={(e) => setWaMobile(e.target.value)}
            placeholder="e.g. 91234567"
            disabled={waSending || waSuccess}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSendWhatsApp(); }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={6}
            label="Message"
            value={waMessage}
            onChange={(e) => setWaMessage(e.target.value)}
            disabled={waSending || waSuccess}
            inputProps={{ style: { fontSize: '0.85rem', fontFamily: 'monospace' } }}
          />
          {waError && <Alert severity="error" sx={{ mt: 2 }}>{waError}</Alert>}
          {waSuccess && <Alert severity="success" sx={{ mt: 2 }}>WhatsApp sent successfully!</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaDialogCodeId(null)} sx={{ textTransform: 'none' }}>Close</Button>
          <Button
            variant="contained"
            startIcon={<WhatsAppIcon />}
            onClick={handleSendWhatsApp}
            disabled={!waMobile.trim() || waSending || waSuccess}
            sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' }, textTransform: 'none' }}
          >
            {waSending ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity="error" onClose={() => setToast(null)} variant="filled">{toast}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
